// Engadin VR client — boot, world assembly, render loop, input dispatch,
// pose reporting, event FX, telemetry. Flat mode and VR share one player body.
// Traversal: walk/sprint/jump, climb-anywhere (grips), glide. Weapons: rifle +
// sniper; one zoomable view on desktop + phone (viewzoom.js), the head zoom
// window in VR. Funicular rides on the real alignments.
import * as THREE from 'three';
import { ClientGround } from './ground.js';
import { MapHeights } from './mapheights.js';
import { AimGround, makeAimGround, wantOf } from './aimray.js';
import { LabelSight, viewDue, VIEW_LABELS, VIEW_RANGE } from './labelsight.js';
import { TerrainManager, setDetailZones, initDetailQuality, detailTier,
  detailGpu, detailDevice, setFocusWedge } from './terrain.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { installAdaptRes, adaptResTick, resScale, ADAPT_DEFAULTS, resBandRefresh } from './adaptres.js';
import { questProfileName, setQuestProfile, QUEST_PROFILE_NAMES } from './questprof.js';
import { vrStyleOn, vrsFoveation, vrsState } from './vrstyle/index.js';
import { vrsInit, vrsFogDensity, vrsTune, vrsHideFar } from './vrstyle/fog.js';
import { startGpuProbe, deferGpuProbe, deviceClass, classOn } from './device.js';
import { createMultiview } from './xrmv.js';
import { createSpaceWarp, swFeatures } from './spacewarp.js';
import { earlyContext, RENDERER_PARAMS, sizeRenderer } from './bootgl.js';
import { applyGenTunables, genFrame, genState, genReport, genPending } from './gensched.js';
import { installCanopy, canopyTick, canopyState, canopyCheck } from './canopygen.js';
import { readSea } from './season.js';
import { installForestVol, forestVolTick, forestVolState, forestVolGroups, forestVolume } from './forestvol.js';
import { installSplatForest, splatForestTick, splatForest } from './splatforest.js';
import { FirstActs } from './firstacts.js';
import { BuildingManager, FAC_AA } from './buildings.js';
import { buildSky, setSun, buildPanorama, buildHorizon, buildSheets, buildRivers, buildClouds, updateClouds, waterTime, atmosFor } from './sky.js';
import { Net, PLACE_TIMEOUT_MS } from './net.js';
import { Player, FlatControls, VRControls, Vignette } from './player.js';
import { VrUi } from './vrui.js';
import { HeadSeat, seatBody } from './headseat.js';
import { MobileControls, isMobileDevice, gpsToGame, headingCorrectionDeg, policyAllows } from './mobile.js';
import { Weapons } from './weapons.js';
import { Focus } from './focus.js';
import { ScopeHud } from './scopehud.js';
import { ViewZoom, TiltSwitch, desktopVFov } from './viewzoom.js';
import { chargeFill, readiness, columnAlpha, arrivalCard, BeamColumn, FlightFill } from './beamcharge.js';
import { waitNames, waitShown, waitFacts, waitFact, waitProgress, paintBeamSpot } from './beamlabels.js';
import { flightMode, flightCfg, flightSAt, nadirPose, leadCovers, flightPath, arcPath, arcPlan, arcDur, flightPress, skipFill, lookOffset, handRel, landLook, resBack, vrPose, vrVmax, vrBeam, openHeading, nodeTop, flightTest, FlightPace, FlightCover } from './beamflight.js';
import { nakedNear } from './handoff.js';
import { XrLoad, XRLOAD_LAYER, loadProgress, loadOpacity, settleTerms, FpsWindow } from './xrload.js';
import { pixelAngle, classPixelAngle } from './ringtiers.js';
import { Funicular } from './funicular.js';
import { Aerialway } from './aerialway.js';
import { boreCut, corridorCovAt, insideBoreVolume, setSpans,
  updateCorridors } from './corridor.js';
import { loadWater, RiverIndex } from './water.js';
import { loadGazetteer, addLakes, addRivers, addStations } from './gazetteer.js';
import { Sfx } from './audio.js';
import { Voice } from './voice.js';
import { BuildingCollision } from '../../game/bldcol.js';
import { Forest, corridorMask, setTreeline } from './trees.js';
import { Shrubs } from './shrubs.js';
import { Grass, setGrassBands, deckCover, setVegSpriteLook } from './grass.js';
import { vegSpriteUrl } from './vegmat.js';
import { setupLook, LookScale } from './look.js';
import { JetSuit } from './jetsuit.js';
import { JetVr } from './jetvr.js';
const _jetVr = new JetVr();       // the VR jet's flight heading + pitch (jetvr.js)
import { boreTexture } from './roads3d.js';
import { WaysNet, SINK_U } from './ways2.js';
import { TrailMarks, MARK, gradeText, joinTrailData, prepRoutes, prepExtra, prepAmenities } from './trailmarks.js';
import { buildObjects, loadObjects } from './objects.js';
import { Furniture, standOf, nearestWater } from './furniture.js';
import { ShopSigns } from './shopsigns.js';
import { loadSpans } from '../../game/spans.js';
import { StructCollider } from '../../game/structcol.js';
import { StructDraw, detailTexture } from './structs2.js';
import { FarBuildings, setSwapArm, swapArm } from './farbld.js';
import { TrailLines } from './traillines.js';
import { railRunsSliced, boreFrames } from './structplan.js';
import { loadSurface } from './surface.js';
import { configureGround, loadGroundMaterials, attachNearWindow, attachFarWindow, noteGroundOptic, setGroundRoughSteps, waterIn, waterBoxesBusy, waterBoxesIdle } from './groundmat.js';
import { loadRough } from './rough.js';
import { StationZones, setPosters } from './stationzones.js';
import { BikeView } from './bike.js';
import { PlayerMap, slopeLabel } from './playermap.js';
import { Briefing } from './briefing.js';
import { Beacons, GUIDE } from './beacons.js';
import { Inspect, gameToLv95 } from './inspect.js';
import { HEMI, SUN, sceneLight } from './scenelight.js';
import RENDER_CFG from './renderconfig.json';
// WAYS-G A5: THE stair look (its "steps" key) for the raised flights (structs2)
import WAYSLOOK from '../../pipeline/wayslook.json';
import { fetchAsset, pauseBulk, resumeBulk, saveDataOn, assetStats, assetLog, bulkPending, assetEnded,
  configureAssetQueue, setNeedView, needReport, familyBytes, wireBytes, indexSteps, reportTables, REPORT_STEP,
  sendInSteps, flushReports } from './assetq.js';
import { needResolver, needDue } from './needscore.js';
import { loadWorldManifest, swapWorld, registerSwapHook, metaHook, buildingsHook, terrainHook, forestHook, vegHook } from './worldswap.js';
import COMPAT from './compat.json';
import RING_TIERS from './ringtiers.json';
import { compatVerdict, compatReloadKey } from './compat.js';
import { setPhase, setXR, observeLongTasks, longTaskReport, longTaskAttribution, longTaskWindow, act, sec, secFrame, secWindow,
  heavyOk, allowHeavy, heavyReady, setHidden, onLongTask, slice, setFrameClass, frame, phase, xrFrame, xrRoom } from './sched.js';
import { BenchRun, benchWanted, parseBenchRoute } from './bench.js';
import { seamDrive } from './seamhook.js';   // the seam lab's body hold (?seam=1 only; tools/seamlab.mjs)
import { farviewDraw } from './farview.js';   // w45 far-view spike: ?farview=1 / ?seam=1 only (farview.js)
import { createCompileQueue, askDevice } from './compileq.js';
import { runSteps, parseSteps } from './decode.js';
import { run as poolRun } from './pool.js';
import { restoreStructures, restoreCollider, transferOf } from './pool-structures.js';
import { emptyStructures } from '../../game/structures.js';
import { loadMapPack } from './mappack.js';
import { FamTris, famScene } from './famtris.js';
import { createGlWatch } from './glwatch.js';
import { VisFrame, poseOf, boundsFor } from './visframe.js';
import { VisCull } from './viscull.js';
import './swreg.js';   // service worker (client/sw.js): a returning visit downloads only what changed — APP_STARTUP "Service worker"
import { netSeen } from './netseen.js';   // the load report's h2 / enc / via, per response (SW-aware)
import { t, onLang } from './i18n.js';
import { keyHintPages, mountHints } from './keyhints.js';   // the key-hint line: pages + cycle (desktop, phone, VR)
import './i18n/landing.js';   // the dictionaries register at import
import './i18n/main.js';
import { setupLanding, recommend } from './landing.js';

const $ = (id) => document.getElementById(id);
const overlay = $('overlay'), status = $('status'), hud = $('hud');

// ── Perceptual FOV (Hor+ with cap): viewzoom.js desktopVFov ─────────────
// Design target: 90° horizontal FOV at 16:9 — the angular subtense of a
// ~27″ monitor at desk distance × a 1.8× comfort multiplier, matching the
// industry standard for outdoor-traversal games (Battlefield, Far Cry).
// Wider than 16:9 keeps the vFOV (Hor+, hFOV capped at 110°), narrower keeps
// the hFOV (Vert+). It is the 1× of the one view's zoom (ViewZoom.fov0).

// The frame's ONE display transform: setupLook → look.js.

const net = new Net();
net.onPlaced = onPlaced;      // every accepted 'placed' teleports us (placeAt)
// a promote under a running client ({t:'world'}): worldswap.js diffs the two
// manifests and keeps running or reloads the page
net.onWorld = (m) => { swapWorld(m).catch((e) => { console.error('[world]', e); location.reload(); }); };
const voice = new Voice(net);   // proximity voice chat (open mic, spatial)
// Baker 2: the ways doc (ways_slim.json, R0 WAY1) is read ONCE, by WaysNet
// (client/src/ways2.js). The structure draw needs two things from it — the
// rails over decks / through bores and the bore frames the lamp shading
// measures along (client/src/structplan.js railRuns / boreFrames) — so this
// subclass keeps them at the moment the document is read (its `_docSteps`,
// one task each), without a second fetch and without editing the ribbon module.
// COUPLING (S10): this hooks ways2.js WaysNet's PRIVATE `_docSteps(doc)` —
// a rename there silently drops the rails and the lamp frames (the structure
// draw then falls back to no rails / the flat lamp term; a warning names it).
// A throw in railRuns / boreFrames must never reject WaysNet._load (the
// lane ribbons would be lost): caught, logged, the structure draw degrades.
class WaysDrawNet extends WaysNet {
  _docSteps(doc) {
    return [
      // railRuns in slices (structplan.js railRunsSliced: the same output)
      ['ways.rails', async () => {
        try {
          const rails = await railRunsSliced(doc, {}, async () => { await slice(); act('ways.rails'); });
          this.drawDoc = { rails, bores: boreFrames(doc) };
        } catch (e) {
          console.warn('[structs2] ways.json rails / bore frames unavailable, structures draw without them:', e);
          this.drawDoc = null;
        }
      }],
      // nothing grows under a deck (grass.js deckCover; roadnet.onReady)
      ['ways.deckcover', () => {
        try { this.deckCover = deckCover(doc.decks); } catch (e) { this.deckCover = null; console.warn('[veg] deck cover unavailable:', e); }
      }],
    ];
  }
}

let renderer, scene, camera, rig;
// GL HEALTH (glwatch.js): the sampled getError + three's GL complaints, the telemetry `gl` block
let glWatch = null;
let cq = null;                // THE compile queue (compileq.js): every program built off the frame
let vrui = null;              // the in-headset UI: laser, menu, help, Map panel, comfort turn (vrui.js)
let hf, terrain, buildings, player, flat, vr, mobile, vignette, weapons, funicular, aerialway, forest, grass, shrubs, jetsuit, roadnet, bikeView, zones;
let focus = null;             // scope focus wedge (focus.js) — shared by terrain/buildings
let structDraw = null;        // structures.bin draw (structs2.js)
let farBuildings = null;      // the far-building LOD on the focus layer (farbld.js)
let trailLines = null;        // the trail lines: the zoom instrument's coloured ways (traillines.js)
let playerMap = null;         // minimap / full map / VR wrist map (playermap.js)
let trailMarks = null;        // marker posts + signposts (trailmarks.js), once the ways doc is indexed
let shopSigns = null;         // the shop name boards (shopsigns.js, D1-BLD), once shopsigns.json is in
let furniture = null;         // the amenity points as 3D objects (furniture.js), once trailAmenities is in
// the multi-level ground the Player stands on has settled (each declared file
// loaded or failed; an undeclared one counts as in): furniture seats wait for
// it (standOf ready) so no bench caches a terrain-only height under a deck
const groundLevels = { spans: false, structs: false };
let beacons = null;           // far light columns over other players (beacons.js)
let inspect = null;           // the monocular's inspect card (inspect.js), combat off
let mode = null;              // 'flat' | 'vr' | 'mobile' — set at the ⤳ Beam (the entry), null before
let entryMode = null;         // the launch tap's mode ('flat' | 'mobile' | 'vr'); the Map before entry runs with it
let _meta = null;             // bake meta.json (kept for GPS → game coord transform)
let tunables = null;
let gazetteer = null;         // POI spatial index from gazetteer.json
let rivers = null;            // RiverIndex for scope river-name query
let riverWays = null;         // raw rivers.json ways (label points)
let guideposts = null;        // trails/guideposts.json (the signposts; the Map's guidepost layer)
// MAP-D (all optional; an older bake has none): meta.routes (prepRoutes),
// meta.amenities (prepAmenities), meta.pathinfo's TLM-only segments
// (prepExtra) — the Map's trail ladder + the inspect cards
let trailRoutes = null, trailAmenities = null, trailExtra = null;
let _guideAt = 0;             // the route guide's last step (GUIDE.period_ms)
let _mapObjects = null;       // objects.json (the Map's object layer; loadObjects' one fetch)
let _mapPack = null;          // the map pack (mappack.js): the Map's streets + trails until WaysNet's are in
let _lookEase = null;         // desktop: {yaw, pitch} the view eases to (the Map card's 👁 Look)
let mapHeights = null;        // the Map's 16 m posts of the GROUND-M skin (mapheights.js), frame-wide ≈
let farHf = null;             // pano ring + peak islands rendered-surface sampler
let aimGround = null;         // the aim ray's GM L0 nodes (aimray.js AimGround, held in hf's wasm)
let _aimMarch = null;         // aimMarch over hf (∪ aimGround's nodes) / its node boxes / farHf
let labelSight = null;        // every label's line of sight on the aim ray (labelsight.js), gazetteer.sight
let panoRing = null;          // the far ring (sky.js buildPanorama)
let scopeFrame = 0;           // frame counter for the VR optic overlay throttle
let _monoLabelData = null;    // VR: the zoom window's HUD data (synced with its RT)
let _monoLabelOwed = false;   // VR: scopeTarget skipped it; tick fills it if the RT draws after all
// the one view (desktop + phone; APP_PLAYER.md §5): zoom state, the phone's
// tilt-to-map switch, the fullscreen HUD painter, and what tick resolved
let viewZoom = null;          // ViewZoom (viewzoom.js)
let tilt = null;              // TiltSwitch (viewzoom.js), phone only
let scopeHud = null;          // ScopeHud on #view-hud
let viewBand = false;         // this frame renders through the optic (weapons.monoCam)
let viewArm = false;          // below the band, zoomed past focus.view_arm: the main camera drives the wedge
let farWin = null;            // groundmat far window (ROCK-F B), tier 2 with shade tiles
let _viewFocusOn = false;     // the main camera's layers are the optic set (_viewFocusLayers)
let viewCam = null;           // the camera on screen this frame (camera | monoCam)
// the main pass's visibility frame (APP_VISIBILITY P0, visframe.js): built
// after the draw (visNote); its reused pose / gens objects
const visMain = new VisFrame('main');
const _visPose = {}, _visVp = [0, 0], _visGens = {};
let visCull = null;           // the P2 cull of the main pass (viscull.js; APP_VISIBILITY §3e)
let _vLabels = null;          // {features, at, band}: labels of the view, world-anchored
let _vRange = null;           // {dist, dh, name, dim, at, x, z}: the centre range readout (+ the Map wedge's range point)
let _hitUntil = 0, _hitBig = false;   // HUD hit flash (the reticle centre)
let dizzyMesh = null;         // red overlay quad (dizzy death mode)
let waterMesh = null;         // blue overlay quad (eye dipped under a lake)
let xrLoad = null;            // VR: the loading view from the session's first frame to the world's fade-in (xrload.js)
const _xrl = { at: -Infinity, p: 0, miss0: 0, d0: undefined, place: false };   // its readiness, re-read every XRL_MS; the place asked
const XRL_MS = 250;
const fx = [];
const frameTimes = [];        // the window's frame intervals (ms), the REAL gaps (never the clamped dt)
let lastTelemetry = 0;
// THE TIME STEP (vr-judder): a frame simulates its whole real interval up to
// the hitch — adaptres' hitch_ms (250), the same line the resolution
// controller draws — and a longer gap (a load, a tab switch) is a pause:
// the world loses the excess, never a normal frame's motion. The old 100 ms
// cap lost 30–40 % of a 140 ms frame on top of player.js's 50 ms step cap.
const DT_MAX_S = ADAPT_DEFAULTS.hitch_ms / 1000;
// server corrections applied since the last telemetry (net.onCorrection):
// count + the largest snap (m) — a pose discontinuity, not a slow frame
const _corr = { n: 0, max: 0 };
// per-frame CPU split for telemetry (update block vs render call, ms): a
// preallocated ring, reset at each 5 s report — no per-frame allocation
const SPLIT_CAP = 1024;       // > 5 s at 144 Hz; older frames are overwritten
const _updMs = new Float32Array(SPLIT_CAP);
const _rndMs = new Float32Array(SPLIT_CAP);
let _splitN = 0;
let inputAccum = 0;
let famTris = null;            // drawn tris / calls per family, one frame in N (famtris.js)
// ------------------------------------------------------------ load phases
// MAP FIRST (APP_PLAYER §7). A connected player has NO position until its
// first accepted place — the ⤳ Beam on a Map card. Phase 1 (startPhase1 in
// boot) — everything location-independent: ways, vegetation, water, rivers,
// objects, lifts, the classmap's coarse grid, ground materials, horizon, far
// ring, clouds, trails — starts at boot through the asset queue's `bulk`
// class, behind the Map. The ground at a TARGET (terrain rings, the 3×3
// GM L0 ground nodes, building collision + near GLBs, the 1 m classmap tiles) loads
// only once there is one: `_loadAt`, set by a card's prefetch, a GPS fix or
// the place reply (loadRetarget); in play the streaming follows player.pos.
const _loadAt = new THREE.Vector3();
let _hasTarget = false;       // _loadAt is a real target (nothing streams for the ground before one)
let _phase1 = false;          // phase 1 started
// a bench's front (furniture.js benchFront) reads the coarse terrain, the
// lakes and the rivers: the furniture plan waits for all three to settle
let _benchEnvDone = null;
const benchEnvP = new Promise((res) => { _benchEnvDone = res; });
let _startPhase1 = null;      // set in boot (closes over its world objects)
let _opticNoted = false;      // noteGroundOptic asked (the first optic / magnified view)
// performance.now() stamps for the ONE `load` report (CONTRACT; buildLoadReport)
const loadT = {
  t_connect: null, t_meta: null, t_hf_ready: null, t_near_ready: null,
  t_map: null, t_card: null, t_beam: null, t_dismiss: null, t_gps_fix: null,
  t_ovl: null,
};
// one report per phase (load-report-pre-entry): 'entry' at the first beam-in
// latch, 'pre-entry' once if the page is hidden / left (abandon) or
// PRE_ENTRY_TIMEOUT_MS pass before that latch — a phone that never gets in
// still reports how far it got
const _loadSent = { 'pre-entry': false, entry: false };
// 120 s from page start: on desktop phase 1 has settled by ~30 s (24 s of
// long tasks measured on St. Moritz); a phone is 3–5× slower, so 120 s still
// lands inside — or right after — its load, while a hung or crawling page
// that the player keeps open reports before most give up. Leaving earlier is
// covered by the abandon path (hidden / pagehide), not by this timer.
const PRE_ENTRY_TIMEOUT_MS = 120000;
let _noReport = false;        // a reload we asked for (client-bake-compat) is no abandon
let _prefetchHit = null;      // the near ground was resident at the FIRST ⤳ Beam click
let _beamAt = 0;              // the LATEST ⤳ Beam (the beam-in release timeout counts from it)
let _entering = false;        // an enterAt is between its click and its outcome
// the beam-in latch: null, or {at (the latch time, 0 = waiting), smooth (the
// consecutive smooth frames since the warm-up ended)} from a successful entry
// until the overlay is gone
let _beamIn = null;
let _firstDraw = false;         // the next world draw is the first after a ⤳ Beam (act 'frame.first')
const firstActs = new FirstActs();   // the cold first activations after a Beam, one a frame (firstacts.js)
// THE SMOOTHNESS GATE (freeze-free entry): flat / phone keep the overlay
// after the latch until the FIRST VIEW is warm (fvReady: its programs linked
// and first-used, the water variants if water is near, the vegetation of the
// draw range or ovl_veg_ms) AND ovl_smooth_frames consecutive frames ran
// under ovl_smooth_ms — at most ovl_max_ms after the latch (renderconfig
// `load`). VR goes at the latch once the vegetation rule holds. The rest of
// the warm-up (warmShaders) runs after the overlay.
const OVL = {
  frames: (RENDER_CFG.load && RENDER_CFG.load.ovl_smooth_frames) ?? 30,
  ms: (RENDER_CFG.load && RENDER_CFG.load.ovl_smooth_ms) ?? 50,
  max: (RENDER_CFG.load && RENDER_CFG.load.ovl_max_ms) ?? 20000,
  veg: (RENDER_CFG.load && RENDER_CFG.load.ovl_veg_ms) ?? 5000,
  // THE DEVICE'S OWN PACE (ovlSmooth): a frame under `pace` × the median of
  // the last `frames` gaps (up to capMs) is smooth too — an iGPU drawing the
  // first view at a steady 48-55 ms never counted 30 frames under 50 ms
  // (probe 2026-10-24: fvReady true 10.8 s, the count at most 6, released by
  // ovl_max every time). A fast device keeps `ms`; a hitch >= capMs resets.
  pace: 1.25,
  capMs: 100,
};
// one overlay frame's gap: smooth? (the gap joins the last OVL.frames, `g`)
function ovlSmooth(g, gap) {
  g.push(gap);
  if (g.length > OVL.frames) g.shift();
  const m = g.slice().sort((a, b) => a - b)[g.length >> 1];
  return gap < Math.max(OVL.ms, Math.min(OVL.capMs, OVL.pace * m));
}
// the first-view pass of a beam-in (fvTick): null outside one, else {busy (a
// pass in flight: the frame does not draw), clean (a scan after the latch
// found nothing left), at (the last scan), water (the water warm-up of a near
// lake, in flight)}
let _fv = null;
// THE BEAM FLIGHT (PC; beamflight.js, APP_STARTUP §7): null, or from the ⤳
// Beam click until control {F (flightCfg), stage 'zoom' | 'hold' | 'fade' |
// 'descend' | 'land', cover (FlightCover), pace (FlightPace), base (the feet
// at the beam point), eye (the ground tree's eye under the camera), pose,
// yaw1 / pitch1 (the view control lands on), late (warmLate owed), t {stage
// times}, log [the flight record], test (the last pacing test)}. The beam-in
// state (_beamIn, _fv: the latch, the first view, the water) runs as for the
// overlay; the flight replaces the overlay and its release.
let _flight = null;
let _flightLast = null;         // the last flight's record (__vr.flight after landing)
let _flightLead = null;         // __vr.flightLead: the probe's A/B of tunables.flight.lead (null: the tunable)
let _arcFps = null;             // __vr.arcFps: the probe's override of tunables.flight.arc_fps (null: the tunable)
let _resBack = null;            // the arc's full scale owed after control {at, stillAt, v, F, ramp, k0} (resTick)
let lookScale = null;           // the flight's lower resolution, a sub-rect of one target (look.js LookScale; flat mode only, from launch)
let _charge = null;             // the ⤳ Beam held (beamCharge): its target, preload, readiness
let _chargeLast = null;         // the last charge's record (__vr.charge)
let _beamCol = null, _colAt = 0; // the light column at the target (beamcharge.js BeamColumn), its launch time
let _fill = null;               // the flight's labels + arrival card (beamcharge.js FlightFill)
let _noArc = false;             // __vr.goto without `arc`: the placement stays instant (the probe's shot moves)
// the right-button Map toggle is dead while the overlay is up and RMB_QUIET_MS
// after it went (owner ruling 2026-10-19: a click during the load or queued
// behind a long task opened the Map — "it goes back to 2D")
const RMB_QUIET_MS = 3000;
let _ovlGoneAt = -1e9;
let _entryWhy = null;           // the entry report's why ('latch' | 'timeout'), sent when the overlay goes
let _ovlRelease = null;         // what released the overlay: smooth | max | vr | left | flight (the load report's `release`)
let _frameGap = 0;              // this frame's raw gap since the last tick (ms; tick)
let _gapAvg = 0;                // its smoothed value (the arc's frame-rate plan, arcPlan)
// the device bench (bench.js, `?bench=1`; APP_STARTUP "The device bench"): without the query
// _bench stays null and nothing of it runs
const BENCH = benchWanted(location.search);
let _bench = null;              // the run (BenchRun), from the launch tap
let _benchRoute = null;         // this world's route (dist bench/<meta.region>.json), once fetched
let _benchData = null;          // the `load` report the run sent (phase 'bench'; __vr.bench)
const _benchLts = [];           // long tasks before the run exists (it counts from page start)
if (BENCH) onLongTask((st, ms, l) => (_bench ? _bench.longTask(st, ms, l) : _benchLts.length < 5000 && _benchLts.push([st, ms, l])));
let climbHand = null;         // active VR climb hand ('left'|'right'), null = none
let flatClimbing = false;
let riding = false;           // inside a funicular car this frame
let _prevRiding = null;       // last zones.riding (identity) — board/arrival beams

// ------------------------------------------------------------- GPS follow
// "GPS on" means the avatar keeps standing where the user really stands: a
// continuous watchPosition feeds the follow controller in the mobile tick
// branch, which WALKS the body to the fix (the server's anti-teleport wall
// rejects anything else) and asks for a re-placement on a big jump.
const gps = {
  on: false,          // user intent — survives a watch that had to stop
  watchId: null,      // navigator.geolocation watch, null = not watching
  fix: null,          // { lat, lng, acc, t } — most recent position
  err: null,          // short reason string, shown on the 📍 button
  fail: null,         // { code, message, dt } of the geolocation error, if any
  diag: null,         // gpsDiagnose() result for THIS attempt (cached)
  preflight: null,    // verdict known before the first tap ('insecure'|…|null)
  reqAt: 0,           // Date.now() of the request — a <500 ms code 1 = no prompt
  lastPlaceAt: 0,     // Date.now() of the last net.place (server: ≥ 2 s apart)
  placing: false,     // a net.place is in flight
};

function gpsStart() {
  if (gps.watchId !== null || !('geolocation' in navigator)) return;
  gps.err = null;
  gps.fail = null;
  gps.diag = null;      // a fresh attempt deserves a fresh diagnosis
  gps.preflight = null; // …and supersedes whatever the pre-flight guessed
  gps.reqAt = Date.now();
  // NOTE: this call IS the permission prompt, so it must sit directly on the
  // user's tap — a browser drops a geolocation request that arrives after an
  // await, and the prompt would never appear.
  gps.watchId = navigator.geolocation.watchPosition((p) => gpsOnFix(p, false), (e) => {
    gps.err = e.code === 1 ? 'denied' : e.code === 2 ? 'no fix' : e.code === 3 ? 'timeout' : 'error';
    gps.fail = { code: e.code, message: e.message || '', dt: Date.now() - gps.reqAt };
    // a denial is remembered by the browser, so retrying burns battery for
    // nothing; "no fix" / "timeout" are transient (indoors, cold start) and
    // the very next second may succeed — keep those watches alive
    if (e.code === 1) gpsStopWatch();
    gpsBtnSync();
    gpsFail(gps.fail);                    // → diagnosis → guidance card
  }, { enableHighAccuracy: true, maximumAge: 1000, timeout: 20000 });
  // …and, on the same tap, a FAST coarse fix: network / cell position, a
  // cached one up to 5 min old is fine. A cold GNSS fix takes tens of seconds
  // on a phone; this one is usually in within a second or two, and it is
  // enough to start streaming the right valley (gpsPreload) and to place the
  // player if the precise fix keeps them waiting. Its errors say nothing the
  // watch will not say itself, so they are dropped.
  try {
    navigator.geolocation.getCurrentPosition((p) => gpsOnFix(p, true), () => {},
      { enableHighAccuracy: false, maximumAge: 300000, timeout: 8000 });
  } catch { /* the watch stands alone */ }
  gpsBtnSync();
}

// One fix from either request. A coarse one only fills an empty slot: it
// never overwrites a fix the watch already delivered (newer, or finer).
function gpsOnFix(p, coarse) {
  if (coarse && (gps.fix || !gps.on)) return;
  gps.fix = {
    lat: p.coords.latitude, lng: p.coords.longitude,
    acc: p.coords.accuracy || 0, t: Date.now(),
  };
  if (loadT.t_gps_fix === null) loadT.t_gps_fix = performance.now();
  gps.err = null;
  gps.fail = null;
  gps.diag = null;
  gpsHelpHide();
  if (mobile) mobile.hideGpsHelp();     // a fix answers the card's question
  gpsBtnSync();
  gpsPreload();
}

// PRELOAD before entry: with GPS on and no world entered yet, stream the
// ground at the fix while the user is on the landing page or the Map — the
// Map's "📍 Beam to my position" then finds it loading or loaded. Client-
// only: a place from here would seat a player who has not entered (the beam
// does that). Never moves `player`; the streaming target `_loadAt` does
// (loadRetarget). Throttled to a 150 m move AND 2 s; a fix that arrives
// inside the 2 s is taken when they are up.
const GPS_PRELOAD_M = 150, GPS_PRELOAD_MS = 2000;
let _preloadAt = -Infinity, _preloadTimer = 0;
function gpsPreload() {
  if (mode !== null || !gps.on || !gps.fix || !_meta || !player) return;
  const g = gpsToGame(gps.fix.lat, gps.fix.lng, _meta);
  if (g.unsupported || !g.inBounds) return;
  if (Math.hypot(g.x - _loadAt.x, g.z - _loadAt.z) < GPS_PRELOAD_M) return;
  const wait = _preloadAt + GPS_PRELOAD_MS - performance.now();
  if (wait > 0) {
    if (!_preloadTimer) _preloadTimer = setTimeout(() => { _preloadTimer = 0; gpsPreload(); }, wait);
    return;
  }
  _preloadAt = performance.now();
  loadRetarget(g.x, player.pos.y, g.z);
}

// The ONE placement path (PROTOCOL place → placed): the entry (enterAt),
// GPS follow, the inspect card's "beam to there" and the dev handle all ask through
// placeAt; net hands EVERY accepted reply to onPlaced, which teleports — the
// callers only read the outcome (pos, or null = refused: combat on after the
// first move, out of bounds, < 2 s since the last placement, no reply).
function placeAt(x, z, why = false) {
  if (mode !== 'flat') return net.place(x, z, why);
  // the reply gap (flat): the beam has started — input is held as in its flight (the capture below)
  const tok = ++_placeTok;
  _placing = true;
  return net.place(x, z, why).finally(() => { if (tok === _placeTok) _placing = false; });
}
let _placing = false, _placeTok = 0;
function onPlaced(pos, waited = true) {
  if (!player) return;
  // before entry a reply nobody waits for is a place() that timed out —
  // enterAt already took the fail path and sent the leave: the body stays
  // off the world (the Map is still up)
  if (!mode && !waited) return;
  if (_flight && (_flight.kind === 'arc' || _flight.kind === 'map')) endFlight(true);   // a newer placement: the running arc / inverse beam ends at once (the new one flies)
  const from = mode === 'flat' ? player.pos.clone() : null;
  resetRide();
  player.spawn(pos[0], pos[1], pos[2]);
  if (from) beginArc(from);
  if (focus) focus.reset();
  if (hf) hf.ensureAround(pos[0], pos[2]);
  _full = { at: performance.now(), t: {} };   // the full look's clock (fullTick) restarts at every placement
}

// Every placement and the VR end drop the ride state: a car the body was
// riding (stationzones re-pins player.pos to it every frame → the movement
// validator rejects, corr snaps back, forever), the bike, a climb grip and
// the momentum belong to the old spot.
function resetRide() {
  if (zones) zones.riding = null;
  _prevRiding = null;
  climbHand = null;
  flatClimbing = false;
  if (!player) return;
  if (player.state === 'climb' && player.endClimb) player.endClimb();
  player.bike = false;
  player.bikeInd = 0;
  player.bikeSpeed = 0;
  player.vel.set(0, 0, 0);
}

// Point the streaming before entry at (x, y, z) now: the ground's node block,
// the terrain rings (its rescan drops the old spot's queued tiles), the
// building queues, the 1 m classmap tiles and the tree / shrub tiles of the
// draw range. The tick keeps updating at
// `_loadAt` after this until a mode runs. Before entry only — in play the
// streaming follows the player.
function loadRetarget(x, y, z) {
  _loadAt.set(x, y, z);
  _hasTarget = true;
  _needBeam = null;             // a newer target (a card, a GPS fix) replaces the Beam's point
  needTick(true);               // a target change re-scores the bulk queue (needscore.js)
  hf.ensureAround(x, z);
  terrain.update(_loadAt);
  buildings.update(_loadAt, null);
  if (player && player.surface) player.surface.ensure(x, z);
  if (forest) forest.stream(_loadAt, true);
  if (shrubs) shrubs.stream(_loadAt, true);
  warmTarget(x, z);             // the first view there compiles first (preWarm)
}

// A card on the Map before entry opened at (x, z) (inspect.openAt →
// ctx.prefetch, R10): the near ground there starts loading now; a newer card
// retargets (the terrain rescan drops the old spot's queue). The first card
// also ends a Save-Data hold on the bulk class.
function prefetchAt(x, z) {
  if (mode || !hf) return;
  if (loadT.t_card === null) loadT.t_card = performance.now();
  openHeavy();                  // quiet start ends: the first card
  resumeBulk('save-data');
  const y = hf.height(x, z);
  loadRetarget(x, y ?? 0, z);
}

function gpsStopWatch() {
  if (gps.watchId === null) return;
  try { navigator.geolocation.clearWatch(gps.watchId); } catch { /* gone */ }
  gps.watchId = null;
}

function gpsStop() {
  gpsStopWatch();
  gps.on = false;
  gps.fix = null;
  gps.err = null;
  gps.fail = null;
  gps.diag = null;
  gps.placing = false;
  gpsHelpHide();
  if (mobile) mobile.hideGpsHelp();
  gpsBtnSync();
  // GPS off on the landing page: the preload no longer applies — stream for
  // where the player really is (the spawn) again
  if (mode === null && player) {
    clearTimeout(_preloadTimer);
    _preloadTimer = 0;
    loadRetarget(player.pos.x, player.pos.y, player.pos.z);
  }
}

// ---------------------------------------------------- GPS failure diagnosis
// A geolocation code 1 does NOT mean "the user pressed Block". On this game it
// usually means the opposite: the join link is the LAN address on a SELF-SIGNED
// certificate (server/gameserver.js ensureCerts), and a phone refuses every
// powerful feature on a page whose certificate it does not trust — it returns
// PERMISSION_DENIED instantly, without ever prompting. Telling that user to
// "allow Location for this site" points at a setting that does not exist, so
// the whole feature reads as broken. These helpers tell the cases apart and
// hand back text the user can actually act on.

// A bare address literal is never a name a public CA can certify, so a page
// served from one is a self-signed page by construction.
const _HOST_IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;
const _HOST_IPV6 = /^\[?[0-9a-f]*:[0-9a-f:.]*\]?$/i;

// Permission state WITHOUT asking. Safari < 16 has no geolocation descriptor
// and throws on the query, so "unknown" is a normal, expected answer.
async function gpsPermState() {
  try {
    if (!navigator.permissions || !navigator.permissions.query) return 'unknown';
    const st = await navigator.permissions.query({ name: 'geolocation' });
    return st.state;                                 // granted | prompt | denied
  } catch { return 'unknown'; }
}

// True when the origin can only ever be carrying a certificate no phone trusts:
// an IP literal, the mDNS CN ensureCerts() bakes in, or a bare LAN hostname.
function gpsSelfSignedOrigin() {
  const h = location.hostname || '';
  return _HOST_IPV4.test(h) || _HOST_IPV6.test(h)
    || /\.local$/i.test(h) || !h.includes('.');
}

// The four features one Permissions-Policy header usually kills together, in
// one grep-able string: geolocation (GPS), gyroscope + magnetometer (tilt-look
// and the compass) and microphone (proximity voice). A remote report that says
// `policy=geo:blocked,gyro:blocked,mag:blocked,mic:blocked` names the whole
// fault and its single fix; without it, three separate "it's broken" reports
// arrive and none of them points at the web server. '?' = browser cannot tell.
function gpsPolicyTech() {
  const v = (f) => { const a = policyAllows(f); return a === null ? '?' : a ? 'ok' : 'blocked'; };
  return `geo:${v('geolocation')},gyro:${v('gyroscope')},`
    + `mag:${v('magnetometer')},mic:${v('microphone')}`;
}

// → { cause, title, steps, tech }. `err` is gps.fail (or null for a pre-flight
// look, where "no error yet" reads as the transient case). Cached per attempt:
// gpsStart() clears gps.diag, so the answer never outlives its question.
async function gpsDiagnose(err) {
  if (gps.diag) return gps.diag;
  const state = await gpsPermState();
  const secure = window.isSecureContext !== false;
  const have = 'geolocation' in navigator;
  // "denied before a human could possibly have answered" — the browser refused
  // on its own, which is exactly the self-signed-certificate signature.
  const noPrompt = !!err && err.code === 1 && err.dt < 500 && state !== 'denied';

  let cause;
  if (!secure) cause = 'insecure';
  else if (!have) cause = 'unsupported';
  // BEFORE the 'denied' branch: a Permissions-Policy block makes
  // permissions.query report 'denied' too, and the 'blocked' card would then
  // send the user to padlock → Permissions → Location, a setting that does not
  // exist for a policy-blocked feature. The header is the only truth here, so
  // the header is what we check first.
  else if (policyAllows('geolocation') === false) cause = 'policy';
  else if (state === 'denied') cause = 'blocked';
  else if (noPrompt) cause = gpsSelfSignedOrigin() ? 'cert' : 'os';
  else if (err && err.code === 1) cause = 'blocked';   // slow code 1 = a real tap on Block
  else cause = 'transient';                            // code 2/3, or no error at all

  const copy = GPS_HELP[cause]();
  // One line, technical, always visible in small print: err.message carries the
  // browser's own reason ("Only secure origins are allowed", "User denied
  // Geolocation") and is the only thing that makes a phone report debuggable.
  const tech = `cause=${cause} code=${err ? err.code : '-'} `
    + `msg=${JSON.stringify(err ? err.message : '')} dt=${err ? err.dt : '-'}ms `
    + `perm=${state} origin=${location.origin} secureContext=${secure} `
    + `policy=${gpsPolicyTech()}`;
  gps.diag = { cause, title: copy.title, steps: copy.steps.slice(), tech };
  return gps.diag;
}

// The user-facing copy, one entry per cause. Written for someone standing
// outdoors holding a phone: what is wrong, then what to touch, in order.
// Each entry is called at use (i18n main.gps.*): the current language.
const GPS_HELP = {
  // The one cause nobody on the phone can act on: it is the SERVER's answer,
  // not the browser's and not the user's. Every step here is addressed to the
  // person who runs the site, because they are the only one who can fix it.
  policy: () => ({
    title: t('main.gps.policy.title'),
    steps: [t('main.gps.policy.s1'), t('main.gps.policy.s2'), t('main.gps.policy.s3'), t('main.gps.policy.s4')],
  }),
  cert: () => ({
    title: t('main.gps.cert.title'),
    steps: [t('main.gps.cert.s1'), t('main.gps.cert.s2'), t('main.gps.cert.s3')],
  }),
  blocked: () => ({
    title: t('main.gps.blocked.title'),
    steps: [t('main.gps.blocked.s1'), t('main.gps.blocked.s2'), t('main.gps.blocked.s3')],
  }),
  os: () => ({
    title: t('main.gps.os.title'),
    steps: [t('main.gps.os.s1'), t('main.gps.os.s2'), t('main.gps.os.s3'), t('main.gps.os.s4')],
  }),
  insecure: () => ({
    title: t('main.gps.insecure.title'),
    steps: [t('main.gps.insecure.s1'), t('main.gps.insecure.s2'), t('main.gps.insecure.s3')],
  }),
  unsupported: () => ({
    title: t('main.gps.unsupported.title'),
    steps: [t('main.gps.unsupported.s1'), t('main.gps.unsupported.s2'), t('main.gps.unsupported.s3')],
  }),
  transient: () => ({
    title: t('main.gps.transient.title'),
    steps: [t('main.gps.transient.s1'), t('main.gps.transient.s2'), t('main.gps.transient.s3')],
  }),
};

// The single truth about "GPS is not working": the diagnosed cause of the
// attempt that failed, or the pre-flight verdict from before the first tap.
// 'pending' = failed, diagnosis still resolving (one microtask, at most).
// Coordinate complaints from the follow tick ('not CH', 'outside world') are
// deliberately NOT failures here — the permission is fine, the place is not.
function gpsFailCause() {
  if (gps.fail) return gps.diag ? gps.diag.cause : 'pending';
  return gps.preflight;
}

// A failed attempt is a question ("why?"), not a verdict. Diagnose it and put
// the answer where the user is looking: the landing card, or — in fullscreen,
// where the landing page is unreachable — the overlay card.
async function gpsFail(err) {
  const d = await gpsDiagnose(err);
  gpsBtnSync();
  if (mode === 'mobile' && mobile) mobile.showGpsHelp(d);
  else gpsHelpShow(d);
}

// Learn what we can WITHOUT asking, so the button tells the truth before the
// first tap instead of after it. Only two verdicts may be pre-empted: a
// non-https origin and an already-denied permission. A 'prompt' state MUST
// leave the button alone — only the real tap may open the real prompt.
async function gpsPreflight() {
  if (!('geolocation' in navigator)) gps.preflight = 'unsupported';
  else if (window.isSecureContext === false) gps.preflight = 'insecure';
  // a header block is knowable without asking anything — and asking is exactly
  // what it makes pointless, so the button must read "unavailable" before the
  // first tap, with the policy card waiting behind it
  else if (policyAllows('geolocation') === false) gps.preflight = 'policy';
  else if (await gpsPermState() === 'denied') gps.preflight = 'blocked';
  else gps.preflight = null;
  gpsBtnSync();
}

// ------------------------------------------------- landing guidance card DOM
function gpsHelpShow(diag) {
  const card = document.getElementById('gps-help');
  if (!card || !diag) return;
  document.getElementById('gps-help-title').textContent = diag.title;
  const ol = document.getElementById('gps-help-steps');
  ol.textContent = '';                                 // rebuilt per diagnosis
  for (const s of diag.steps) {
    const li = document.createElement('li');
    li.textContent = s;          // never innerHTML: some of this is browser text
    ol.appendChild(li);
  }
  document.getElementById('gps-help-tech').textContent = diag.tech;
  card.hidden = false;
}

function gpsHelpHide() {
  const card = document.getElementById('gps-help');
  if (card) card.hidden = true;
}

// Re-open the card for a state that already failed (or that the pre-flight
// already condemned) without firing a request the browser will only refuse.
function gpsHelpReopen() {
  if (gps.diag) { gpsHelpShow(gps.diag); return; }
  gpsDiagnose(gps.fail).then(gpsHelpShow);
}

// Landing-page button label, DERIVED from `gps` — the old toggle wrote the
// label by hand, so it could never say "asking…" or "denied" and a refused
// prompt looked exactly like a granted one. The failure wording now comes from
// the DIAGNOSIS, never from the raw 'denied' string: on a self-signed page the
// browser says "denied" about something the user never denied.
function gpsBtnSync() {
  const b = document.getElementById('btn-gps');
  if (!b) return;
  const cause = gpsFailCause();
  b.dataset.active = gps.on ? '1' : '0';
  b.classList.toggle('active', gps.on && !cause);
  b.classList.toggle('bad', !!cause);
  b.textContent = cause === 'transient' ? t('land.gps.nofix')
    : cause ? t('land.gps.off')
    : !gps.on ? t('land.gps.use')
    : gps.fix ? t('land.gps.fix', { m: Math.round(gps.fix.acc) })
    : t('land.gps.asking');
}

// Wait for a usable fix (entering mobile mode): one within GPS_GOOD_ACC_M,
// or — once any fix is in (the coarse one usually is) — at most refineMs
// more for it to sharpen, then go with what there is. Resolves early on a
// denial — there is nothing left to wait for.
const GPS_GOOD_ACC_M = 100;
function gpsWaitForFix(ms, refineMs = 3000) {
  return new Promise((done) => {
    const t0 = Date.now();
    let tFix = 0;
    const poll = () => {
      const now = Date.now();
      if (gps.fix && !tFix) tFix = now;
      if ((gps.fix && gps.fix.acc <= GPS_GOOD_ACC_M) || gps.err === 'denied'
        || now - t0 > ms || (tFix && now - tFix > refineMs)) done(!!gps.fix);
      else setTimeout(poll, 200);
    };
    poll();
  });
}

// combat toggle (tunables.combat.enabled): off = no guns, no bots, monocular only
function _combatOn() {
  if (!tunables || !tunables.combat) return true;
  return tunables.combat.enabled !== false;
}

// Quest controllers: three.js hands out controller slots in CONNECTION order,
// not by handedness — slot 1 is NOT reliably the right hand (it flips when a
// controller wakes late or hand tracking blips). Everything hand-shaped (gun,
// aim, climb, off-hand steady, pose report) resolves through this map, filled
// from each input source's declared handedness on its 'connected' event. The
// index guess survives only as the fallback until both hands have announced.
const handCtrl = { left: null, right: null };
function xrCtrl(hand) {
  return handCtrl[hand] || renderer.xr.getController(hand === 'left' ? 0 : 1);
}

// ------------------------------------------------------------- world naming
// The entry page ships neutral placeholders: one bundle serves EVERY world, so
// anything written into index.html is one place's name shown for all of them
// (it read "Engadin VR — St. Moritz" whatever was live). The big headline is
// the static GEO-VR brand; the WORLD names itself from the bake into the
// "now live" chip (#world-name) — label, provider, fidelity tier and the real
// framed size, so the chip and the provenance line are true by construction.
let _namedMeta = null;          // the meta nameWorld last named (re-named on a language switch)
function nameWorld(meta) {
  _namedMeta = meta;
  const label = meta.region_label || meta.region || t('land.world.this');
  document.title = label + ' — GEO-VR';
  const h1 = $('world-name');
  if (h1) h1.textContent = label;
  const sub = $('world-sub');
  if (!sub) return;
  const bb = meta.bbox;
  const bits = [meta.provider, meta.tier].filter(Boolean);
  if (bb) {
    const km = (m) => (m / 1000).toFixed(1);
    bits.push(`${km(bb.e1 - bb.e0)} × ${km(bb.n1 - bb.n0)} km`);
  }
  sub.textContent = bits.join(' · ') + (bits.length ? ' — ' : '') + t('land.world.real');
}

// ------------------------------------------------------- weapon wall retract
// The rifle hangs 0.5 m in front of the camera and its barrel reaches another
// 0.6 m past that, but the body stops ~0.35 m short of a wall — so walking up
// to a house put half the gun through the masonry. Probe along the muzzle line
// and pull the weapon back to whatever room there actually is.
//
// This is the honest fix rather than the usual one. Games normally render the
// viewmodel on a second pass with the depth buffer cleared, which does not stop
// the gun being inside the wall, it just draws it on top — and here that would
// also lie about where the muzzle is, which is where hitscan starts.
// Desktop + phone have no ADS: the zoom is the one view's (viewzoom.js), and
// the kit only shows while it does not cover the view (VIEW_KIT_MAX_Z).
const WEAPON_REST = { flat: new THREE.Vector3(0.25, -0.22, -0.5), mobile: new THREE.Vector3(0.25, -0.22, -0.5), vr: null };
const WEAPON_REACH = 0.62;        // barrel tip past the group origin (sniper)
const VIEW_KIT_MAX_Z = 1.3;       // the gun view-model hides above this zoom
const _wp = new THREE.Vector3();
const _wd = new THREE.Vector3();
let _retract = 0;                 // metres pulled back, eased

function retractWeapon() {
  const rest = WEAPON_REST[mode];
  if (!rest || !weapons || !player) return;
  // the monocular is not drawn in hand on desktop / phone: the view IS the
  // optic once zoomed. A gun hides once the zoom passes VIEW_KIT_MAX_Z.
  weapons.group.visible = weapons.current !== 'monocular'
    && !(viewZoom && viewZoom.z > VIEW_KIT_MAX_Z);
  camera.getWorldPosition(_wp);
  camera.getWorldDirection(_wd);
  const reach = WEAPON_REACH - rest.z;          // rest.z is negative (forward)
  let free = reach;
  const STEP = 0.12;
  for (let d = STEP; d <= reach; d += STEP) {
    const x = _wp.x + _wd.x * d, y = _wp.y + _wd.y * d, z = _wp.z + _wd.z * d;
    const g = player.hf.height(x, z);
    const solid = (g !== null && y < g)
      || (player.collider && player.collider.solidAt(x, y, z));
    if (solid) { free = Math.max(0, d - STEP); break; }
  }
  // ease, so brushing past a corner does not snap the gun about
  const want = Math.max(0, reach - free);
  _retract += (want - _retract) * Math.min(1, 12 * (player._dt || 1 / 72));
  weapons.group.position.set(rest.x, rest.y, Math.min(-0.06, rest.z + _retract));
}

// ------------------------------------------------- which optic drives Focus
// Focus streams a wedge of high-detail terrain toward whatever the optic is
// looking at, so the gate is "the player is USING it", not "holds it". VR:
// the head zoom window once its fov is the optic's band (never the wide end
// of the zoom; the monocular in hand and its raise gesture were removed,
// owner 2026-11-01). Desktop / phone: below.

// `band` = desktop / phone: the view renders through the optic this frame
function opticFocusCam(band) {
  if (!weapons) return null;
  if (mode === 'vr') {
    return weapons.headOn && weapons.monoCam.fov <= weapons.monoBaseFov + 1e-6 ? weapons.monoCam : null;
  }
  // flat/mobile: the zoom band is the gate (any weapon — the band is every
  // weapon's optic); below it monoCam is not even posed — but a view zoomed
  // past renderconfig.focus.view_arm (viewArm, _viewPose) arms the wedge for
  // the MAIN camera (focus.js view mode: terrain levels only)
  if (band) return weapons.monoCam;
  return viewArm ? camera : null;
}

// the main camera draws the focus layer exactly while it drives the wedge
// (view mode): the optic cameras' own layer set. Every other frame (and
// every VR frame: viewArm is never set there) the boot layer set.
function _viewFocusLayers(on) {
  if (on === _viewFocusOn) return;
  _viewFocusOn = on;
  if (on) camera.layers.enable(focus.layer);
  else camera.layers.disable(focus.layer);
}

// -------------------------------------------------- landing-page live roster
// Fed by the snapshots' `roster` (every connected human, `in` 1 = in the
// world, 0 = on the Map before entry; the server sends it ~1 Hz) via
// net.onRoster; rendered only while the entry overlay is visible. Bots come
// from the entities. Names are player-controlled -> textContent.
let _maxPlayers = null;           // from /healthz, shown as "room for N"
function _fmtUp(sec) {
  if (sec < 60) return `${sec} s`;
  if (sec < 3600) return `${Math.floor(sec / 60)} min`;
  return `${Math.floor(sec / 3600)} h ${Math.floor((sec % 3600) / 60)} min`;
}
let _popLast = null;              // the head count's last [nIn, nMap, max] (re-rendered on a language switch)
function _popText(nIn, nMap, max) {
  _popLast = [nIn, nMap, max];
  const room = max ? t('land.pop.room', { n: max }) : '';
  return nIn + nMap ? t('land.pop.some', { in: nIn, map: nMap, room })
    : t('land.pop.none', { room });
}
function renderRoster(roster) {
  if (overlay.style.display === 'none') return;
  const pop = document.getElementById('world-pop');
  const list = document.getElementById('roster');
  if (!pop || !list) return;
  const humans = roster.slice().sort((a, b) => (b.up || 0) - (a.up || 0));
  const nIn = humans.filter((e) => e.in).length;
  const bots = net.ents.filter((e) => e.bot).length;
  pop.textContent = _popText(nIn, humans.length - nIn, _maxPlayers);
  list.hidden = humans.length === 0 && !bots;
  list.textContent = '';
  for (const e of humans) {
    const li = document.createElement('li');
    const name = document.createElement('span');
    name.className = 'r-name';
    name.textContent = e.n || `player-${e.id}`;
    li.appendChild(name);
    if (e.id === net.id) {
      const you = document.createElement('span');
      you.className = 'r-you';
      you.textContent = t('land.roster.you');
      li.appendChild(you);
    }
    const up = document.createElement('span');
    up.className = 'r-up';
    up.textContent = e.up != null ? `${t(e.in ? 'land.roster.in' : 'land.roster.map')} · ${_fmtUp(e.up)}` : '';
    li.appendChild(up);
    list.appendChild(li);
  }
  if (bots) {
    const li = document.createElement('li');
    li.className = 'r-bots';
    li.textContent = t(bots === 1 ? 'land.roster.bot' : 'land.roster.bots', { n: bots });
    list.appendChild(li);
  }
}

// the status line (#status): the last key + vars, re-written on a language switch
let _status = null;
function setStatus(key, vars) {
  _status = { key, vars };
  status.textContent = t(key, vars);
}
let _voiceMsg = null;             // the 🎤 button's ready / fail text ({key, vars}), once set

// EN | DE switched (the landing's toggle; i18n.js applyI18n has re-rendered
// every data-i18n element already): what main.js wrote by hand, again
onLang(() => {
  gpsBtnSync();
  if (_status) status.textContent = t(_status.key, _status.vars);
  if (_namedMeta) nameWorld(_namedMeta);
  const bv = $('btn-voice');
  if (bv && _voiceMsg) bv.textContent = t(_voiceMsg.key, _voiceMsg.vars);
  if (gps.diag) {
    Object.assign(gps.diag, GPS_HELP[gps.diag.cause]());
    const card = $('gps-help');
    if (card && !card.hidden) gpsHelpShow(gps.diag);
  }
  if (overlay.style.display === 'none') return;
  if (net.roster.length) renderRoster(net.roster);
  else if (_popLast) {
    const pop = $('world-pop');
    if (pop) pop.textContent = _popText(..._popLast);
  }
});

// the farpeak label anchors (gazetteer _apexOf) in steps, 'labels.apex', once
// the gazetteer, the ground's envelope and farHf are all in — the arguments
// inView gets (R8 M6a: its first call searched them all in one task)
function warmApex() {
  if (!gazetteer || !hf || !hf.env || !farHf) return;
  runSteps(gazetteer.apexSteps(groundEnv, farHf, hf.extentX, hf.extentZ), 'labels.apex');
}
// the in-frame envelope the gazetteer's apex reads: the GM L0 node's highest
// vertex (ClientGround.upper — the apex never sinks under the summit); gen
// moves once, when the index is in
const groundEnv = {
  height: (x, z) => (hf ? hf.upper(x, z) : null),
  get gen() { return hf && hf.env ? 1 : 0; },
};
// conservativedepth.js lowerGroundFromNodes' source (viscull.js `groundLower`):
// the GM L0 nodes' lowest vertices, null until the index is in
const groundLower = {
  get env() { return hf ? hf.env : null; },
};

async function boot() {
  // the GPU probe on its own context, before any GPU work; read at initDetailQuality (device.js).
  // A return visit with a stored result skips it here (deferGpuProbe at the release)
  const probeP = startGpuProbe(RENDER_CFG.ground && RENDER_CFG.ground.probe);
  // R8d: the renderer's context made early, one GL call per task behind the
  // network waits below, after the probe (bootgl.js); the renderer task takes it
  const early = earlyContext({ probe: probeP });
  // the landing's static text in the current language, the EN | DE toggle,
  // the video frame (landing.js) — before the first status write
  setupLanding();
  // the load report reads PerformanceResourceTiming: the default buffer of
  // 250 entries overflows on the first hundred terrain tiles
  if (performance.setResourceTimingBufferSize) performance.setResourceTimingBufferSize(5000);
  // long tasks for the load report (split at the first ⤳ Beam); the time
  // slicer's Map budget; Save-Data holds the bulk class until the first card
  observeLongTasks();
  setPhase('map');
  if (saveDataOn()) pauseBulk('save-data');
  // instant pre-hello count (also carries the entry cap for "room for N");
  // superseded by the live roster once snapshots flow
  fetch('/healthz').then((r) => r.json()).then((h) => {
    if (h.max) _maxPlayers = h.max;
    const el = document.getElementById('world-pop');
    if (!el || el.textContent || typeof h.players !== 'number') return;
    el.textContent = _popText(h.players, h.on_map || 0, h.max || null);
  }).catch(() => { /* hint only */ });

  setStatus('land.status.connecting');
  let welcome;
  try {
    // hello carries the remembered name; empty → the server assigns the
    // unique player-<id> default and echoes it back in welcome.name
    welcome = await net.connect(localStorage.getItem('geovr_name') || '');
    loadT.t_connect = performance.now();
    armPreEntryReport();
  } catch (e) {
    const ld = document.getElementById('loader');
    if (ld) ld.classList.add('done');
    setStatus(/full/.test(e.message) ? 'main.status.connFailFull' : 'main.status.connFail', { err: e.message });
    status.style.color = '#f87171';
    return;
  }
  tunables = net.tunables;
  applyGenTunables(tunables);   // GENSURF live parameters (gensched.js)

  // live who's-in list under the world chip (see renderRoster above)
  net.onRoster = renderRoster;
  if (net.roster.length) renderRoster(net.roster);

  // name field: shows what this player joined as; edits apply live ({t:'name'})
  // and persist for the next visit. Cleared → back to the server default.
  const nameEl = document.getElementById('player-name');
  if (nameEl) {
    nameEl.value = welcome.name || '';
    nameEl.addEventListener('change', () => {
      const v = nameEl.value.trim().slice(0, 24);
      if (v) localStorage.setItem('geovr_name', v);
      else localStorage.removeItem('geovr_name');
      net.sendName(v);
    });
  }

  // content-addressed: bake id in every artifact URL -> immutable caching is safe
  const baseUrl = '/bake/' + welcome.bake;
  // a big json body (registry, groundm index, rivers, objects, water, meta)
  // is parsed in steps at the phase budget, never one long task (assetq.js)
  configureAssetQueue({ parseJson: (text, label) => runSteps(parseSteps(text), label) });
  // the world manifest (WORLD2, /o/<welcome.world>, engine/store/world.js): the
  // top level names one family index per family (/o/<sha>, immutable: a return
  // visit re-reads only the changed ones); every artifact below keeps its
  // logical url, the queue fetches it as /o/<sha> (assetq.js THE TRANSPORT).
  // None, not this bake's or an index missing = no world, as without meta.json.
  // THE loader (worldswap.js): a world swap ({t:'world'}) diffs against it.
  const loaded = await loadWorldManifest(welcome.world, welcome.bake);
  const files = loaded ? loaded.files : null;
  const worldOk = !!files;
  if (worldOk) {
    configureAssetQueue({ manifest: { base: baseUrl, files } });
    // the load report's reverse map, built in steps once the quiet start ends (R8 M5c)
    heavyReady().then(() => runSteps(indexSteps(), 'assetq.index'));
  }
  // the world's season data (SEA1, the family `season`: season/season.json,
  // written at ingest — engine/store/world.js seaOf; season.js readSea): the
  // ONE source of the snow band, the species ramps, the measurement season
  // for the season consumers (Forest, Shrubs, CanopyGen, the forest volume).
  // A world without it (an ingest before R8i) or a bad one = no world, as a
  // missing family index (the meta is not read).
  const seaP = worldOk && files['season/season.json']
    ? fetchAsset(baseUrl + '/season/season.json', { prio: 'map', as: 'json' }).then(readSea).catch(() => null) : null;
  const metaP = worldOk ? fetchAsset(baseUrl + '/meta.json', { prio: 'map', as: 'json' }).catch(() => null) : null;
  const sea = seaP ? await seaP : null;
  const meta = sea ? await metaP : null;
  loadT.t_meta = performance.now();
  act('boot');                // the long-task label of the boot's rest of this task (not the meta fetch)
  // the need score's footprints (needscore.js): the bulk URLs the meta
  // places — before any bulk fetch is queued
  if (meta) configureAssetQueue({ need: needResolver(meta, baseUrl), needCfg: RING_TIERS.need });
  if (!meta) {
    setStatus('main.status.noMeta', { bake: welcome.bake });
    status.style.color = '#f87171';
    return;
  }
  // client-bake-compat: the bake names the lowest client level that reads its
  // artifacts right (meta.client_min).
  // Behind it: ONE reload (the page and bundle are served no-cache — a rebuilt
  // client arrives with it), guarded per tab by bake + requirement; still
  // behind = no world and a message. Rebuild client broadcasts `reload`, so
  // this page then comes back by itself.
  const cv = compatVerdict(COMPAT.client_compat, meta.client_min, COMPAT.bake_min);
  if (cv === 'behind') {
    const key = compatReloadKey(welcome.bake, meta.client_min);
    let first = false;
    try { first = !sessionStorage.getItem(key); if (first) sessionStorage.setItem(key, '1'); } catch { first = false; }
    if (first) {
      _noReport = true;
      setStatus('main.status.clientReload');
      location.reload();
      return;
    }
    const ld = document.getElementById('loader');
    if (ld) ld.classList.add('done');
    setStatus('main.status.clientBehind', { min: meta.client_min, cur: COMPAT.client_compat });
    status.style.color = '#f87171';
    return;
  }
  // the other way: a bake older than this client reads (meta.client_min <
  // compat.json bake_min) — refused HERE, not dropped asset by asset (a
  // pre-GLBQ bake lost every building); no reload: only a Build + Make live cures it
  if (cv === 'old') {
    const ld = document.getElementById('loader');
    if (ld) ld.classList.add('done');
    setStatus('main.status.bakeOld', { need: meta.client_min, min: COMPAT.bake_min });
    status.style.color = '#f87171';
    return;
  }
  // The world names itself. index.html carries neutral placeholders because
  // this page is served for whatever bake is live — every word below comes
  // from the bake, so it is true of the world actually loading and can never
  // be the previous world's name.
  nameWorld(meta);
  _meta = meta;
  if (meta.region_label || meta.region) setStatus('main.status.loading', { name: meta.region_label || meta.region });
  else setStatus('main.status.loadingWorld');
  if (BENCH) loadBench(meta.region);

  scene = new THREE.Scene();
  // world matrices: render() never walks the scene; tick walks it ONCE right
  // before its draw, weapons.js once more before a VR optic RT pass. The
  // root never moves; static sets are matrixAutoUpdate false (APP_SURFACE_PERF)
  scene.matrixAutoUpdate = false;
  scene.matrixWorldAutoUpdate = false;
  // 130 km far plane: the peak-island horizon reaches 100 km (APP_HORIZON.md)
  const aspect0 = innerWidth / innerHeight;
  camera = new THREE.PerspectiveCamera(desktopVFov(aspect0), aspect0, 0.08, 130000);
  viewZoom = new ViewZoom({ aspect: aspect0 });
  rig = new THREE.Group();
  rig.add(camera);
  scene.add(rig);

  // the early context (bootgl.js): what is left of its steps runs here; none / lost = three's own
  const ctx0 = early.finish();
  renderer = new THREE.WebGLRenderer(ctx0 ? { ...RENDERER_PARAMS, ...ctx0 } : RENDERER_PARAMS);
  askDevice(renderer);   // R8d5: three's lazy driver asks, here in the creation task (compileq.js)
  glWatch = createGlWatch(renderer.getContext());
  glWatch.watchConsole();
  renderer.setClearAlpha(1);   // three's alpha:false default (a passed context reads its alpha: true)
  // R8d2: three's checkShaderErrors reads the link status at every program's
  // first use — a SYNC wait on a still-linking program (the quiet-box A/B:
  // 6.3 s of main thread to the overlay). Off; ?shadercheck=1 turns it on
  // (?dev= is the device-class pin)
  renderer.debug.checkShaderErrors = new URLSearchParams(location.search).get('shadercheck') === '1';
  // == setSize(innerWidth, innerHeight) + setPixelRatio(min(dpr, 2)); the early canvas already
  // has that size: no reshape (bootgl.js THE SIZE)
  sizeRenderer(renderer);
  // R8d seams (boot steps): the renderer task ends here, then 'boot.gfx' (look, queue,
  // XR, the detail tier), 'boot.world' (sky, ground, canopy, KTX2) — every callback set
  // up before a seam touches only what exists before it or null-checks
  await slice();
  act('boot.gfx');
  setupLook(renderer, RENDER_CFG.look);
  famTris = new FamTris(renderer);
  // every program built off the frame, one scheduler (compileq.js; the policies: THE SHADER WARM-UP below)
  cq = createCompileQueue({ renderer, camera, act, frame, taskRoom: xrRoom });   // THE FRAME BUDGET's compile ms + upload bytes, inside the XR frame room
  renderer.xr.enabled = true;
  renderer.xr.setReferenceSpaceType('local-floor');
  // the XR camera's world pose is written only by three's render and by
  // xrEyeWorld (tick), never re-composed by a getWorld* read (xrEyeWorld)
  const xrc = renderer.xr.getCamera();
  xrc.matrixAutoUpdate = false;
  xrc.matrixWorldAutoUpdate = false;
  if (renderer.xr.setFoveation) renderer.xr.setFoveation(vrsFoveation(RENDER_CFG.foveation));
  installAdaptRes(renderer, RENDER_CFG);   // automatic resolution: VR + phones, never desktop (adaptres.js)
  // the time slicer's XR cap (sched.js setXR): while a session presents,
  // every slice fits a share of the XR frame (its rate when the browser
  // says it, else 90 Hz), whatever the phase; off again at its end
  renderer.xr.addEventListener('sessionstart', () => {
    const s = renderer.xr.getSession();
    setXR(true, s && s.frameRate);
    _visGen++;                  // the frame loop switches: no freeze (watchFrame)
    if (s) s.addEventListener('frameratechange', () => setXR(true, s.frameRate));
    if (s) s.addEventListener('visibilitychange', () => {
      _visGen++;
      // back to 'visible' from 'hidden' / 'visible-blurred' (a headset sleep,
      // the system menu): tracking may have moved — re-seat (headseat.js)
      if (s.visibilityState === 'visible' && _xrVis !== 'visible') _seat.invalidate('visible');
      _xrVis = s.visibilityState;
    });
    // a new session = a new floor; the controllers asleep / back (VRControls,
    // vrui's hint) — the sources are re-read every poll, never kept
    _seat.begin(); _xrVis = (s && s.visibilityState) || 'visible';
    seatOnReset(renderer.xr.getReferenceSpace());
    if (s) s.addEventListener('inputsourceschange', () => { if (vr) vr.sourcesChanged(); });
    // the VR landing: a trigger or a grip lands at once (flightSkip)
    if (s) for (const e of ['selectstart', 'squeezestart']) s.addEventListener(e, () => { if (_flight && _flight.kind === 'vr') flightSkip(); });
  });
  renderer.xr.addEventListener('sessionend', () => { setXR(false); _visGen++; xrRebindLog(true); });
  watchContext(renderer.domElement);
  // handedness → controller map (see handCtrl above). The gun re-attaches the
  // moment the true right hand announces itself, so it never rides the wrong
  // hand even when the controllers connect in the "wrong" order.
  for (const i of [0, 1]) {
    const c = renderer.xr.getController(i);
    c.addEventListener('connected', (e) => {
      const h = e.data && e.data.handedness;
      if (h !== 'left' && h !== 'right') return;
      handCtrl[h] = c;
      if (h === 'right' && mode === 'vr') c.add(weapons.group);
    });
    c.addEventListener('disconnected', () => {
      for (const h of ['left', 'right']) if (handCtrl[h] === c) handCtrl[h] = null;
    });
  }
  // terrain detail tier (low/mid/high) from the live GL context — must run
  // before the first terrain material compiles, the tier is baked into GLSL
  initDetailQuality(renderer);
  // scope focus streaming: ONE Focus drives the terrain/building want-sets AND
  // the terrain shader's wedge uniform. setFocusWedge hands terrain.js the
  // very Vector4 Focus writes in place, so it must run before any material is
  // built (TerrainManager below). Tier 0 makes the whole thing inert.
  focus = new Focus(RENDER_CFG.focus, detailTier(), RENDER_CFG.rings);
  // uZoom: the optic zoom during an optic pass (1 otherwise) — the detail
  // overlay's bands scale with it; the SAME object, so before any material
  setFocusWedge(focus.uWedge, focus.uZoom);
  document.body.appendChild(renderer.domElement);
  addEventListener('resize', () => {
    const a = innerWidth / innerHeight;
    camera.aspect = a;
    if (viewZoom) viewZoom.setAspect(a);
    camera.fov = viewZoom && mode !== 'vr' ? Math.max(viewZoom.fov, viewZoom.bandFov) : desktopVFov(a);
    camera.updateProjectionMatrix();
    renderer.setSize(innerWidth, innerHeight);
    if (lookScale) lookScale.resize();
  });
  await slice();
  act('boot.world');

  // lights: avatars/weapons/bike/lifts only (terrain, panorama AND buildings
  // are pre-lit). Direction, exposure and the sky/sun intensities come from
  // the bake (scenelight.js) so a runtime-lit object is lit like the baked
  // building beside it.
  const sl = sceneLight(meta);
  scene.add(new THREE.HemisphereLight(HEMI.sky, HEMI.ground, sl.hemi * sl.exposure));
  const sun = new THREE.DirectionalLight(SUN.color, sl.sun * sl.exposure);
  sun.position.set(sl.dir[0], sl.dir[1], sl.dir[2]).multiplyScalar(100);
  scene.add(sun);

  buildSky(famScene(scene, 'ring'), tunables);
  setSun(meta);   // the dome's sun; buildPanorama (phase 1) aims it again, the same
  // This world's elevation bands, before anything that bakes them into a
  // shader or a scatter rule. They used to be Upper Engadin literals.
  // per-world station wall art (empty list = no posters, never a 404 frame)
  fetchAsset('photos/placements.json', { prio: 'bulk', as: 'json' })
    .then((pl) => setPosters(pl && pl.placements)).catch(() => {});
  setDetailZones(meta.bands);
  // GROUND-M: the bake's light model + sun + classmap tile size, before the
  // first terrain material compiles (groundmat.js; the tile size is GLSL)
  configureGround(meta);
  // S4: the measured scree / rock contrast (rough.js; none: k = 1, the default look),
  // its k grid in steps ('gm.rough')
  loadRough(baseUrl, meta).then((R) => runSteps(setGroundRoughSteps(R), 'gm.rough'), (err) => console.warn('[rough]', err));
  setTreeline(meta.bands);
  setGrassBands(meta.bands, null);
  terrain = new TerrainManager(famScene(scene, 'ground'), meta, baseUrl);
  // ONE GROUND (rearch-r8k, ground.js): the GROUND-M L0 nodes through vrm_core's
  // Ground — the game server's artefact and sampler; its envelope = the index
  // the ground tree already fetched
  hf = new ClientGround(meta, baseUrl, { index: terrain.gm ? terrain.gm.indexRaw : null });
  // the far tree's programs take the backdrop atmosphere (terrain.js V_FAR); its atlas is
  // terrain.farAtlas (ring.js's gen pass paints it)
  if (terrain.farAtlas) terrain.setAtmos(atmosFor(tunables, meta));
  // THE FRAME BUDGET (R8d, sched.js): the class detectDevice just named (TerrainManager) —
  // its slices, compile ms, upload bytes and XR share (renderconfig device_budgets.<class>.frame)
  setFrameClass(deviceClass());
  vrsInit({ scene, meta, camera, renderer });   // THE VR LOOK (vrstyle/, APP_VRSTYLE): the Quest's flat style; off elsewhere
  // GENSURF CanopyGen (canopygen.js): the canopy pass, only with a crown pack (meta.crowns)
  // (a bad bake block refuses generation, never the game)
  if (!vrStyleOn()) try { installCanopy({ renderer, meta, sea, baseUrl, tree: terrain.gm }); } catch (e) { console.error('[canopy]', e.message); }
  // gs-forestvol: the far canopy volume (forestvol.js), only with a forest shell (meta.forest.shell);
  // the weapons (its optic cameras) are built later: read through a getter
  if (meta.forest && meta.forest.shell && !vrStyleOn()) {   // THE VR LOOK: no far canopy volume (nor its splats)
    try { installForestVol({ renderer, scene, meta, sea, baseUrl, get weapons() { return weapons; }, get cull() { return visCull; } }); } catch (e) { console.error('[forestvol]', e.message); }
  }
  // the ground tree (groundtree.js _opt, t40) and its texture pool's fill:
  // the view's pixel angle; the eye's 1x K everywhere, the optic's true K
  // inside the focus wedge only
  terrain.pixelAngle = viewPixelAngle;
  terrain.basePixelAngle = basePixelAngle;
  terrain.opticPixelAngle = opticPixelAngle;
  // ONE KTX2Loader (its transcoder workers) for every KTX2 the client reads:
  // the GPU-compressed ground materials (groundmat.js, client/materials) on
  // every tier >= 1, block format or not (without one they transcode to
  // RGBA32 at tier 1 size), and the facade atlas (buildings.js, ETC1S) on
  // every tier. detectSupport needs the live renderer to choose a
  // transcode target — ASTC on the Quest — so this has to happen after the
  // WebGLRenderer is up. The far ring ships no texture: its sectors are
  // generated on the device (ringgen.js).
  const wantGround = detailTier() >= 1;
  let ktx2 = null, groundKtx2 = null, groundBlock = false;
  try {
    ktx2 = new KTX2Loader().setTranscoderPath('basis/').detectSupport(renderer);
    // ASK THE DEVICE, do not assume: detectSupport fills workerConfig with
    // the transcode targets this GPU actually has
    const c = ktx2.workerConfig || {};
    const target = c.astcSupported ? 'astc' : c.bptcSupported ? 'bc7'
      : c.dxtSupported ? 'dxt' : c.etc2Supported ? 'etc2' : null;
    if (wantGround) { groundKtx2 = ktx2; groundBlock = !!target; }
  } catch (e) {
    console.warn('ktx2 unavailable:', e.message);
  }
  // R8d: the boot task ends here when it ran past the slice (a seam: every
  // callback set up above touches only what exists above); the rest is 'boot.scene'
  await slice();
  act('boot.scene');
  const collider = new BuildingCollision(meta.buildings && meta.buildings.tile_m);
  buildings = new BuildingManager(famScene(scene, 'buildings'), meta, baseUrl, collider, ktx2);
  // the drip feed (buildings.js _drip): a built tile's programs compiled
  // sliced and first-used before it is shown (the queue's `prepare` class);
  // the facade atlas uploaded in a task of its own
  buildings.prepare = (root) => cq.want(root, { label: 'glb.compile', use: 'glb.firstUse', target: scene, prio: 'prepare' });
  buildings.upload = (t) => renderer.initTexture(t);
  // FARBLD: every building as its far shape in the wedge, to the frame edge,
  // where no GLB tile is drawn (meta.buildings_far; absent = inert)
  farBuildings = new FarBuildings(famScene(scene, 'buildings'), meta, baseUrl, RENDER_CFG.focus && RENDER_CFG.focus.tiers, detailTier());
  // FAR-SWAP (APP_FACADES §11): the GLB tiles' parses measure against the far
  // shapes, which the main view draws past each building's switch distance
  buildings.far = farBuildings;
  // the trail lines (ways-3d-coloured-lines): the zoom instrument's layer —
  // material + keeper in the scene from boot (every warm-up compiles them),
  // the chunk buffers built once the trail index is joined (trailsP)
  trailLines = new TrailLines(famScene(scene, 'structures'), RENDER_CFG.trail_lines, RENDER_CFG.focus && RENDER_CFG.focus.tiers, detailTier(), focus.layer);
  // D1-BLD: the shop signs of the bake's anchor layer (buildings/shopsigns.json,
  // only when the facade census counted signs); one draw, ≤ 150 m around the player
  const _fo = meta.buildings && meta.buildings.facades && meta.buildings.facades.stats
    ? meta.buildings.facades.stats.openings : null;
  if (_fo && _fo.signs > 0) {
    fetchAsset(`${baseUrl}/buildings/shopsigns.json`, { prio: 'bulk', as: 'json' }).then((doc) => {
      shopSigns = new ShopSigns(famScene(scene, 'buildings'));
      const ss = shopSigns.build(doc);
      if (mode) shopSigns.update(player.pos, true);
      console.info('[shopsigns]', JSON.stringify(ss));
    }).catch((e) => { shopSigns = null; console.warn('[shopsigns] not built:', e); });
  }
  // The gazetteer (64 KB; the Map's places, class map, the peak labels)
  loadGazetteer(baseUrl).then((g) => {
    if (!g) return;
    gazetteer = g;
    g.sight = labelSight;
    if (player && player.water) addLakes(gazetteer, player.water.lakes);
    if (riverWays) addRivers(gazetteer, riverWays);
    if (zones) addStations(gazetteer, zones.zones);
    warmApex();
    // BLADES-T: the signposts' destinations re-plan once with the places
    // when the gazetteer lands after the marks were built (no-op otherwise)
    if (trailMarks) trailMarks.setPois(g.pois);
  });
  // no target yet: the ground streams once a card / a GPS fix / the beam
  // names one (loadRetarget); t_near_ready is the first time it is in
  terrain.whenNearReady().then(() => { loadT.t_near_ready = performance.now(); });
  // the Map's light + the frame-wide ≈ elevation: GROUND-M posts, class map
  mapHeights = new MapHeights({ meta, baseUrl, index: terrain.gm ? terrain.gm.indexRaw : null });
  // the aim ray's GM L0 nodes (inspect / centre range / VR rangefinder): only
  // the ones an undecided stretch of the ray asks for (aimray.js), held in the
  // ground's wasm (ClientGround.own); a held working set of AIM_CAP nodes, not
  // an LRU (LABELS-CHURN)
  aimGround = new AimGround({ meta, ground: hf,
    // the labels' lane (labelsight.js) fetches 'bulk': it yields to the ground at a target and to Beam
    fetchNode: (tx, ty, lane) => fetchAsset(`${baseUrl}/groundm/L0/${tx}_${ty}.bin`, { prio: lane === 'labels' ? 'bulk' : 'near', as: 'arrayBuffer' }) });
  _aimMarch = makeAimGround(meta, aimGround, { ground: () => hf, farHf: () => farHf });
  // the labels' line of sight: the same march + the same tiles (lane
  // 'labels', served after the aim; its live lines hold theirs), marched on
  // its own budget inside inView
  labelSight = new LabelSight({ march: _aimMarch, ground: _aimMarch.ground, exact: _aimMarch.exact, aim: aimGround });
  if (gazetteer) gazetteer.sight = labelSight;
  // the rides fetch nothing until startPhase1 — the objects (and every hook
  // below) exist from here on
  funicular = new Funicular(famScene(scene, 'lifts'), baseUrl);
  aerialway = new Aerialway(famScene(scene, 'lifts'), baseUrl, hf);
  // P2 cdb-cull (viscull.js): the main pass skips its provably hidden units —
  // hidden in scene.onBeforeRender (after three posed the XR camera), back
  // in onAfterRender; the managers by getter (some come later in the load)
  visCull = new VisCull({
    get terrain() { return terrain; }, get buildings() { return buildings; }, get hf() { return hf; },
    groundLower, get roadnet() { return roadnet; }, get forest() { return forest; },
    get structDraw() { return structDraw; }, get farBuildings() { return farBuildings; },
    get aerialway() { return aerialway; }, get forestVol() { return forestVolGroups(); }, boreCut,
  }, meta);
  scene.onBeforeRender = (r, s, cam) => visCull.before(r, cam);
  scene.onAfterRender = () => visCull.restore();
  // every ride (funicular + cable cars + gondolas) behind ONE lines array so
  // the station zones treat them uniformly; built once BOTH loaders finish
  const rides = {
    get lines() {
      return (funicular.ready && aerialway.ready)
        ? [...funicular.lines, ...aerialway.lines] : [];
    },
  };
  // where everyone is: map surfaces + beams. Desktop + phone always have the
  // Map (combat = self + terrain only); VR's wrist map is combat-off only
  // the full Map's layers read the data the client already holds (getters:
  // most of it arrives later; nothing here fetches except the register,
  // through inspect's one shared loader)
  const mapSrc = {
    get gazetteer() { return gazetteer; },
    get lakes() { return player && player.water ? player.water.lakes : null; },
    get rivers() { return riverWays; },
    // WaysNet's graph once decoded, until then the map pack's (mappack.js)
    get graph() { return roadnet && roadnet.mapReady ? roadnet.graph : (_mapPack ? _mapPack.graph : null); },
    get forest() { return forest; },
    get shrubs() { return shrubs; },
    collider,
    get guideposts() { return guideposts; },
    objects: () => _mapObjects,
    registry: () => (inspect ? inspect.registry() : null),
    get waypoint() { return beacons ? beacons.waypoint : null; },
    get routes() { return trailRoutes; },
    get amenities() { return trailAmenities; },
    // elevation for the readout: the ground (exact, where its node is in),
    // else the Map's GROUND-M posts (≈); off the extent nothing
    elevAt: (x, z) => {
      if (!hf || x < 0 || z < 0 || x > hf.extentX || z > hf.extentZ) return null;
      const y = hf.height(x, z);
      if (y !== null && y !== undefined) return { y, approx: false };
      const c = mapHeights ? mapHeights.heightAt(x, z) : null;
      return c !== null && c !== undefined ? { y: c, approx: true } : null;
    },
    lv95: (x, z) => (_meta ? gameToLv95(x, z, _meta) : null),
  };
  playerMap = new PlayerMap({ baseUrl, extentX: hf.extentX, extentZ: hf.extentZ,
    rides, getZones: () => zones, meta, heights: mapHeights, src: mapSrc });
  // MAP PACK (meta.mappack, class map): the Map's streets + trails in
  // ~0.2 MB, drawn until WaysNet's own are in (onMapData / trailsP); a bake
  // without it: the Map waits for the ways (ways_slim.json + WAY1 tiles).
  // The decode and the trail buckets IN STEPS (map.pack / map.trails); the
  // ways in first (mapReady) drop the pack's commit, as when they beat it
  loadMapPack(meta, baseUrl).then(async (p) => {
    if (!p || roadnet.mapReady) return;
    const commit = await runSteps(playerMap.linesSteps(p.trails, p.extras.length ? { extra: p.extras } : undefined), 'map.trails');
    if (roadnet.mapReady) return;
    _mapPack = p;
    commit();
    console.info(`[mappack] ${p.graph.ways.length} road / rail runs, ${p.trails.length} trails, ${p.extras.length} extras`);
  });
  playerMap.applyTunables(tunables);
  // the world's red beam spots (region beam_spots → meta, APP_STARTUP §7)
  playerMap.setSpots(meta.beam_spots);
  playerMap.onSpot = (s) => beamSpot(s);
  // leaving the full map by its × (or a card's 👁 Look) on desktop gives the
  // mouse straight back (a click is a user gesture: the lock is honoured).
  // A Map card closes with the Map.
  playerMap.onClose = (why) => {
    // the × on the Map before entry: back to the landing page
    if (why === 'x' && playerMap.preEntry && !mode) { backToLanding(); return; }
    if (tilt && why !== 'tilt') tilt.release();
    if (inspect && inspect.origin === 'map') inspect.close('map');
    if (mode === 'flat' && (why === 'x' || why === 'look' || why === 'beam')) _relock();
    // back to 3D (N, the right button, ×, Esc): the inverse beam's descent onto you
    if (mode === 'flat' && !playerMap.preEntry && !_flight && player && (why === 'toggle' || why === 'rmb' || why === 'x' || why === '')) beginMapFlight('down', playerMap.lastView());
  };
  // 3D → Map in flat play: the inverse beam's rise first, then the Map opens there (mapEnd)
  playerMap.onWantOpen = (o) => {
    if (mode !== 'flat' || playerMap.preEntry || _flight || _entering || !player || o.at || o.headingUp) return false;
    beginMapFlight('up');
    return true;
  };
  // a tap / click on the Map (not a player dot): with a Map card open it
  // closes the card (the next tap opens one); else a card about that point
  // the Map's "?" (before entry and in the game): its briefing; the first
  // in-game Map of a mode opens it by itself (the launch does before entry)
  playerMap.onHelp = () => briefing.open('2d', mode || entryMode);
  playerMap.onOpen = () => { if (mode === 'flat' || mode === 'mobile') briefing.auto('2d', mode); };
  // the Map's × before entry / ⏻ in the game: confirm, then leave
  playerMap.onQuit = (where) => askQuit(where);
  // desktop: the minimap's "2D" = RMB (the phone hides it: tilt / its map button)
  const b2d = $('pmap-2d');
  if (b2d) b2d.addEventListener('click', (e) => { e.stopPropagation(); _rmbToggle(); });
  playerMap.onTapWorld = (x, z, info) => {
    if (!inspect) return;
    if (inspect.isOpen) { inspect.close('map-tap'); return; }
    inspect.openAt(x, z, info);
  };
  beacons = new Beacons(scene);
  beacons.applyTunables(tunables);
  // the inspect card (inspect.js): getters onto the live world objects —
  // most of them arrive after this line
  inspect = new Inspect({
    mode: () => mode,
    // before entry (MAPFIRST): the launch tap's mode, null once a mode runs;
    // no player until the beam (ctx.player null = nothing measured from one)
    preEntry: () => (mode ? null : entryMode),
    enterAt: (x, z, o) => enterAt(x, z, o),
    prefetch: (x, z) => prefetchAt(x, z),
    get weapons() { return weapons; }, get net() { return net; }, get player() { return mode ? player : null; },
    get hf() { return hf; }, get gazetteer() { return gazetteer; }, get roadnet() { return roadnet; },
    get zones() { return zones; }, rides, get rivers() { return rivers; },
    get water() { return player ? player.water : null; },
    get surface() { return player ? player.surface : null; },
    collider, get beacons() { return beacons; }, get playerMap() { return playerMap; },
    get mobile() { return mobile; }, get meta() { return _meta; }, baseUrl,
    get scene() { return scene; }, get canvas() { return renderer.domElement; },
    rangeGround: (x, z) => _rangeGround(x, z),
    rayMarch: (ori, dir, req = 'now') => { _scopeOri.copy(ori); _scopeDir.copy(dir); return _rayMarch(req); },
    aimRefine: (ori, dir) => _aimRefine(ori, dir),
    // the view on screen (desktop / phone): below the band inspect aims the
    // render camera's centre ray; VR: the pulling controller's ray (vrPoint),
    // or no view() → the optic path (the head zoom window's reticle)
    view: () => ((mode === 'flat' || mode === 'mobile') && viewCam
      ? { cam: viewCam, band: viewBand, opticZoom: viewZoom ? viewZoom.monoZoom : 1 }
      : mode === 'vr' && _vrAimOn ? { cam: _vrAimCam, band: false, opticZoom: 1 } : null),
    buildingHit: (eye, dir, max) => _buildingStep(eye, dir, max),
    viewLabels: () => (mode === 'vr' ? _monoLabels().features : (_vLabels ? _vLabels.features : null)),
    combatOn: () => _combatOn(),
    fireHeld: () => !!(flat && flat.fireHeld), dizzy: () => !!(player && player.dizzyActive),
    xrCamera: () => renderer.xr.getCamera(),
    placeAt: (x, z) => placeAt(x, z),
    beamCharge: (bp) => beamCharge(bp),     // the held ⤳ Beam (PC; null: the click as before)
    sfx: { ok: () => Sfx.hitConfirm(), deny: () => Sfx.uiDeny() },
    // the Map card (openAt): is the Map up, its target ring, the phone's
    // pan-clear-of-the-sheet, and 👁 Look
    mapOpen: () => !!(playerMap && playerMap.fullOpen),
    closeMap: () => { if (playerMap && playerMap.fullOpen) playerMap.closeFull('beam'); },
    mapSelect: (p) => { if (playerMap) playerMap.select(p); },
    mapReveal: (x, z, top) => { if (playerMap) playerMap.reveal(x, z, top); },
    lookAt: (x, y, z, label) => _lookAt(x, y, z, label),
    // MAP-D: the trail data (all optional) + the route card's map highlight
    get routes() { return trailRoutes; },
    get amenities() { return trailAmenities; },
    get furniture() { return furniture; },
    objects: () => _mapObjects,        // D1-OBJ kinds on the card (inspect objectNear)
    get pathExtra() { return trailExtra; },
    chips: () => (playerMap ? playerMap.chips : null),
    // highlight route ri (stage si; null clears) and fit o.fit 'route' |
    // 'stage' in view (o.top: the phone sheet's client y)
    mapRoute: (ri, si, o = {}) => {
      if (!playerMap) return;
      playerMap.highlightRoute(ri, si);
      const r = trailRoutes && Number.isInteger(ri) && ri >= 0 ? trailRoutes[ri] : null;
      const b = !r ? null : o.fit === 'stage' && r.stages[si] ? r.stages[si].bbox : o.fit === 'route' ? r.bbox : null;
      if (b) playerMap.fitBox(b, o.top ?? null);
    },
  });
  // station zones: yellow ring + boarding sign per terminal cluster
  // (buildings stay untouched — the zone IS the station interface)
  const tryZones = () => {
    if (!funicular.ready || !aerialway.ready || zones) return;
    zones = act('lifts.zones', () => new StationZones(famScene(scene, 'lifts'), hf, rides, net));
    if (gazetteer) addStations(gazetteer, zones.zones);
    // no tuft or rock ON the funicular: bed + stair walkways (2.3 m half)
    // plus a margin. Open-air points only — the meadow over a bore shares the
    // xz and is ordinary ground. Deck runs are excluded too: under a low
    // carrying structure the tufts poked through the girder. (Trees and
    // shrubs: the bake's lift corridors, bake.py VegCover, t39.)
    const openRuns = [];
    for (const l of funicular.lines) {
      const p = l.path, b = p.bore || [];
      let run = [];
      for (let i = 0; i <= p.points.length; i++) {
        if (i < p.points.length && !b[i]) { run.push(p.points[i]); continue; }
        if (run.length >= 2) openRuns.push({ points: run });
        run = [];
      }
    }
    if (openRuns.length) grass.exclude(corridorMask(openRuns, 2.8));
  };
  funicular.onReady = tryZones;
  aerialway.onReady = tryZones;
  // the static lane ribbons, the bike graph and the road mask come from the
  // WAY1 tiles + ways_slim.json (client/src/ways2.js; ways.json is private,
  // sim / inspect only) — fetched FIRST in startPhase1; maskP
  // and onReady settle then
  roadnet = new WaysDrawNet(famScene(scene, 'structures'), baseUrl, meta);
  roadnet.prepare = (root) => cq.want(root, { label: 'ways.compile', use: 'ways.firstUse', target: scene, prio: 'prepare' });
  // trail marks (TRAILS-C, trailmarks.js): once the ways are indexed
  // (maskP settles after the trail index is built), the map's trail layer,
  // then the marker posts + the signposts at trails/guideposts.json
  // (meta.guideposts; when absent: no signposts, one console.info). Static world furniture, never culled (2 draws). Joins
  // phase 1's promises, so warmLate compiles its programs.
  // MAP-D: meta.pathinfo / routes / amenities (each optional, never throws,
  // one console.info when absent) — the official class + route join
  // (joinTrailData, R4: it may change a run's mark) runs BEFORE the map's
  // final setTrails and TrailMarks.build, so the 3D marks agree with the Map
  const optJson = (key, format) => {
    const m = meta[key];
    if (!m || !m.file) { console.info(`[trails] no meta.${key} in this bake`); return Promise.resolve(null); }
    return fetchAsset(`${baseUrl}/${m.file}`, { prio: 'bulk', as: 'json' }).catch(() => null)
      .then((d) => {
        if (!d || d.format !== format) { console.info(`[trails] ${m.file} unavailable — no ${key}`); return null; }
        return d;
      });
  };
  // the Map's streets + trails as soon as the decode has the graph — before
  // the 3D stages (ways2.js onMapData; mapSrc.graph reads roadnet.mapReady)
  // (with a map pack its trails stay until the join below: they carry the
  // official class the bare index has not got yet)
  roadnet.onMapData = () => { if (playerMap && !_mapPack) playerMap.setTrails(roadnet.trails); };
  roadnet.onPlatforms = (p) => { hf.platforms = p; };   // the grid stands on their plane (game/pave.js platformGround)
  const trailsP = roadnet.readyP.then(() => {
    if (!roadnet.ready) return null;
    const gm = meta.guideposts;
    if (!gm || !gm.file) console.info('[trails] no meta.guideposts in this bake — no signposts');
    const gpP = gm && gm.file
      ? fetchAsset(`${baseUrl}/${gm.file}`, { prio: 'bulk', as: 'json' }).catch(() => null)
      : Promise.resolve(null);
    return Promise.all([gpP, optJson('pathinfo', 'vrm-pathinfo'), optJson('routes', 'vrm-routes'),
      optJson('amenities', 'vrm-amenities')]).then(async ([gp, pi, rd, am]) => {
      if (gm && gm.file && !gp) console.info(`[trails] ${gm.file} unavailable — no signposts`);
      guideposts = gp && Array.isArray(gp.posts) ? gp : null;
      try {
        // the routes first: the reverse index (trail.routes) points into
        // the prepared list (prepRoutes drops a route without stages)
        trailRoutes = prepRoutes(rd);
        if (pi || trailRoutes) {
          const js = joinTrailData(roadnet.trails, pi, trailRoutes ? { routes: trailRoutes } : null);
          console.info('[trails] join', JSON.stringify(js));
          if (playerMap) playerMap.setTrails(roadnet.trails);
        } else if (playerMap && _mapPack) playerMap.setTrails(roadnet.trails);   // the pack's trails go
        trailExtra = prepExtra(pi);
        trailAmenities = prepAmenities(am);
        if (playerMap && (trailRoutes || trailExtra || trailAmenities || pi)) {
          playerMap.setTrailExtras({ routes: trailRoutes, extra: trailExtra,
            structs: pi && Array.isArray(pi.structs) ? pi.structs : null, amenities: trailAmenities });
        }
      } catch (e) { console.warn('[trails] MAP-D data skipped:', e); }
      // the trail lines read every run's stations (surface stretches only);
      // TrailMarks.build drops `st`, so the references are taken first and
      // released when the sliced line build is done
      const lineSt = new Map();
      for (const t of roadnet.trails) if (t.st) lineSt.set(t, t.st);
      // BLADES-T: blades list destinations (guideposts, gazetteer places,
      // route ends) with walking times; pois null until the gazetteer lands.
      // The build in steps ('marks.build'), published at its end; a
      // gazetteer that landed meanwhile re-plans the blades (setPois: a
      // no-op when the build already had the pois)
      const tm = new TrailMarks(famScene(scene, 'structures'));
      const st = await runSteps(tm.buildSteps(roadnet.trails, roadnet.trailByRun, roadnet.graph, gp,
        { routes: trailRoutes, pois: gazetteer ? gazetteer.pois : null }), 'marks.build');
      trailMarks = tm;
      if (gazetteer) tm.setPois(gazetteer.pois);
      console.info('[trails]', JSON.stringify({ index: roadnet.trailStats && roadnet.trailStats.marks, ...st }));
      if (trailLines) {
        trailLines.build(roadnet.trails, lineSt)
          .then((ls) => { if (ls) console.info('[trails] lines', JSON.stringify(ls)); })
          .catch((e) => console.warn('[trails] lines not built:', e));
      }
      // FURN-3D: the same amenity list as 3D furniture (no second fetch;
      // absent → optJson's one console.info, nothing built). Oriented by the
      // nearest way, a cross near a gazetteer peak is a summit cross; drawn
      // set rebuilt in tick (furniture.update); one draw, the signposts' program
      // A bench's front reads the Map's GROUND-M posts + lakes + rivers (benchEnvP).
      // The build in steps ('furn.build': the atlas paint), published at its end
      if (trailAmenities) benchEnvP.then(async () => {
        try {
          // the seat: the Player's own ground query (deck / structure floor /
          // spans bed / skin, no lake float), once the levels are in
          const f = new Furniture(famScene(scene, 'structures'), {
            stand: standOf(player, () => groundLevels.spans && groundLevels.structs),
            peakNear: (x, z, r) => (gazetteer ? gazetteer.within(x, z, r, ['peak']) : null),
            terrain: (x, z) => (mapHeights ? mapHeights.heightAt(x, z) : null),
            water: (x, z, r) => nearestWater(player && player.water ? player.water.lakes : null, riverWays, x, z, r),
          });
          const fs = await runSteps(f.buildSteps(trailAmenities, roadnet.graph), 'furn.build');
          furniture = f;
          if (mode) furniture.update(player.pos, performance.now(), true);
          console.info('[furniture]', JSON.stringify(fs));
        } catch (e) { furniture = null; console.warn('[furniture] not built:', e); }
      });
    });
  }).catch((e) => console.warn('[trails] marks not built:', e));
  // trees / shrubs in a bore, under a deck, on a way or a building: the
  // bake dropped them (bake.py VegCover, t39); the grass is the client's
  roadnet.onReady = () => {
    const sinkM = meta.trees && meta.trees.min_h_m;
    // grass/bushes/rocks on a bore FLOOR: where the terrain under an open
    // underpass is levelled to the paved floor (uncarved) the carve test in
    // Grass.update never fires, so the bore volume excludes them
    grass.exclude((x, z) => {
      const y = hf.height(x, z);
      return y !== null && insideBoreVolume(x, y + 0.1, z, sinkM);
    });
    // "no plants or rocks ... in covered places" (owner 2026-10-12): under a deck
    const dc = roadnet.deckCover;
    if (dc) grass.exclude(dc);
  };
  // trees / shrubs: set up in startPhase1 (Forest/Shrubs.start), their tiles
  // streamed for the target (loadRetarget) and the player (update)
  forest = new Forest(famScene(scene, 'trees'), meta, sea, baseUrl, terrain);
  // THE SPLATS (splatforest.js): the far forest past the sprite band on the Forest's crowns + the volume's canopy
  try { installSplatForest(forest, { scene: famScene(scene, 'trees') }); } catch (e) { console.error('[splats]', e.message); }
  grass = new Grass(famScene(scene, 'grass'), hf, collider, baseUrl);
  grass.setGround(meta, terrain);
  // measured shrub layer (meta.trees.shrubs): real hedges/scrub; the grass system
  // damps its own procedural bushes so the same hedge isn't drawn twice
  shrubs = new Shrubs(famScene(scene, 'shrubs'), meta, sea, baseUrl, terrain);
  grass.shrubLayer = !!(meta.trees && meta.trees.shrubs && meta.trees.shrubs.count);
  jetsuit = new JetSuit(scene);
  bikeView = new BikeView(scene);
  roadnet.maskP.then((r) => {
    if (!r) return;
    grass.roads = r;
    player.roads = r;
  });
  // ── load phase 1: everything location-independent, started at boot
  // (below, R9) behind the Map through the asset queue's `bulk` class (cap,
  // priority low, paused from a ⤳ Beam until the beam-in latch). The ORDER
  // below is the request order: the ways first (lane ribbons, bike graph,
  // road mask, the bore carve), the far ring last. Every promise other code
  // awaits (roadnet.maskP, the rides' onReady, farHf) settles then. Nothing
  // here reads a position: the player has none before the beam. Each piece
  // that joins the scene before entry is compiled off the frame (preWarm,
  // compileAsync); once ALL of it has settled after an entry, the shader
  // warm-up runs once more (warmLate).
  _startPhase1 = (why) => {
    if (_phase1) return;
    _phase1 = true;
    console.info(`[load] phase 1 (${why}) at ${Math.round(performance.now())} ms`);
    const all = [roadnet.start(), forest.start(), shrubs.start(), trailsP];
    // WATER-G: the water is the terrain's (groundmat), a `sheet` lake's a
    // sheet at level_m (sky.js buildSheets); loadWater is the swim / float
    // index (+ name and depth on aim)
    const waterP = loadWater(baseUrl);
    // (the meshes join the scene once their programs are made: joinWater, never awaited here)
    const sheetsP = buildSheets(waterStage(), meta, baseUrl);
    sheetsP.then((m) => joinWater(m, 'water.sheets')).then((m) => { visCull.water.push(...m); })
      .catch((e) => console.warn('[water] sheets join:', e));
    all.push(sheetsP, waterP.then((w) => {
      if (!w) return;
      player.water = w;
      grass.water = w;                      // no tuft/bush/rock on a lake sheet
      setGrassBands(meta.bands, w.lakes);   // real lake level for this world
      if (gazetteer) addLakes(gazetteer, w.lakes);
    }));
    // ways feed the scatter's mid-stream exclusion; ways + river areas the
    // river name on aim (full painted width)
    // the Map's object layer shares this one fetch (loadObjects caches it)
    loadObjects(meta, baseUrl).then((d) => { _mapObjects = d && Array.isArray(d.objects) ? d : null; });
    const riversP = buildRivers(waterStage(), meta, baseUrl, loadObjects(meta, baseUrl)).then((r) => {
      if (r && r.meshes) {
        joinWater(r.meshes, 'water.rivers').then((m) => { visCull.water.push(...m); })
          .catch((e) => console.warn('[water] rivers join:', e));
      }
      if (r && r.ways && r.ways.length) {
        rivers = new RiverIndex(r.ways, r.areas);
        if (grass) grass.rivers = rivers;
        riverWays = r.ways;
        if (gazetteer) addRivers(gazetteer, riverWays);
      }
    });
    all.push(riversP);
    Promise.allSettled([mapHeights ? mapHeights.loaded : null, waterP, riversP]).then(() => _benchEnvDone());
    // TLM structure catalogue (masts, walls, power lines... APP_OBJECTS.md):
    // static merged decor, ~2 draw calls; absent on bakes without objects.json.
    // Walkable archetypes (jetty/platform/basin/berm/dam) register collider
    // entries — walk on the deck, collide with its underside from below. A
    // walkable top is cut open over the stair / ramp wells the lane ribbons
    // dive into (objects.js cutWells): the objects wait for the ribbons.
    // (the mesh build waits for the quiet start's end, heavyReady; the
    // Map's object layer reads loadObjects' data, never this)
    all.push(buildObjects(famScene(scene, 'structures'), meta, baseUrl, collider, Promise.all([roadnet.maskP, heavyReady()]).then(() => roadnet.pieces || null)),
      funicular.start(), aerialway.start());
    // classmap (MLOAD-CM): the 4 m coarse grid here (ground material
    // classes), then the 1 m tiles around the player — tick keeps that 3 x 3
    // resident, and groundmat's near window mirrors it (+ the shade tiles)
    all.push(loadSurface(baseUrl, meta).then((s) => {
      if (!s) return null;
      player.surface = s;
      grass.surface = s;
      terrain.setSurfaceClasses(s);   // the coarse class grid
      warmWaterSoon();                // WATER-G: its V_WATER variants, then the swap
      // the windows' fills + uploads in steps (R8d4); a tile landing meanwhile is queued
      const wins = wantGround ? attachNearWindow(renderer, s, cq) : null;
      if (wantGround) farWin = attachFarWindow(renderer, s, detailTier(), cq);
      // the 1 m tiles: around the player, or the target before entry
      if (mode || _hasTarget) { const p = loadPos(); s.ensure(p.x, p.z); }
      return wins;
    }));
    // GROUND-M material arrays (client/materials): uniforms swap in when
    // they land — no recompile; until then the plain baked ground
    if (groundKtx2) all.push(loadGroundMaterials(detailTier(), groundKtx2, renderer, groundBlock, cq));
    // panorama ring + peak islands: capture their height samplers into ONE
    // far-field sampler (finer pano first) — the gazetteer's occlusion chain
    // and the farpeak label anchor (APP_HORIZON.md)
    // the peak islands join after their program is made + first-used off the frame (cq)
    const hznP = buildHorizon(famScene(scene, 'ring'), meta, baseUrl, tunables, (m) => heavyReady()
      .then(() => cq.want(m, { label: 'horizon.compile', use: 'horizon.firstUse', target: scene, prio: 'prepare' }))).catch((e) => { console.warn('[horizon] not loaded:', e); return null; });
    all.push(Promise.all([
      // the far ring's textures: painted into the far tree's ONE sector atlas
      // (terrain.farAtlas, null without a far tree: nothing generated)
      buildPanorama(famScene(scene, 'ring'), meta, baseUrl, tunables, {
        atlas: terrain.farAtlas || null, getRenderer: () => renderer, gmIndex: terrain.gm ? terrain.gm.indexRaw : null }),
      hznP,
    ]).then(([pano, hzn]) => {
      panoRing = pano;
      vrsHideFar([pano && pano.border, hzn && hzn.mesh]);   // THE VR LOOK: the far ring border + peak islands lie past the void
      const chain = [pano, hzn].filter((s2) => s2 && s2.height);
      if (chain.length) {
        farHf = (x, z) => {
          for (const s2 of chain) {
            const y = s2.height(x, z);
            if (y !== null) return y;
          }
          return null;
        };
        // the post spacing of the grid farHf answers from at (x, z) — the
        // gazetteer's occlusion-march stride. Ring: meta.panorama.sampler
        // (every step_posts-th post of post_m, over n posts from the origin);
        // horizon: meta.horizon.spacing_m over its square; null outside both.
        const pm = pano && pano.height ? meta.panorama : null, hm = hzn && hzn.height ? meta.horizon : null;
        const rs = pm ? pm.sampler.step_posts * pm.post_m : 0, rn = pm ? (pm.sampler.n - 1) * rs : 0;
        const hn = hm ? (hm.n - 1) * hm.spacing_m : 0;
        farHf.step = (x, z) => (pm && x >= pm.origin_game_x && z >= pm.origin_game_z
          && x < pm.origin_game_x + rn && z < pm.origin_game_z + rn ? rs
          : hm && x >= hm.origin_game_x && z >= hm.origin_game_z
            && x < hm.origin_game_x + hn && z < hm.origin_game_z + hn ? hm.spacing_m : null);
        warmApex();
      }
    }));
    buildClouds(famScene(scene, 'ring'), meta);
    // (the building GLBs past load.building_near_m: buildings.allowFar() at
    // the beam-in latch — they are positional)
    // before entry each piece is compiled as it joins the scene
    for (const p of all) Promise.resolve(p).then(preWarm, () => {});
    // a frame after the last of it has landed (so it has joined the scene)
    Promise.allSettled(all).then(() => requestAnimationFrame(() => setTimeout(warmLate, 0)));
  };
  // Spans: per-column multi-level ground/ceiling (APP_BAKER.md §6). The one-set
  // model: the same columns are the resolver's truth AND the carve's paint,
  // so the drawn hole and the collision hole can never disagree. A bake
  // without spans has no multi-level ground until it is re-baked. At boot,
  // class `near`: the player cannot stand in a bore without it.
  if (meta.spans && meta.spans.file) {
    fetchAsset(`${baseUrl}/${meta.spans.file}`, { prio: 'near' })
      .then((buf) => {
        const sp = buf && loadSpans(buf);
        if (!sp) {
          // the bake declares its emitted spans (S6.a): a bad file is loud
          console.error(`[spans] ${meta.spans.file}: missing or corrupt — no bore air`);
          return;
        }
        player.spans = sp;
        setSpans(sp);
      })
      .catch(() => {})
      .finally(() => { groundLevels.spans = true; });
  } else groundLevels.spans = true;

  player = new Player(hf, tunables);
  player.collider = collider;
  // the structure collider (game/structcol.js, APP_BAKER §3 One
  // collider) over structures.bin — the same bytes and the same index the
  // server builds. Empty until (or unless) the declared file arrives; a
  // corrupt file stays empty and says so (the server refuses to boot on it).
  // Whole-file load: 300 k tris is 9.8 MB / ~0.1 s to read + ~0.1 s to index
  // (check-structcol) — both on the client worker pool (R8c2, the op
  // 'structures': pool-structures.js, the file moved there and back); here
  // only the restore (the record's views, the collider's fields: nothing
  // recomputed). At boot, class `near` (decks and bridges are ground).
  // strDraw: the op's second copy of the record, held until the draw job
  // takes it (moved, never copied here)
  const sOpt = { slopeWalkDeg: tunables.locomotion && tunables.locomotion.slope_walk_max_deg };
  let strDraw = null;
  player.structs = new StructCollider(emptyStructures(), sOpt);
  // the draw (structs2.js) is built from the SAME loaded bytes, plus the
  // ways' rails over decks / bores and bore frames (WaysDrawNet) once
  // that document is read — the collider does not wait for it
  // (WAYS-G A5) steps = pipeline/wayslook.json "steps": the raised flights
  // draw the ground stairs' tread colour / riser / nosing band (one stair look)
  // (R8c5) its two procedural textures — the bore lining (roads3d.js
  // boreTexture), the lit / grate detail grain (structs2.js detailTexture:
  // one texture for both) — are painted in the client worker pool: their
  // textures exist now (the materials compile with them) and hold pixels once
  // `ready`; the draw below waits for both (never drawn with an unlanded one).
  // A bake without structures draws none: nothing painted
  const sf = meta.structures && meta.structures.file;
  const boreT = sf ? boreTexture(poolRun) : null, detailT = sf ? detailTexture(poolRun) : null;
  const sdraw = structDraw = new StructDraw(famScene(scene, 'structures'), RENDER_CFG.structures, { bore: boreT && boreT.tex, detail: detailT && detailT.tex, steps: WAYSLOOK.steps });
  if (sf) {
    const strP = fetchAsset(`${baseUrl}/${sf}`, { prio: 'near', status: true })
      .then((r) => (r.body ? r.body : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((buf) => poolRun('structures', { buf, opt: sOpt }, { transfer: [buf], prio: 'near' }))
      .then((o) => {
        const str = restoreStructures(o.rec);
        player.structs = restoreCollider(str, o.col, sOpt);
        strDraw = o.draw;
        return str;
      })
      .catch((e) => { console.error(`[structures] ${sf}: ${e.message} — no structure collision`); return null; })
      .finally(() => { groundLevels.structs = true; });
    // the structures GROUND artifact (T0 - y per vertex, structplan.js
    // FAR_RULE): feet follow the drawn far ring. Absent (every bake before
    // it ships) or unreadable -> the static draw, said once
    const gf = meta.structures.ground && meta.structures.ground.file;
    const gP = gf ? fetchAsset(`${baseUrl}/${gf}`, { prio: 'bulk', status: true })
      .then((r) => (r.body ? r.body : Promise.reject(new Error(`HTTP ${r.status}`))))
      .catch((e) => { console.error(`[structures] ${gf}: ${e.message} — far-ring rule off`); return null; }) : Promise.resolve(null);
    Promise.all([strP, roadnet.maskP.then(() => roadnet.drawDoc || null), gP, boreT.ready, detailT.ready, heavyReady()]).then(([str, dd, gbuf]) => {
      act('structures.build');   // the long-task label of this task (sched.js)
      if (!str) return;
      if (!dd) console.error('[structures] ways.json gave no rails / bore frames — structures drawn without rails, lamps at their mean');
      // the draw's arrays on the pool (the op 'structures.draw':
      // pool-structures.js structDrawArrays + the ground file's decode): the
      // record's copy and the ground file moved (the collider keeps `str`);
      // here structs2.js StructDraw.wrap — the THREE objects
      const args = { rec: strDraw, dd, ground: gbuf, steps: sdraw.steps };
      strDraw = null;
      poolRun('structures.draw', args, { transfer: transferOf([args.rec, args.ground]), prio: 'near' })
        .then((A) => {
          if (A.groundErr) console.error(`[structures] ${gf}: ${A.groundErr} — far-ring rule off`);
          act('structures.wrap', () => sdraw.wrap(A));   // the THREE objects only (R8d label)
          const t = sdraw.stats;
          console.info(`[structures] ${sf}: ${str.nStruct} structure(s), ${sdraw.tris} tris drawn (lit ${t.lit} / bore ${t.bore}), ${t.chunks} chunk(s), ${t.meshes} mesh(es), ${t.rails} rail run(s), ${t.framed} bore frame(s), ${t.contacts} far-rule contact(s)`);
          preWarm();
        })
        .catch((e) => {
          // fail fast (a kind / material without a draw row): loud, never a
          // silent partial draw; the collider above is unaffected
          sdraw.dispose();
          console.error(`[structures] ${sf}: not drawn — ${e.message}`);
        });
    }, (e) => {
      // a texture job failed (the pool's error): loud, nothing drawn; the collider is unaffected
      sdraw.dispose();
      console.error(`[structures] ${sf}: not drawn — ${e.message}`);
    });
  } else groundLevels.structs = true;
  // R8d: the second seam (the player, the collider, every manager exist; the
  // net hooks below check for null); the rest to the XR ask is 'boot.ui'
  await slice();
  act('boot.ui');
  player.forest = forest;
  player.roadnet = roadnet;      // way graph for the bike's auto-follow mode
  // no player.spawn here: the body has no position until the first accepted
  // place (the ⤳ Beam → onPlaced); nothing reads player.pos before a mode

  flat = new FlatControls(renderer.domElement);
  // RMB = World ⇄ Map, synchronously inside the mousedown: leaving the Map
  // re-acquires pointer lock, which the browser only grants in a gesture
  flat.onRmb = () => { if (!rmbQuiet()) _rmbToggle(); };
  vr = new VRControls(renderer, player);
  // the in-headset UI (vrui.js): the laser + trigger on every panel, the menu
  // (right A), the help, the Map panel (its beam = the server's place, as the
  // in-game Map card's), the comfort turn
  vrui = new VrUi({
    rig, head: camera, eye: () => renderer.xr.getCamera(), vr: () => vr, ctrl: (h) => xrCtrl(h),
    playerMap: () => playerMap,
    self: () => (mode === 'vr' && player ? { x: player.pos.x, z: player.pos.z } : null),
    combatOn: () => _combatOn(),
    place: (x, z) => placeAt(x, z),
    blocked: (x, z) => !!(collider.insideFootprint(x, z)
      || (player && player.water && player.water.surfaceAt && player.water.surfaceAt(x, z) !== null)),
    extent: () => (hf ? { x: hf.extentX, z: hf.extentZ } : null),
    groundY: (x, z) => _rangeGround(x, z),
    recenter: () => vrRecenter(),
    exitVr: () => { const s = renderer.xr.getSession(); if (s) s.end().catch(() => {}); },
    // the triggers off every panel: "the data at the point" (vrPoint)
    point: (hand) => vrPoint(hand),
    canPoint: () => mode === 'vr' && !!player && !player.dizzyActive && !_flight,   // the landing: a trigger only lands
    worldHit: (o, d) => _vrWorldHit(o, d),
    card: () => (inspect ? inspect.vrCard : null),
    cardClick: (px, py) => { if (inspect) inspect.vrClick(px, py); },
    zoom: () => (weapons && weapons.headOn ? weapons.headZ : 1),
    // the Quest picture profile (questprof.js): null off the Quest = no menu row
    picture: () => questProfileName(), setPicture: (name) => switchQuestProfile(name),
  });
  vrui.applyTunables(tunables);
  playerMap.onVrMap = (x, z) => vrui.openMap(x, z);
  mobile = new MobileControls(renderer.domElement);
  // the one view's HUD: one fullscreen canvas over the render, desktop + phone
  scopeHud = new ScopeHud($('view-hud'));
  tilt = new TiltSwitch();
  _applyViewTunables(tunables);
  // the phone's compass reads MAGNETIC north; the world is a LV95 grid, so a
  // constant declination − convergence turn makes the view geo-true
  mobile.setHeadingCorrection(headingCorrectionDeg(_meta));
  vignette = new Vignette(camera);
  // dizzy red wash: camera-attached quad (works in flat AND VR, like the
  // vignette), opacity driven by player.dizzy01 each frame
  dizzyMesh = new THREE.Mesh(new THREE.PlaneGeometry(6, 6),
    new THREE.MeshBasicMaterial({
      color: 0xaa0000, transparent: true, opacity: 0,
      depthTest: false, depthWrite: false, fog: false,
    }));
  dizzyMesh.position.z = -0.55;
  dizzyMesh.renderOrder = 998;
  dizzyMesh.visible = false;
  camera.add(dizzyMesh);
  // underwater blue wash: shows only while the EYE is under a lake sheet —
  // i.e. the entry plunge or a dip; the swim eye clamp keeps a settled
  // swimmer's head above the water (APP_PLAYER §Swim mode)
  waterMesh = new THREE.Mesh(new THREE.PlaneGeometry(6, 6),
    new THREE.MeshBasicMaterial({
      color: 0x16455f, transparent: true, opacity: 0,
      depthTest: false, depthWrite: false, fog: false,
    }));
  waterMesh.position.z = -0.55;
  waterMesh.renderOrder = 997;
  waterMesh.visible = false;
  camera.add(waterMesh);
  // VR's beam-in: the LOADING VIEW (xrload.js) from the session's first frame
  // until the world fades in — a room in the tracking space, never black (the
  // DOM overlay is not in the headset)
  xrLoad = new XrLoad({ rig });
  // R8d: a seam (the callbacks above null-check weapons; mode is null to the Beam)
  await slice();
  act('boot.weapons');
  weapons = new Weapons(renderer, scene, camera);
  weapons.applyTunables(tunables);
  // only the optic cameras see focus-only tiles, and the wedge term in the
  // terrain shader is armed for their render alone
  weapons.setFocusLayer(focus.layer);
  // desktop / phone band: the fades at the view zoom Z (focus.setPass mag) —
  // continuous with the view pass below the band; VR / the scope: as ever
  weapons.opticPass = (on) => focus.setPass(on, viewBand && (mode === 'flat' || mode === 'mobile') ? viewZoom.z : 0);
  if (!_combatOn()) weapons.switchTo('monocular');

  net.onTunables = (t) => {
    tunables = t;
    player.setTunables(t);
    weapons.applyTunables(t);
    _applyViewTunables(t);
    // combat toggled off live: force monocular (the only allowed slot)
    if (!_combatOn() && weapons.current !== 'monocular') weapons.switchTo('monocular');
    voice.applyTunables(t);
    playerMap.applyTunables(t);
    beacons.applyTunables(t);
    vrui.applyTunables(t);
    applyGenTunables(t);
    if (scene.fog) scene.fog.density = vrsFogDensity((t.world && t.world.fog_density) || scene.fog.density);
  };
  // Vegetation comes from the BAKE only (meta.vegetation = the world's
  // region.vegetation at bake time). tunables.json is global — a vegetation
  // key there once carried one world's species into every world — so it is
  // never read for vegetation. The forest resolves meta.vegetation itself;
  // grass and shrubs style from an explicit block.
  // the owner sprites' look: region.vegetation.sprite_look, else renderconfig
  // look.veg_sprite (one shared uniform, read by every owner-capable material)
  setVegSpriteLook(meta.vegetation);
  if (meta.vegetation) {
    grass.applyVegetation(meta.vegetation);
    shrubs.applyVegetation(meta.vegetation);
  }
  // R8h2: the world swap's per-family hooks (worldswap.js), now that their
  // subsystems exist — a promote that moved only these families swaps them in
  // place; before this line any moved family reloads. `meta` = the live meta
  // (the root hook writes the moved owned blocks into it)
  registerSwapHook('root', metaHook(meta));
  registerSwapHook('buildings', buildingsHook(buildings));
  registerSwapHook('terrain', terrainHook(hf));
  registerSwapHook('forest', forestHook(forestVolume));
  registerSwapHook('veg', vegHook([forest, shrubs, grass]));
  net.onEvent = onGameEvent;
  net.onLift = (s) => funicular.setServerS(s);
  net.onAerial = (a) => aerialway.setServerS(a);
  // authoritative correction: our reported motion was rejected for several
  // frames, so the server and we disagree about where our body is. Movement is
  // client-simulated, so the server cannot move us — it can only tell us.
  net.onCorrection = (p) => {
    if (_bench) _bench.correction();
    _corr.n++;
    _corr.max = Math.max(_corr.max, Math.hypot(p[0] - player.pos.x, p[1] - player.pos.y, p[2] - player.pos.z));
    player.pos.set(p[0], p[1], p[2]);
    player.vel.set(0, 0, 0);
    player.cancelGlide();
    hf.ensureAround(p[0], p[2]);
  };
  // server restarted / connection dropped: say so and get back in automatically
  net.onDisconnect = () => {
    overlay.style.display = 'flex';
    status.style.color = '#fbbf24';
    setStatus('main.status.lost');
    setTimeout(() => location.reload(), 2500);
  };

  const loader = document.getElementById('loader');
  if (loader) loader.classList.add('done');

  const btnVr = $('btn-vr'), btnFlat = $('btn-flat');
  btnFlat.disabled = false;
  let vrOk = false;
  if (navigator.xr) {
    const ok = await navigator.xr.isSessionSupported('immersive-vr').catch(() => false);
    vrOk = !!ok;
    if (ok) btnVr.disabled = false;
    else devOff(btnVr, 'land.off.vrXr');
  } else if (window.isSecureContext) {
    devOff(btnVr, 'land.off.vrXr');
  } else {
    devOff(btnVr, 'land.off.vrHttps');
  }
  act('boot.start');            // after the XR ask: the landing's cards, __vr, phase 1's start
  // the one card this device should take (landing.js tags it "Recommended")
  recommend(isMobileDevice() ? 'btn-mobile' : vrOk ? 'btn-vr' : 'btn-flat');
  // voice opt-in: getUserMedia MUST run on this pre-entry gesture — Quest's
  // mic permission prompt is a browser overlay that cannot be answered from
  // inside an immersive session (accepting the cert had the same shape)
  voice.applyTunables(tunables);
  const btnVoice = $('btn-voice');
  if (voice.available && (!tunables.voice || tunables.voice.enabled !== false)) {
    btnVoice.disabled = false;
    btnVoice.onclick = async () => {
      try {
        await voice.prime();
        _voiceMsg = { key: 'land.voice.ready' };
        btnVoice.textContent = t(_voiceMsg.key);
        btnVoice.disabled = true;
        if (mode || entryMode) voice.start();     // enabled after the launch tap
      } catch (e) {
        _voiceMsg = { key: 'land.voice.fail', vars: { err: e.message } };
        btnVoice.textContent = t(_voiceMsg.key, _voiceMsg.vars);
      }
    };
  } else {
    btnVoice.style.display = 'none';
  }
  // the launch taps open the Map before entry (every mode); the world is
  // entered from a card's ⤳ Beam there (enterAt) — VR asks for its session
  // on that click
  btnFlat.onclick = () => launch('flat');
  btnVr.onclick = () => launch('vr');
  // mobile mode — shown when touch + mobile UA detected
  const btnMobile = $('btn-mobile');
  const btnGps = $('btn-gps');
  if (btnMobile && !isMobileDevice()) devOff(btnMobile, 'land.off.mob');
  if (btnMobile && isMobileDevice()) {
    btnMobile.disabled = false;
    if (btnGps && 'geolocation' in navigator) {
      btnGps.style.display = '';
      // silent pre-flight: an http origin or an already-blocked permission is
      // knowable without asking, and saying so up front beats letting the user
      // tap and be told "denied" for something they never denied
      gpsPreflight();
    }
    btnMobile.onclick = () => {
      // fullscreen + landscape lock ride THIS user gesture (before any await):
      // fullscreen hides the browser chrome (the address bar eats a big slice
      // of a phone screen), and orientation.lock only works while fullscreen
      enterFullscreen();
      // iOS gyroscope permission must be requested from a user gesture; the
      // briefing's heading line follows its answer
      Promise.resolve(mobile.requestGyroPermission()).then(() => briefing.refresh(), () => briefing.refresh());
      launch('mobile');
    };
  }
  // the Map's briefing (before entry, GPS on, phone): "📍 Beam to my
  // position" enters at the fix; the 3D view's "?" re-opens its briefing
  const miGps = $('mi-gps'), bHelp = $('brief-help');
  if (miGps) miGps.onclick = () => beamToFix();
  if (bHelp) bHelp.onclick = (e) => { e.stopPropagation(); if (mode) briefing.open('3d', mode); };
  if (btnGps) {
    gpsBtnSync();
    btnGps.onclick = (e) => {
      e.preventDefault();
      // in the failed state the button IS the help button: re-firing a request
      // the browser refuses without prompting would just repeat the same dead
      // end, and the panel is the only thing that can move the user forward
      if (gpsFailCause()) { gpsHelpReopen(); return; }
      // start/stop the watch RIGHT HERE: the permission prompt has to ride
      // this tap, and asking now also means the fix is usually already in by
      // the time the user hits "Mobile"
      gps.on = !gps.on;
      if (gps.on) gpsStart(); else gpsStop();
      gpsBtnSync();
    };
  }
  const ghRetry = $('gps-help-retry'), ghClose = $('gps-help-close');
  if (ghRetry) {
    ghRetry.onclick = (e) => {
      e.preventDefault();
      // The retry must ride THIS tap. Nothing may be awaited first: a browser
      // only opens the permission prompt for a request made inside a user
      // gesture, so a retry scheduled after an await would be refused silently
      // and the user would see the card blink for no reason.
      gpsStopWatch();
      gps.err = null;
      gps.on = true;
      gpsHelpHide();
      gpsStart();                 // clears fail/diag/preflight, re-arms the watch
    };
  }
  if (ghClose) ghClose.onclick = (e) => { e.preventDefault(); gpsHelpHide(); };
  bakeShort = welcome.bake.slice(0, 8);
  setStatus('main.status.connected', { bake: bakeShort, build: BUILD,
    nb: meta.buildings ? meta.buildings.count : 0, nt: meta.trees ? meta.trees.count : 0 });
  // dev/debug handle (headless smoke tests + console poking) — no gameplay use
  window.__vr = {
    // the asset queue's request log (class, url, queue / start / end, bytes)
    assets: () => assetLog(),
    // the beam-in overlay's release terms now (fvReady + the smooth count; null
    // outside one) — the startup probe reads which one holds it
    xrload: () => (xrLoad ? xrLoad.state() : null),   // the VR loading view: opacity, readiness, frames drawn (xrload.js)
    flight: () => (_flight ? flightRecord(_flight) : _flightLast),   // the beam flight's record (PC)
    flightLead: (v) => { _flightLead = v || null; },                  // the next flight's lead ('landing' | 'camera')
    arcFps: (v) => { _arcFps = Number.isFinite(v) ? v : null; },       // the in-game arc's frame-rate gate (probe)
    get lookScale() { return lookScale; },                            // the flight's resolution scale (look.js LookScale; probe)
    ovl: () => _beamIn && { at: _beamIn.at, smooth: _beamIn.smooth, gap: _frameGap, warm: _warm,
      fv: _fv && { clean: _fv.clean, busy: _fv.busy, water: !!_fv.water }, veg: vegSettled(player.pos), ready: fvReady(performance.now()), top: !_beamIn.at || topOk(performance.now()), spr: spritesWorn() },
    // the XR path (client/vendor/three-r170-xr.cjs): render() calls while presenting and the ones that
    // found the frame's target unbound (a painter's resetState) and bound it again — questeyes reads it
    xr: () => ({ renders: renderer.xr.frameRenders, rebinds: renderer.xr.frameRebinds }),
    // quest-multiview: the controller's facts (null = not installed: three's per-eye path) + THE SMOKE
    mv: () => renderer.multiview && renderer.multiview.state(),
    mvTest,
    // quest-steady72: the Quest picture profile in force (null: not the Quest) / switch it (questprof.js)
    qp: (name) => (name ? switchQuestProfile(name) : questProfileName()),
    // GENSURF: the device class, the governor's factors, the queue + pools (gensched.js)
    gen: () => ({ ...genState(), canopy: canopyState(), forestvol: forestVolState(), splats: splatForest() ? splatForest().state() : null }),
    // THE SPLATS (splatforest.js): force on (1) / off (0) / the class's tunable (null) — the seam lab's arm
    splat: (f) => { const s = splatForest(); if (s && f !== undefined) s.setForce(f); return s ? s.state() : null; },
    // FIRST-MINUTE (first-minute-measure): the cumulative counters, and the telemetry gen block between two of them
    genSnap: () => genSnap(), genTel: (cur, prev) => genTel(cur, prev),
    // the P2 cull (viscull.js): .enabled toggles it (the probe's in-tab A/B), .stats per family [units, hidden]
    cull: () => visCull,
    // FAR-SWAP arm (the seam lab's A/B; ?farbld=0|1|trim|swap at load): no arg reads it
    farbld: (a) => (a === undefined ? swapArm() : setSwapArm(String(a))),
    // FACADE PREFILTER (buildings.js FAC_AA, quest-shimmer w53): [on, fade start, fade end, aniso] — the seam lab's arms
    // SLEEPER SINK (ways2.js SINK_U, quest-shimmer w53): [lo, hi] px of a sleeper's projected pitch — the seam lab's arms
    sink: (v) => { if (Array.isArray(v)) SINK_U.value.fromArray(v); return SINK_U.value.toArray(); },
    vrstyle: (o) => (o ? vrsTune(o) : vrsState()),   // THE VR LOOK's facts / the void live ({d, bg}; vrstyle/)
    facaa: (v) => { if (Array.isArray(v)) FAC_AA.value.fromArray([...v, ...FAC_AA.value.toArray().slice(v.length)]); return FAC_AA.value.toArray(); },
    // CanopyGen read-back vs its CPU reference (crowns.js; tools/gpuprobe.mjs) — stalls the GPU: dev only
    genCheck: (n) => canopyCheck(n),
    // GL HEALTH since load (glwatch.js; tools/questsmoke.mjs): samples, errors per kind, getError ms
    glwatch: () => (glWatch ? glWatch.state() : null),
    // GENSURF: the ground painter (groundgen.js) — tools/gpuprobe.mjs --ground-check
    ground: { state: () => terrain.ground.state(), read: (k) => terrain.ground.read(k), keys: () => terrain.ground.keys(), refWorld: (k) => terrain.ground.refWorld(k), ensure: (k) => terrain.ground.ensure(k) },
    tp(x, z, yaw = 0, pitch = -0.25) {
      const y = (hf.height(x, z) || 2000) + 40;
      player.spawn(x, y, z);
      focus.reset();
      flat.lookYaw = yaw; flat.lookPitch = pitch;
    },
    // the launch tap + ⤳ Beam at (x, z), as the player does them (tools/gpuprobe.mjs)
    enter(x, z, m = 'flat') { launch(m); return enterAt(x, z, { label: 'probe' }); },
    map(m = 'flat') { launch(m); },   // the Map before entry alone (the probe's Map dwell)
    // the device bench: its state and, once sent, the exact `load` report (tools/benchrun.mjs)
    bench: () => ({ state: _bench ? _bench.state : BENCH ? (_benchRoute ? 'armed' : 'no-route') : 'off', sent: _benchData }),
    // the in-game beam to (x, z), as the Map's red spot does it (beamSpot → placeAt → onPlaced): the server
    // seats the body there and the streaming follows; one retry past the 2 s seat cooldown. Resolves null when
    // moved, else the reason (tools/gpuprobe.mjs --shot: the resident tab moves without a reload). Instant
    // (no arc: the shot aims right after) unless `arc` — the in-game beam's arc / hop as a player gets it
    // the card's held ⤳ Beam as the player does it (inspect.js → beamCharge): hold `ms`, release, beam
    // (before entry: the entry; in play: the arc). Resolves the beam's outcome; __vr.charge() = the record
    chargeBeam(x, z, ms = 1300) {
      const h = beamCharge({ x, z });
      if (!h) return Promise.resolve('no charge');
      return new Promise((r) => setTimeout(r, ms)).then(() => {
        const go = h.stop(h.fill() >= 1);
        if (!go) return 'cancelled';
        return mode ? placeAt(x, z).then((p) => (p ? null : 'refused')) : enterAt(x, z, { label: '' });   // no label: the card names the place
      });
    },
    charge: () => _chargeLast,
    goto(x, z, arc = false) {
      if (!mode) return Promise.resolve(t('main.beam.noMode'));
      if (_combatOn()) return Promise.resolve('combat on');
      const go = () => { _noArc = !arc; return placeAt(x, z).catch(() => null).finally(() => { _noArc = false; }); };
      return go().then((pos) => pos || new Promise((r) => setTimeout(r, BEAM_RETRY_MS)).then(go))
        .then((pos) => (pos ? null : t('main.beam.refused')));
    },
    look(yaw, pitch) { flat.lookYaw = yaw; flat.lookPitch = pitch; if (mobile) { mobile._yawOffset = yaw; mobile._pitchOffset = pitch; } },
    get player() { return player; }, get roadnet() { return roadnet; },
    get terrain() { return terrain; }, get scene() { return scene; },
    get hf() { return hf; }, get renderer() { return renderer; }, get rig() { return rig; },
    get focus() { return focus; }, get weapons() { return weapons; },
    get mobile() { return mobile; }, get grass() { return grass; },
    get shrubs() { return shrubs; }, get warm() { return _warm; },
    // the compile queue (compileq.js state): per class queued / running / done, in flight, made /
    // first-used / gone, uploads, pins, inFrame (the programs a draw built: the acceptance number)
    compileq: () => cq && cq.state(),
    get viewZoom() { return viewZoom; },
    // the view's labels now (_vLabels.features) and whether the farpeak apex sampler is in (tools/gpuprobe.mjs --timeline)
    labels: () => ({ features: (_vLabels && _vLabels.features) || [], farHf: !!farHf }),
    // the main pass's visibility frame (APP_VISIBILITY P0): frames, keys
    // built (change frames), stable / exact frames, the current key's id
    get vis() { return { frames: visMain.frames, keys: visMain.keys, stable: visMain.stableN, exact: visMain.exactN, key: visMain.key && visMain.key.id }; },
  };
  // phase 1 at boot (R9): the location-independent content loads behind the
  // landing page and the Map (bulk class); the ground waits for a target
  _startPhase1('boot');
  renderer.setAnimationLoop(tick);
}

// The ground the beam-in latch waits for: the streaming target before entry,
// the player once a mode runs. nearOk = terrain.nearReady and the
// ground's L0 node block there (ground.js readyAround).
function loadPos() { return mode ? player.pos : _loadAt; }

// the tree / shrub tiles of the draw range around p landed (or failed: the
// latch's dismiss timeout is the backstop) — no tree pops in after the latch
function vegSettled(p) { return forest.nearSettled(p) && shrubs.nearSettled(p); }

// ring tiers (APP_HORIZON.md §8c, ringtiers.js): the far ring's textures
// re-plan (panoRing.want) on a move past ringtiers.json replan_m or a
// pixel-angle change (resize, fov, the view's zoom). No target yet (Map
// first): no eye, they stay at the boot plan (the coarsest level). Pixel
// angle: VR = the class table (ringtiers.json classes.vr), flat / mobile =
// the live camera. (Until the far draw calls want itself.)
let _ringKey = '';
// the view's pixel angle (rad): VR = the class table, flat / mobile = the
// live camera over the drawing buffer — the ring tiers' and the ground
// tree's one source (terrain.pixelAngle)
function viewPixelAngle() {
  if (!camera || !renderer) return null;
  return mode === 'vr' ? classPixelAngle(RING_TIERS, 'vr')
    : pixelAngle(camera.fov, renderer.domElement.height || 1);
}
// t40: the naked eye's 1x pixel angle (VR the class table, flat / mobile the
// view's 1x fov over the drawing buffer) — in the band camera.fov is the
// band's, not the eye's
function basePixelAngle() {
  if (!camera || !renderer) return null;
  return mode === 'vr' ? classPixelAngle(RING_TIERS, 'vr')
    : pixelAngle(desktopVFov(camera.aspect), renderer.domElement.height || 1);
}
// t40: the optic's true pixel angle while the focus wedge is armed (its
// linger too): the VR optic's render target (focus rt_px), the flat band /
// view arm over the drawing buffer (the band renders through monoCam full
// screen); null: no wedge
function opticPixelAngle() {
  if (!focus || !focus.active || !renderer) return null;
  return pixelAngle(focus.vfov, mode === 'vr' ? focus.rtPx : renderer.domElement.height || 1);
}
function ringTick() {
  const cfg = RING_TIERS;
  const eye = (mode || _hasTarget) ? loadPos() : null;
  if (!eye) return;
  const pa = viewPixelAngle();
  const q = Math.max(1, cfg.replan_m || 256);
  const key = `${Math.round(eye.x / q)},${Math.round(eye.z / q)},${Math.round(pa * 1e5)}`;
  if (key === _ringKey) return;
  _ringKey = key;
  panoRing.want({ eye: { x: eye.x, z: eye.z }, pa });
}
// the need score's view (needscore.js, assetq.js setNeedView): the eye =
// the streaming target / the player (none before a target: Map first, the
// queue stays FIFO), the ring tiers' pixel angle (zoom narrows it), the view
// height in px and, once a mode runs, the camera's heading (before entry a
// target has no heading: every footprint counts as in view). Re-scores on a
// target change (force: loadRetarget — a card, a GPS fix, the place reply —
// and the Beam's point), else by needscore.js needDue: a move >= need.replan_m,
// a 15 deg turn or a 10 % pixel-angle change FROM THE LAST SCORED VIEW
// (hysteresis: a head trembling at a bucket edge, a zoom ease do not re-score
// every frame). `at` = the eye to score at (the Beam's point); while that
// Beam is pending (`_needBeam`: until the entry, a failed Beam or a newer
// target's loadRetarget) the pre-entry ticks keep scoring there. A flat / mobile canvas
// under 64 px tall has no meaningful pixel angle (everything would read
// sub-pixel): skipped, the call is owed to the next tick.
let _needLast = null;         // the last scored {x, z, yaw, pa}
let _needOwed = false;        // a forced call skipped (tiny canvas): the next one scores
let _needBeam = null;         // the Beam's point while it is pending
const _needDir = new THREE.Vector3();
function needTick(force = false, at = null) {
  if (!camera || !renderer) return;
  const eye = at || (!mode && _needBeam) || ((mode || _hasTarget) ? loadPos() : null);
  if (!eye) return;
  const vr = mode === 'vr';
  if (!vr && (renderer.domElement.height || 0) < 64) { _needOwed = _needOwed || force; return; }
  const pa = viewPixelAngle();
  const px = vr ? ((RING_TIERS.classes || {}).vr || {}).px || 1760 : renderer.domElement.height;
  let dir = null;
  if (mode) {
    camera.getWorldDirection(_needDir);
    const h = Math.hypot(_needDir.x, _needDir.z);
    if (h > 1e-6) dir = { x: _needDir.x / h, z: _needDir.z / h };
  }
  const v = { x: eye.x, z: eye.z, yaw: dir ? Math.atan2(dir.x, -dir.z) : null, pa };
  const q = Math.max(1, (RING_TIERS.need && RING_TIERS.need.replan_m) || 64);
  if (!force && !_needOwed && !needDue(_needLast, v, q)) return;
  _needLast = v;
  _needOwed = false;
  setNeedView({ eye: { x: eye.x, z: eye.z }, pa, px, aspect: vr ? 1 : (camera.aspect || 16 / 9), dir });
}
function nearOk(p) { return terrain.nearReady && hf.readyAround(p.x, p.z); }

// a launcher this device cannot run (the landing's device cards): dimmed and
// last in its row (.off), the reason in place of what it needs — never hidden
function devOff(btn, key) {
  btn.disabled = true;
  btn.classList.add('off');
  const need = btn.querySelector('.dev-need');
  if (!need) return;
  need.dataset.i18n = key;      // a language switch re-renders it (i18n.js applyI18n)
  need.textContent = t(key);
}

// Best-effort fullscreen + landscape lock (mobile). iPhone Safari has no
// element fullscreen and no orientation lock (only <video> goes full screen):
// every call is feature-detected and failure is silent — the briefing says
// "Add to Home Screen" there instead (fsHomeScreenHint), the world shows
// "turn your phone" in portrait (mobile.js #mob-rotate). iPad Safari has the
// webkit spelling. The ⤳ Beam click retries with a fresh gesture.
function enterFullscreen() {
  try {
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (!req || document.fullscreenElement || document.webkitFullscreenElement) return;
    Promise.resolve(req.call(el, { navigationUI: 'hide' })).then(() => {
      if (screen.orientation && screen.orientation.lock) {
        screen.orientation.lock('landscape').catch(() => {});
      }
    }).catch(() => {});
  } catch { /* not available */ }
}

// ------------------------------------------------------------- MAP FIRST
// Every mode enters the same way (APP_PLAYER §7). A launch tap (🖥 / 📱 / 🥽)
// opens the full-screen 2D Map BEFORE ENTRY — with its briefing the first
// time per mode (briefing.js; its "?" re-opens it): the Map's top bar says it
// is loading + the device's few words (playermap.js HINT). `mode` stays null, `entryMode` is the tap's (the Map /
// card layout: the phone's iff 'mobile'). A Map click opens the inspect card
// — its prefetch loads the near ground there (prefetchAt) — and the card's
// ⤳ Beam is the entry (enterAt): place → the world at that spot, the beam-in
// overlay until the ground is in. The Map alone is a complete use; × asks,
// then goes back to the landing page (askQuit).
function launch(m) {
  if (mode) return;
  const bench = BENCH && _benchRoute && !_bench;    // the bench enters by itself (benchStart)
  // gesture-bound: the AudioContext resume must ride the click
  Sfx.unlock();
  voice.start();                // no-op unless the mic was primed on the landing page
  entryMode = m;
  xrMultiview(m === 'vr');      // quest-multiview: both eyes in one pass on xr_multiview classes (xrmv.js)
  // the flight's lower resolution (the PC's arcs): allocated at the launch tap (+ resize), never at a beam
  if (m === 'flat' && !lookScale) lookScale = new LookScale(renderer);
  overlay.style.display = 'none';
  // GPS on with a fix in this world: the Map opens there; after a ⟳ reload
  // here (a lost GL context, reloadHere) at the spot the player left, pinned
  const back = _resumeAt();
  if (back) playerMap.pin(back.x, back.z, t('main.pin.here'));
  const g = _gpsGame() || back;
  playerMap.openPreEntry(m, g ? { at: { x: g.x, z: g.z, mpp: 4 } } : {});
  startMapLoop();
  if (!bench) briefing.auto('2d', m);
  if (loadT.t_map === null) loadT.t_map = performance.now();
  if (bench) benchStart();
}

// ------------------------------------------------------------ device bench
// `?bench=1` (bench.js): the route of this world, fetched once meta names it
function loadBench(region) {
  fetch(`bench/${encodeURIComponent(region)}.json`, { cache: 'no-cache' })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((j) => { _benchRoute = parseBenchRoute(j); console.info('[bench] route', _benchRoute.id); })
    .catch((e) => {
      console.warn('[bench] no route:', region, e.message);
      benchBanner('main.bench.noRoute', { world: region });
    });
}
// the launch tap with a route: the run begins (it counts from page start) and
// enters at the walk's first point — in the tap's gesture (VR asks for its session)
function benchStart() {
  _bench = new BenchRun(_benchRoute, tunables, wireBytes);
  for (const x of _benchLts.splice(0)) _bench.longTask(...x);
  benchBanner('main.bench.run', { phase: 'entry' });
  const [x, z] = _benchRoute.walk.pts[0];
  enterAt(x, z, { label: 'bench' });
}
// one frame (tick, before the body moves): the sample, the stand, the end
function benchFrame(now) {
  const ri = renderer.info, pm = performance.memory;
  _bench.frame(_frameGap, { calls: ri.render.calls, tris: ri.render.triangles, heap: pm ? pm.usedJSHeapSize : null,
    tex: ri.memory.textures, geo: ri.memory.geometries, res: resScale() });
  if (_bench.state === 'entry' && mode && loadT.t_ovl !== null) {
    _bench.stand(now, player.pos);
    benchBanner('main.bench.run', { phase: 'walk' });
  }
}
// the body this frame, from the route (in place of player.step); null = not driving
function benchDrive(now) {
  const was = _bench.state;
  const p = _bench.drive(now, _frameGap / 1000, (x, y, z) => player._groundY(x, y + 0.5, z),
    (x, z) => { const h = hf.height(x, z); return h !== null ? h : mapHeights ? mapHeights.heightAt(x, z) : null; });
  if (_bench.state !== was) {
    if (_bench.state === 'done') benchDone();
    else benchBanner('main.bench.run', { phase: _bench.state });
  }
  if (!p) return null;
  hf.ensureAround(p.x, p.z);
  player.pos.set(p.x, p.y, p.z);
  player.vel.set(p.vx, p.vy, p.vz);
  player.speed = p.speed;
  const jet = p.state === 'glide';
  player.state = p.state;
  player.gliding = jet;
  player.grounded = !jet;
  if (jet) player.glideDir.set(p.hx, 0, p.hz);
  // the view along the route: flat / phone = the look, VR = the rig (the head stays free)
  if (mode === 'flat') { flat.lookYaw = p.yaw; flat.lookPitch = p.pitch; } else if (mode === 'mobile') {
    mobile._yawOffset = p.yaw; mobile._pitchOffset = p.pitch;
  } else if (mode === 'vr') rig.rotation.y = p.yaw;
  player.yaw = mode === 'vr' ? rig.rotation.y : p.yaw;
  return p;
}
// the run is over: its `load` report (phase 'bench', over the socket), the
// banner; VR leaves the headset session (back to the Map). The body flies on
// as a normal jet-suit.
function benchDone() {
  _bench.stop();
  _loadSent.bench = true;
  try { _benchData = buildLoadReport('bench', 'bench'); } catch (e) { console.warn('[bench] report not built:', e); return; }
  if (net.connected) net.sendLoad(_benchData);
  console.info('[bench] done', _benchData.bench);
  benchBanner('main.bench.done', { id: _benchData.bench.id });
  const xs = mode === 'vr' && renderer.xr.getSession();
  if (xs) setTimeout(() => xs.end().catch(() => {}), 1500);
}
// the bench line (#bench-banner, index.html): what runs now / how it ended
function benchBanner(key, vars) {
  let el = $('bench-banner');
  if (!el) {
    el = document.createElement('div');
    el.id = 'bench-banner';
    document.body.appendChild(el);
  }
  el.textContent = t(key, vars);
}

// the × on the Map before entry: back to the landing page (nothing entered)
function backToLanding() {
  xrMultiview(false);
  briefing.close();
  if (inspect && inspect.isOpen) inspect.close('map');
  playerMap.endPreEntry();
  entryMode = null;
  overlay.style.display = 'flex';
  if (net.roster.length) renderRoster(net.roster);
}

// Quit (#map-quit, confirmed): where 'map' = the Map's × before entry →
// the landing page; 'game' = the in-game Map's ⏻ → a reload (startWorld
// has no inverse for flat / phone; the reload IS the landing page, phase 1
// back from the HTTP cache). Both leave full screen first. Stay / a tap
// beside the box / Esc: nothing happens.
function askQuit(where) {
  const el = $('map-quit');
  if (!el) { if (where === 'map') backToLanding(); return; }
  const game = where === 'game';
  $('mq-q').textContent = t(game ? 'main.quit.game' : 'main.quit.map');
  $('mq-sub').textContent = t(game ? 'main.quit.gameSub' : 'main.quit.mapSub');
  const done = (leave) => {
    el.hidden = true;
    el.onclick = null; $('mq-stay').onclick = null; $('mq-leave').onclick = null;
    removeEventListener('keydown', onKey, true);
    if (!leave) return;
    exitFullscreen();
    if (game) {
      // teardown, explicit before the reload drops the page: GPS watch, wake lock
      // (mobile.hide), the sensors die with the page
      gpsStopWatch();
      if (mobile) mobile.hide();
      location.reload();
    }
    else backToLanding();
  };
  const onKey = (e) => { if (e.code === 'Escape') { e.stopImmediatePropagation(); done(false); } };
  el.onclick = (e) => { if (e.target === el) done(false); };
  $('mq-stay').onclick = () => done(false);
  $('mq-leave').onclick = () => done(true);
  addEventListener('keydown', onKey, true);
  el.hidden = false;
}
// out of full screen (best effort; the phone's landscape lock goes with it)
function exitFullscreen() {
  try {
    if (screen.orientation && screen.orientation.unlock) screen.orientation.unlock();
  } catch { /* not locked */ }
  try {
    const ex = document.exitFullscreen || document.webkitExitFullscreen;
    if (ex && (document.fullscreenElement || document.webkitFullscreenElement)) {
      Promise.resolve(ex.call(document)).catch(() => {});
    }
  } catch { /* not available */ }
}

// the GPS fix in game coordinates when it is usable here, else null
function _gpsGame() {
  if (!gps.on || !gps.fix || !_meta) return null;
  const g = gpsToGame(gps.fix.lat, gps.fix.lng, _meta);
  return g.unsupported || !g.inBounds ? null : g;
}

// The briefing (briefing.js, index.html #map-intro): the 2D Map's and the 3D
// view's controls for this device, drawn. Opens by itself the first time per
// view × mode, again from a "?" (the Map's top bar, the 3D view's
// #brief-help, desktop's ? key). It reads the state at every open.
const briefing = new Briefing($('map-intro'), {
  state: () => ({
    pre: !mode && !!(playerMap && playerMap.preEntry),
    voice: !!(voice && voice.ready),
    others: !_combatOn(),         // the Map's other players + follow: combat off
    wrist: !_combatOn(),          // VR's wrist map: combat off
    spots: !!(playerMap && playerMap.spots.length) && (!mode || !_combatOn()),   // the red beam spots (spotsOn)
    vrSpots: !!(playerMap && playerMap.spots.length) && !_combatOn(),           // … on the in-headset Map panel
  }),
  status: () => _introStatus(),
  gpsBeam: () => !mode && entryMode === 'mobile' && gps.on,
});
// iPhone Safari outside a Home Screen app: no Fullscreen API at all, so the
// browser bars stay — the one cure is the Home Screen (index.html
// apple-mobile-web-app-capable opens it without them)
function fsHomeScreenHint() {
  const el = document.documentElement;
  if (el.requestFullscreen || el.webkitRequestFullscreen) return false;
  const standalone = navigator.standalone === true
    || (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches);
  return !standalone && /iPhone|iPod/i.test(navigator.userAgent);
}

// the phone briefing's outcome lines: heading source, screen wake, GPS, full screen
function _introStatus() {
  const out = [];
  if (fsHomeScreenHint()) {
    out.push(t('main.intro.iosFs'));
  }
  if (gps.on) {
    // a geolocation failure speaks through its DIAGNOSIS (gps.err's raw
    // 'denied' is a lie on a self-signed page); gps.err alone survives for
    // the coordinate complaints, which really are about this world
    out.push(gps.diag ? t('main.intro.gpsDiag', { title: gps.diag.title })
      : gps.fail ? t('main.intro.gpsFail')
      : gps.err ? t('main.intro.gpsErr', { err: _gpsErrText(gps.err) })
      : gps.fix ? t('main.intro.gpsOnAcc', { m: Math.round(gps.fix.acc) }) : t('main.intro.gpsOn'));
  }
  const s = mobile.gyroState;
  out.push(mobile.compassLive ? t('main.intro.compass')
    // a header block is not a phone problem and not a permission: saying
    // "no motion data yet" here would keep the player waiting for events
    // that can never arrive, and tapping settings that cannot help
    : s === 'policy' ? t('main.intro.gyroPolicy')
    : s === 'denied' ? t('main.intro.gyroDenied')
    : s === 'unsupported' ? t('main.intro.gyroNone')
    : s === 'live' ? t('main.intro.gyroLive')
    : t('main.intro.gyroWait'));
  // silence is the working case ('held'/'idle'): speak up only when the
  // screen WILL sleep, so a phone going dark mid-walk is never a mystery
  const w = mobile.wakeState;
  const wt = w === 'policy' ? t('main.intro.wakePolicy')
    : w === 'unsupported' ? t('main.intro.wakeNone')
    : w === 'failed' ? t('main.intro.wakeFailed')
    : '';
  if (wt) out.push(wt);
  return out;
}
// gps.err (a code word: geolocation's error, or the follow tick's coordinate
// complaint) as the briefing's GPS line shows it
function _gpsErrText(e) {
  return e === 'denied' ? t('main.gpsErr.denied') : e === 'no fix' ? t('main.gpsErr.noFix')
    : e === 'timeout' ? t('main.gpsErr.timeout') : e === 'not CH' ? t('main.gpsErr.notCh')
    : e === 'outside world' ? t('main.gpsErr.outside') : t('main.gpsErr.error');
}

// "📍 Beam to my position" (the Map's briefing, GPS on): the entry at the fix,
// after gpsWaitForFix (a good fix at once; a coarse one gets ~3 s to
// sharpen; the watch started on the landing page's 📍 tap). The click's
// activation may be spent by then, so the fullscreen retry is best-effort.
// A refusal stays on the button.
function beamToFix() {
  const b = $('mi-gps');
  const go = () => {
    const g = _gpsGame();
    if (!g) {
      if (b) b.textContent = t(!gps.fix ? 'main.fix.none' : 'main.fix.outside');
      return;
    }
    briefing.close();
    enterAt(g.x, g.z, { label: t('main.fix.label') }).then((msg) => {
      if (!msg || mode) return;
      briefing.open('2d', entryMode);
      if (b) b.textContent = `📍 ${msg}`;
    });
  };
  if (b) b.textContent = t('main.fix.waiting');
  gpsWaitForFix(10000).then(go);
}

// A red beam spot on the Map (PlayerMap.onSpot, meta.beam_spots): one tap =
// the beam, no card. Before entry it IS the entry — enterAt inside the tap's
// own gesture (VR session first, desktop pointer lock, the phone's
// fullscreen + wake lock); a refusal goes to the Map's top bar. In the game
// it is the Map card's in-game beam: the Map closes in the tap, then
// net.place (the server's rules: combat off, in bounds, 2 s) — a refusal
// plays the deny sound.
function beamSpot(s) {
  if (!mode) {
    if (!playerMap.preEntry || _entering) return;
    if (inspect && inspect.isOpen) inspect.close('map-tap');
    enterAt(s.x, s.z, { label: s.label }).then((msg) => { if (msg && !mode) playerMap.flash(`${s.label}: ${msg}`); });
    return;
  }
  if (_combatOn()) { Sfx.uiDeny(); return; }
  if (inspect && inspect.isOpen) inspect.close('map-tap');
  if (playerMap.fullOpen) playerMap.closeFull('beam');
  placeAt(s.x, s.z).then((pos) => (pos ? Sfx.hitConfirm() : Sfx.uiDeny()), () => Sfx.uiDeny());
}

// the beam-in overlay (index.html #beam-in): from the ⤳ Beam click until the
// beam-in latch (tick); the label names the target
let _biTok = 0;
function showBeamIn(label) {
  setHidden(true);              // the world behind the overlay: no frame of it shown (sched hidden)
  if (playerMap) playerMap.setCovered(true);    // the Map under the scrim: frozen, no repaint
  const el = $('beam-in');
  if (!el) return;
  _biTok++;
  el.classList.remove('fade-out');
  el.classList.add('on');
  const sub = $('beam-in-sub');
  if (sub) sub.textContent = label ? `→ ${label}` : '';
}
function beamInSub(key) {
  const sub = $(_flight && _flight.kind !== 'vr' ? 'beam-fly-sub' : 'beam-in-sub');
  if (sub) sub.textContent = t(key);
}
// a ⤳ Beam that got no seat (after its one retry): the card's status line
const BEAM_RETRY_MS = 2200;     // past game/sim.js place()'s 2 s seat cooldown
// (i18n keys: enterAt's fail translates them, {s} = PLACE_TIMEOUT_MS in s)
const BEAM_FAIL = {
  refused: 'main.beam.refused',
  timeout: 'main.beam.timeout',
  offline: 'main.beam.offline',
};
function hideBeamIn(fade = false, off = null) {
  setHidden(false);             // the overlay drops (its fade starts): the world is seen from here
  if (playerMap) playerMap.setCovered(false);   // an open Map repaints now, before its first visible frame
  const el = $('beam-in');
  if (!el || !el.classList.contains('on')) { if (off) off(); return; }
  const tok = _biTok;
  // `off`: the element is gone (the fade ended), once; not when a newer beam-in took over
  const done = () => { if (tok !== _biTok || !el.classList.contains('on')) return; el.classList.remove('on', 'fade-out'); if (off) off(); };
  if (!fade) { done(); return; }
  el.classList.add('fade-out');
  el.addEventListener('animationend', done, { once: true });
  setTimeout(done, 700);
}

// THE BEAM FLIGHT (PC; beamflight.js): the ⤳ Beam click — the 2D stage
// zooms from the Map's view onto (x, z) at the scale of the 3D camera h_m
// above the beam ground looking straight down (m per CSS px at the beam
// ground: 2 h tan(vfov / 2) / the GL canvas's CSS height), the world hidden
// behind it (sched hidden); the place reply hands it the feet (flightPlaced)
function beginFlight(x, z, label) {
  setHidden(true);
  const F = flightCfg(tunables && tunables.flight, deviceClass());
  // H over the near sprite range: top-down never sees a sprite fan
  const N = nakedNear(), H = Math.max(F.h_m, N + F.sprite_pad);
  const d = Math.min(devicePixelRatio || 1, 2);
  const kmpp = 2 * Math.tan(desktopVFov(camera.aspect) * Math.PI / 360) / (renderer.domElement.clientHeight || innerHeight) / d;
  const v0 = playerMap.fullView() || { cx: x, cz: z, mpp: H * kmpp };
  const leadMode = _flightLead || F.lead;
  _flight = { F, H, N, kmpp, stage: 'zoom', cover: new FlightCover($('beam-cover'), playerMap), pace: new FlightPace(F),
    base: null, eye: null, eyeH: 0, pose: null, path: null, yaw1: 0, pitch1: 0, s0: 0, late: false, drew: false, at: -Infinity, n: 0,
    why: null, hurried: false, skip: false, leadMode, lead: leadMode === 'landing', leadCam: null, t: { beam: performance.now() }, terms: {}, log: [], frames: [], test: null };
  // THE MAP WAIT (beamlabels.js): the cover's slow push-in + turn, the beam point's spot + readiness ring
  const cov = _flight.cover, flt = _flight;
  cov.cine({ wide: F.wait_wide, tau_ms: F.wait_tau_ms, rot: F.wait_rot_deg * Math.PI / 180 });
  cov.deco = (g, w, h, v, now) => {
    const c = Math.cos(v.rot || 0), sn = Math.sin(v.rot || 0), dx = (x - v.cx) / v.mpp, dz = (z - v.cz) / v.mpp;
    paintBeamSpot(g, w / 2 + dx * c + dz * sn, h / 2 - dx * sn + dz * c, w / Math.max(1, cov.el.clientWidth), flt.wait ? flt.wait.p : 0, now);
  };
  cov.start(v0, { cx: x, cz: z, mpp: H * kmpp }, F.zoom_ms);
  document.body.classList.add('beam-flight');
  const fl = $('beam-fly');
  if (fl) { fl.hidden = false; $('beam-fly-sub').textContent = label ? `→ ${label}` : ''; }
  _flight.label = label;
}
// the ground's upper bound for the path (clearance, the open heading): the
// ground tree's node max there, else the ground's GM L0 node box
function flightTop(x, z, r) {
  const t = nodeTop(terrain.gm && terrain.gm.index, x, z, Math.max(16, r / 4));
  return t !== null ? t : hf ? hf.upper(x, z) : null;
}
// the place reply (enterAt, after startWorld): the descent's base is the
// feet at the beam point, its end the landing view: the most open heading
// (openHeading), the player's pitch; the ground tree streams the standing set
// from here (leadEye: its ahead pass, groundtree.js); its selection and the
// painter's view are the landing view's while that covers the camera's
// (fl.lead, leadCovers: the near floor is never evicted mid-flight), then
// the camera's (flightCam)
function flightPlaced() {
  const fl = _flight, F = fl.F;
  fl.base = player.pos.clone();
  fl.eyeH = player.eye;
  fl.yaw1 = openHeading(flightTop, fl.base.x, fl.base.z, fl.base.y + fl.eyeH, F.open_n, F.open_m);
  fl.pitch1 = flat.lookPitch;
  if (fl.kind === 'vr') {
    // straight down, looking where the head looks; the optic-flow cap once the world is seen
    fl.path = { pose: (s) => vrPose(s, fl.H, fl.eyeH, fl.look), lift: 0 };
    fl.pace.vmaxAt = (s) => (fl.seen ? vrVmax(s, fl.H, fl.eyeH, F.vr_rate) : Infinity);
  }
  fl.eye = new THREE.Vector3();
  fl.t.placed = performance.now();
  // the 2D stage's floor: h_fade_min, over the sprite range, at most H
  fl.pace.cap = flightSAt(Math.min(fl.H, Math.max(F.h_fade_min, fl.N + F.sprite_pad)), fl.H, fl.eyeH);
  flightAt(fl, 0);
  if (terrain.gm && 'leadEye' in terrain.gm) terrain.gm.leadEye = { x: fl.base.x, y: fl.base.y, z: fl.base.z };
  flightLeadCam(fl);
  if (fl.skip) flightSkip();
}
// the landing view for the painter's view order while the lead holds (the tick)
function flightLeadCam(fl) {
  const c = fl.leadCam = new THREE.PerspectiveCamera(camera.fov, camera.aspect, camera.near, camera.far);
  c.projectionMatrix.copy(camera.projectionMatrix);
  c.position.set(fl.base.x, fl.base.y + fl.eyeH, fl.base.z);
  c.rotation.set(fl.pitch1, fl.yaw1, 0, 'YXZ');
  c.updateMatrixWorld();
}
// THE VR LANDING (beamflight.js vrPose; owner 2026-10-31 "also for the vr
// landing"): the headset shows the loading view (xrload.js) from the ⤳ Beam until the
// start pose passes the readiness test — behind the view it descends toward
// its floor while it fails, as the PC's 2D stage — then the world fades in at
// that height and the rig is lowered straight down onto the beam point:
// translated only (no yaw, pitch or roll: the horizon fixed, the head free),
// eased, its optic flow at most vr_rate (vrVmax), the comfort vignette on that
// flow; the test looks where the head looks. Control at STAND; from the old
// release the rest within vr_hurry_ms; a controller button lands at once.
// tunables flight.vr 'overlay': the old wait behind the loading view (flightMode)
function beginVrFlight() {
  const F = flightCfg(tunables && tunables.flight, deviceClass());
  const N = nakedNear(), H = Math.max(F.vr_h_m, N + F.sprite_pad), leadMode = _flightLead || F.lead;
  _flight = { kind: 'vr', F, H, N, kmpp: 0, stage: 'black', cover: { stop() {} }, pace: new FlightPace({ ...F, vmax: F.vr_vmax, accel: F.vr_accel }),
    base: null, eye: null, eyeH: 0, pose: null, path: null, look: { pitch: 0, yaw: 0 }, rate: 0, seen: false, yaw1: 0, pitch1: 0, s0: 0, late: false,
    drew: false, at: -Infinity, n: 0, why: null, hurried: false, skip: false, leadMode, lead: leadMode === 'landing', leadCam: null,
    t: { beam: performance.now() }, terms: {}, log: [], frames: [], test: null, settle: null, slog: [] };
}
// THE CHARGE (PC; beamcharge.js; owner 2026-10-31 "something semi-manual is
// best"): the card's ⤳ Beam pressed (inspect.js) — the landing preloads from
// the press (before entry the card's prefetch again; in play the ground
// tree's leadEye, the painter's landing view in the tick, the target's
// shaders) and the light column stands there. → a handle { fill(), dist,
// stop(launch) → launch } | null (not PC, a beam running): the old click
function beamCharge(bp) {
  if (!(mode === 'flat' || (!mode && entryMode === 'flat')) || _flight || _entering || !hf || !player) return null;
  const F = flightCfg(tunables && tunables.flight, deviceClass());
  const y = hf.height(bp.x, bp.z) ?? player.pos.y, now = performance.now();
  const dx = bp.x - player.pos.x, dz = bp.z - player.pos.z, d = mode ? Math.hypot(dx, dz) : null;
  const c = _charge = { F, x: bp.x, y, z: bp.z, t0: now, at: -Infinity, ready: 0, leadCam: null, test: null,
    yaw1: d > 1 ? Math.atan2(-dx, -dz) : flat.lookYaw, pitch1: flat.lookPitch };
  if (!mode) prefetchAt(bp.x, bp.z);
  else {
    if (terrain.gm && 'leadEye' in terrain.gm) terrain.gm.leadEye = { x: bp.x, y, z: bp.z };
    hf.ensureAround(bp.x, bp.z);
    warmTarget(bp.x, bp.z);
    const lc = { base: { x: bp.x, y, z: bp.z }, eyeH: player.eye, yaw1: c.yaw1, pitch1: c.pitch1 };
    flightLeadCam(lc);
    c.leadCam = lc.leadCam;
    if (!_beamCol) _beamCol = new BeamColumn(scene, F);
    _beamCol.at(bp.x, y, bp.z);
  }
  const dist = d === null ? '' : d < 1000 ? `${Math.round(d)} m` : `${(d / 1000).toFixed(1)} km`;
  return { fill: () => chargeFill(c.t0, performance.now(), F), dist, stop: (launch) => chargeStop(c, launch) };
}
// the release: launch (the caller beams; the column waits for the flight —
// none within 3 s: it goes, beamFillTick) or cancel (nothing changes)
function chargeStop(c, launch) {
  if (_charge !== c) return false;
  _charge = null;
  const now = performance.now();
  _chargeLast = { ms: Math.round(now - c.t0), launch: !!launch, ready: +c.ready.toFixed(3), test: c.test };
  if (launch) { _colAt = now; return true; }
  if (terrain && terrain.gm && 'leadEye' in terrain.gm) terrain.gm.leadEye = null;
  if (_beamCol) { _beamCol.dispose(); _beamCol = null; }
  return false;
}
// the landing view's readiness while charging: the flight's own test at the standing pose there
function chargeTest(c, now) {
  return flightTest(0, { camera, base: { x: c.x, y: c.y, z: c.z }, eyeH: player.eye,
    pose: () => ({ h: player.eye, dx: 0, dz: 0, pitch: c.pitch1, yaw: c.yaw1 }), tree: terrain.gm,
    march: (o, d, max) => {
      if (!_aimMarch) return null;
      _FLY_MARCH.maxDist = max;
      const r = _aimMarch(o, d, _FLY_MARCH);
      return r ? r.dist : null;
    }, pa: viewPixelAngle(), bld: buildings, now }, c.F);
}
// every flat frame: the column (its readiness: the charge's test, then the
// flight's; flickering while the flight waits on it), the flight's labels
// (the 3D view's own, in the descent) and the arrival card (s < card_s)
function beamFillTick(now) {
  const fl = _flight && _flight.kind !== 'vr' && _flight.kind !== 'map' ? _flight : null;   // the inverse beam: no column, no fill
  if (_charge && terrain && terrain.gm && _aimMarch && now - _charge.at >= _charge.F.test_ms) {
    _charge.at = now;
    const r = chargeTest(_charge, now);
    _charge.ready = readiness(r);
    _charge.test = { white: r.white, tex: r.tex, geo: r.geo, bld: r.bld, rays: r.rays };
  }
  if (fl && fl.base && fl.stage !== 'zoom') {
    if (!_beamCol) _beamCol = new BeamColumn(scene, fl.F);
    _beamCol.at(fl.base.x, fl.base.y, fl.base.z);
  }
  if (_beamCol) {
    if (!fl && !_charge && now - _colAt > 3000) {
      _beamCol.dispose(); _beamCol = null;
      if (terrain.gm && 'leadEye' in terrain.gm) terrain.gm.leadEye = null;
    } else {
      const held = !!fl && fl.stage === 'descend' && !fl.hurried && !fl.hop && fl.pace.ok < 1 && fl.pace.s >= fl.pace.ok - 1e-3;
      _beamCol.update(camera, columnAlpha(fl ? readiness(fl.test) : _charge ? _charge.ready : 1, held, now));
    }
  }
  if (!fl) { if (_fill) { _fill.stop(); _fill = null; } return; }
  const F = fl.F;
  if (!_fill) _fill = new FlightFill(F);
  // THE MAP WAIT (the entry's 2D stage: zoom → hold → its crossfade; beamlabels.js): the names
  // around the beam point pop in one per wait_name_ms (out at the crossfade), the card rotates
  // facts, the cover's ring reads the hold's terms + test (waitProgress)
  const wait = !fl.kind && (fl.stage === 'zoom' || fl.stage === 'hold' || fl.stage === 'fade') && fl.cover && fl.cover.v ? fl : null;
  if (wait) {
    const to = fl.cover.to;
    if (!fl.wait) fl.wait = { names: null, facts: null, p: 0 };
    const W = fl.wait;
    if (!W.names && gazetteer) {
      W.names = waitNames(gazetteer, to.cx, to.cz, F);
      W.facts = waitFacts({ gaz: gazetteer, x: to.cx, z: to.cz, label: fl.label }, F);
    }
    if (fl.k) W.p = waitProgress(fl.k, fl.test, W.p);
    const cv = fl.cover.el;
    _fill.mapNames(W.names || [], fl.stage === 'hold' ? waitShown((W.names || []).length, fl.t.hold, now, F) : 0, fl.cover.v, cv.width / Math.max(1, cv.clientWidth));
  } else _fill.labels(camera, now, () => gazetteer.inView(camera, 10000, hf, groundEnv, farHf, null, { world: true }), fl.stage === 'descend' && !!gazetteer);
  if (fl.base && !fl.card) {
    fl.card = arrivalCard({ label: fl.label, x: fl.base.x, y: fl.base.y, z: fl.base.z, gaz: gazetteer, water: player && player.water, now: Date.now() }, F);
  }
  const c = fl.card && fl.pace.s < F.card_s ? fl.card : wait ? { name: fl.label || '', alt: null, lake: null } : null;
  const fact = wait && fl.stage !== 'fade' && fl.wait.facts ? waitFact(fl.wait.facts, fl.t.beam, now, F) : null;
  _fill.showCard(c, c ? [c.name || t('main.flight.landing'), ...(c.alt !== null ? [t('main.flight.alt', { m: c.alt })] : []),
    ...(c.lake ? [t('main.flight.water', { lake: c.lake.name, c: c.lake.c.toFixed(1) })] : []),
    ...(fact ? [t(fact.key, { ...fact.vars, ...(fact.vars.dir ? { dir: t(`main.dir.${fact.vars.dir}`) } : {}) })] : [])] : []);
}
// THE ARC (PC, an in-game beam; owner 2026-10-31 "a high parabolic effect,
// if fps permit"): the server seated the body at B (onPlaced); the camera
// flies there from the old eye on arcPath, TIMED (arcDur: longer with the
// distance; readiness — flightTest — only slows it to pace_floor; its bound
// goes at arc_max_ms), Space held lands at once. Always on PC (arcPlan): under
// arc_fps at a lower resolution (LookScale; back after control once the view stands: resTick), at every
// frame rate, instant in the entry; a short beam hops (hop_ms, no bound)
function beginArc(from) {
  const F0 = flightCfg(tunables && tunables.flight, deviceClass());
  const F = _arcFps !== null ? { ...F0, arc_fps: _arcFps } : F0;
  const B = player.pos, ox = from.x - B.x, oz = from.z - B.z;
  const plan = arcPlan({ mode, busy: !!(_flight || _entering || _beamIn || _noArc), d: Math.hypot(ox, oz), gapMs: _gapAvg }, F);
  if (!plan) return;
  if (playerMap && playerMap.fullOpen) playerMap.closeFull('beam');
  const path = arcPath(F, { ox, oy: from.y - B.y, oz, eyeH: player.eye, yaw0: flat.lookYaw, pitch0: flat.lookPitch, pitch1: flat.lookPitch,
    top: flightTop, bx: B.x, by: B.y, bz: B.z });
  const now = performance.now(), hop = plan.kind === 'hop';
  const pace = new FlightPace(F);
  pace.timed((hop ? F.hop_ms : arcDur(Math.hypot(ox, oz), F)) / 1000);   // timed: moving from the first frame
  if (hop) pace.lift();                         // near: no readiness bound
  const fl = _flight = { kind: 'arc', hop, F, H: path.apex, N: nakedNear(), kmpp: 0, stage: 'descend', cover: { stop() {} },
    pace, base: B.clone(), eye: new THREE.Vector3(), eyeH: player.eye, pose: null, path,
    yaw1: path.yaw1, pitch1: flat.lookPitch, s0: 0, late: false, drew: false, at: -Infinity, n: 0, why: plan.kind, hurried: false, skip: false,
    leadMode: _flightLead || F.lead, lead: false, leadCam: null, t: { beam: now, placed: now, descend: now }, terms: {}, log: [], frames: [], test: null,
    scaled: false, res: plan.res };
  if (_resBack) { fl.scaled = true; _resBack = null; }   // the last arc's lower scale still stands: kept, owed at this landing
  else if (plan.res < 1 && lookScale) { fl.scaled = true; lookScale.setK(plan.res); }   // nothing allocated (LookScale)
  flightAt(fl, 0);
  flightLeadCam(fl);
  if (terrain.gm && 'leadEye' in terrain.gm) terrain.gm.leadEye = { x: fl.base.x, y: fl.base.y, z: fl.base.z };
  document.body.classList.add('beam-flight');
}
// the arc's stages: paced to s = 1, then control at once (the body stands
// at B since onPlaced, as the instant beam left it)
function arcStage(now) {
  const fl = _flight, F = fl.F;
  if (fl.stage === 'descend') {
    if (!fl.hurried && !fl.hop && now - fl.t.beam >= F.arc_max_ms) { fl.hurried = true; fl.t.hurry = now; fl.pace.hurry(F.hurry_ms / 1000); }
    flightProbe(fl, true, now);
    if (fl.pace.s >= 1) { fl.stage = 'land'; fl.t.land = now; }
  }
  if (fl.stage === 'land') { fl.t.stand = now; endFlight(true); }
}
// THE INVERSE BEAM (PC flat play; owner 2026-10-31 "the toggle to the 2d map
// could be done by an inverse beam! to be consistent"): 3D → Map rises from
// the eye along the entry's path reversed (flightPath from s0 0, the look at
// the press as its landing view) to top-down at H over map_rise_ms, then the
// Map fades in there at that scale (H x kmpp) and opens; Map → 3D zooms the
// cover from the Map's last view onto you at that scale (zoom_ms), crossfades
// (fade_ms) and descends the path over map_drop_ms. Timed, no readiness bound
// (everything stands resident), the lead on the standing eye (nothing near is
// evicted), held and skipped (Space) as a beam. playermap.js onWantOpen /
// onClose start it; dir 'up' | 'down', v0: the Map's last view (down)
function beginMapFlight(dir, v0 = null) {
  const F = flightCfg(tunables && tunables.flight, deviceClass()), up = dir === 'up';
  const N = nakedNear(), H = Math.max(F.h_m, N + F.sprite_pad);
  const d = Math.min(devicePixelRatio || 1, 2);
  const kmpp = 2 * Math.tan(desktopVFov(camera.aspect) * Math.PI / 360) / (renderer.domElement.clientHeight || innerHeight) / d;
  const base = player.pos.clone(), now = performance.now(), yaw1 = flat.lookYaw, pitch1 = flat.lookPitch;
  const path = flightPath(F, { H, eyeH: player.eye, yaw1, pitch1, N, s0: 0, top: flightTop, bx: base.x, by: base.y, bz: base.z });
  const pace = new FlightPace(F);
  pace.timed((up ? F.map_rise_ms : F.map_drop_ms) / 1000);
  pace.lift();                                  // resident: no readiness bound
  const fl = _flight = { kind: 'map', dir, F, H, N, kmpp, stage: up ? 'descend' : 'zoom', cover: new FlightCover($('beam-cover'), playerMap), pace,
    base, eye: new THREE.Vector3(), eyeH: player.eye, pose: null, yaw1, pitch1, s0: 0, late: false, drew: false, at: -Infinity, n: 0,
    why: null, hurried: false, skip: false, leadMode: 'landing', lead: true, leadCam: null, t: { beam: now, placed: now }, terms: {}, log: [], frames: [], test: null,
    path: up ? { pose: (s) => path.pose(1 - s), lift: path.lift } : path, at0: { cx: base.x, cz: base.z, mpp: H * kmpp } };
  flightAt(fl, 0);
  flightLeadCam(fl);
  if (terrain.gm && 'leadEye' in terrain.gm) terrain.gm.leadEye = { x: base.x, y: base.y, z: base.z };
  document.body.classList.add('beam-flight');
  if (!up) fl.cover.start(v0 || fl.at0, fl.at0, F.zoom_ms);
}
// its stages (flightCam): down — zoom (the cover) → fade (the crossfade, the
// descent moving) → descend → control at s = 1; up — descend (the rise) →
// reveal (the Map fades in over the top-down view) → the Map open
function mapStage(now) {
  const fl = _flight, F = fl.F;
  if (fl.stage === 'zoom') {
    if (!fl.cover.zoomed) return;
    fl.stage = 'fade'; fl.t.fade = now;
    fl.cover.fade(F.fade_ms, () => { if (_flight === fl && fl.stage === 'fade') { fl.stage = 'descend'; fl.t.descend = performance.now(); } });
    return;
  }
  if (fl.pace.s < 1 || fl.stage === 'reveal') return;
  fl.t.land = now;
  if (fl.dir === 'up') { fl.stage = 'reveal'; fl.cover.reveal(fl.at0, F.fade_ms, () => { if (_flight === fl) mapEnd(fl); }); return; }
  mapEnd(fl);
}
// the inverse beam's end (or its skip): up — the Map opens at the top view
// under the cover, which goes once the Map has drawn; down — control
function mapEnd(fl) {
  fl.stage = 'land'; fl.t.stand = performance.now();
  if (fl.dir === 'up') {
    const cv = fl.cover;
    fl.cover = { stop() {} };
    endFlight(true);
    playerMap.openFull({ at: { x: fl.at0.cx, z: fl.at0.cz, mpp: fl.at0.mpp }, now: true });
    requestAnimationFrame(() => requestAnimationFrame(() => cv.stop()));
    return;
  }
  endFlight(true);
}
// the path from the crossfade's s0 (flightPath: the swing, the glide, the clearance lift)
function flightBuild(fl, s0) {
  fl.s0 = s0;
  fl.path = flightPath(fl.F, { H: fl.H, eyeH: fl.eyeH, yaw1: fl.yaw1, pitch1: fl.pitch1, N: fl.N, s0, top: flightTop,
    bx: fl.base.x, by: fl.base.y, bz: fl.base.z });
}
// the pose at s: top-down over the beam point up to the crossfade (the 2D
// stage's), then the path (flightPath, built at the crossfade)
function flightPoseAt(fl, s) { return fl.path ? fl.path.pose(s) : nadirPose(s, fl.H, fl.eyeH); }
function flightAt(fl, s) {
  fl.pose = flightPoseAt(fl, s);
  fl.eye.set(fl.base.x + fl.pose.dx, fl.base.y + fl.pose.h - fl.eyeH, fl.base.z + fl.pose.dz);
}
// any key or click in the flight: the camera lands at once (control still at STAND)
function flightSkip() {
  const fl = _flight;
  if (!fl || fl.stage === 'land') return;
  fl.skip = true;
  if (fl.kind === 'map') { fl.t.skip = performance.now(); mapEnd(fl); return; }   // the inverse beam: its end at once
  if (!fl.base) return;                         // before the place reply: at flightPlaced
  if (!fl.path) flightBuild(fl, fl.pace.s);
  fl.lead = false;
  fl.cover.stop();
  setHidden(false);
  const b = $('beam-fly');
  if (b) b.hidden = true;
  fl.pace.lift(); fl.pace.s = 1; fl.pace.v = 0;
  fl.stage = 'land'; fl.t.skip = fl.t.land = performance.now();
  flightAt(fl, 1);
}
// the camera this frame (tick, flat): the descent moves by the LAST frame's
// gap only when that frame drew and was no hitch — a frame not drawn (a
// first-view pass) or a long one is a pause, never a jump. In the hold the 3D
// is under the cover: the 2D stage descends with s (its scale = the height's)
function flightCam() {
  const fl = _flight, F = fl.F, now = performance.now();
  // Space held skip_hold_ms lands at once (the ring fills; flightInput)
  const fill = skipFill(fl.spaceAt, now, F);
  flightHint(fl, fill);
  if (fill >= 1) flightSkip();
  if (_flight !== fl) return;
  if (fl.stage !== 'zoom') {
    // hurried (the cap), a long frame moves too: control never later than the
    // overlay. A hitch is long for THIS device (a 20 fps iGPU arc still moves)
    const hitch = Math.max(F.hitch_ms, 2 * _gapAvg);
    const dt = (fl.drew || fl.stage === 'hold') && (_frameGap <= hitch || fl.hurried) ? Math.min(Math.max(F.dt_max, 1.5 * _gapAvg / 1000), _frameGap / 1000) : 0;
    flightAt(fl, fl.pace.advance(dt));
    // the lead ends where the landing view no longer covers the camera's (an
    // arc's follows it both ways: from the old spot it does not yet)
    if (fl.lead || fl.kind === 'arc' || fl.kind === 'map') {
      const cv = leadCovers(fl.pose, fl.eyeH, camera.fov * Math.PI / 180,
        (x, z) => (flightTop(fl.base.x + x, fl.base.z + z, 64) ?? fl.base.y) - fl.base.y);
      if (fl.lead && !cv) { fl.lead = false; fl.t.lead = performance.now(); } else if (cv && (fl.kind === 'arc' || fl.kind === 'map')) fl.lead = true;
    }
    if (fl.stage === 'hold') fl.cover.track(fl.pose.h * fl.kmpp);
    if (fl.frames.length < F.log_max) {
      fl.frames.push([Math.round(performance.now() - fl.t.beam), +fl.pace.s.toFixed(4), +fl.pose.h.toFixed(1), +_frameGap.toFixed(1), fl.drew ? 1 : 0,
        +(fl.pose.pitch * 180 / Math.PI).toFixed(1), Math.round(Math.hypot(fl.pose.dx, fl.pose.dz))]);
    }
  }
  // the mouse looks around the path's look (lookOffset; the 3D in view only)
  const lk = fl.mlook || (fl.mlook = { yaw: 0, pitch: 0, at: -Infinity, y0: flat.lookYaw, p0: flat.lookPitch });
  const seen = fl.stage === 'descend' || fl.stage === 'land';
  lookOffset(lk, seen ? flat.lookYaw - lk.y0 : 0, seen ? flat.lookPitch - lk.p0 : 0, Math.min(0.1, _frameGap / 1000), now, F);
  lk.y0 = flat.lookYaw; lk.p0 = flat.lookPitch;
  camera.rotation.set(Math.max(-1.5, Math.min(1.5, fl.pose.pitch + lk.pitch)), fl.pose.yaw + lk.yaw, 0, 'YXZ');
  if (fl.kind === 'arc') arcStage(now);
  else if (fl.kind === 'map') mapStage(now);
}
// the flight's input (flat; flightPress): Space starts / ends the skip hold;
// the Map / briefing are held — the last press wins, it runs at landing
// (endFlight) and shows here; a stray press shows the skip line
function flightInput(type, e) {
  const fl = _flight, p = flightPress(type, e);
  if (p.space === 'down') fl.spaceAt = performance.now();
  else if (p.space === 'up') fl.spaceAt = null;
  if (p.act) fl.queued = p.act;
  if (type === 'keydown' || type === 'pointerdown') fl.pressed = true;
}
// the hint line (#beam-skip): the held press, the skip ring
function flightHint(fl, fill) {
  const el = $('beam-skip');
  if (!el) return;
  const show = !!(fl.pressed || fill > 0);
  if (el.hidden === show) el.hidden = !show;
  if (!show) return;
  el.style.setProperty('--fill', fill.toFixed(3));
  const txt = (fl.queued === 'map' ? t('main.flight.mapAfter') + ' · ' : fl.queued === 'brief' ? t('main.flight.briefAfter') + ' · ' : '') + t('main.flight.holdSkip');
  const s = $('beam-skip-t');
  if (s && s.textContent !== txt) s.textContent = txt;
}
// the VR landing this frame (tick, VR; the rig follows fl.pose): the look is
// the head's; it moves as flightCam's (a frame not drawn or a hitch: a pause),
// behind the black too, never in the fade; fl.rate = the optic flow (vignette)
const _vrDir = new THREE.Vector3();
function flightVr(xrCam) {
  const fl = _flight, F = fl.F;
  xrCam.getWorldDirection(_vrDir);
  fl.look.yaw = Math.atan2(-_vrDir.x, -_vrDir.z);
  fl.look.pitch = Math.asin(Math.max(-1, Math.min(1, _vrDir.y)));
  const h0 = fl.pose.h;
  const dt = fl.stage !== 'fade' && (fl.drew || fl.stage === 'black') && (_frameGap <= F.hitch_ms || fl.hurried) ? Math.min(F.dt_max, _frameGap / 1000) : 0;
  flightAt(fl, fl.pace.advance(dt));
  fl.rate = dt > 0 && fl.seen ? Math.abs(Math.log(fl.pose.h / h0)) / dt : 0;
  if (fl.lead && !leadCovers(fl.pose, fl.eyeH, camera.fov * Math.PI / 180,
    (x, z) => (flightTop(fl.base.x + x, fl.base.z + z, 64) ?? fl.base.y) - fl.base.y)) { fl.lead = false; fl.t.lead = performance.now(); }
  if (fl.frames.length < F.log_max) {
    fl.frames.push([Math.round(performance.now() - fl.t.beam), +fl.pace.s.toFixed(4), +fl.pose.h.toFixed(1), +_frameGap.toFixed(1), fl.drew ? 1 : 0,
      +(fl.pose.pitch * 180 / Math.PI).toFixed(1), +fl.rate.toFixed(3)]);
  }
}
// the VR landing's optic flow as the comfort vignette's speed (full at vr_vig_rate)
function flightVig() {
  const fl = _flight, c = tunables && tunables.comfort;
  if (!fl || fl.kind !== 'vr' || !c) return 0;
  return c.vignette_speed_min + (c.vignette_speed_max - c.vignette_speed_min) * Math.min(1, fl.rate / fl.F.vr_vig_rate);
}
const _FLY_MARCH = { skip: true, maxDist: 0 };
function flightCheck(s, now) {
  const fl = _flight;
  return flightTest(s, { camera, base: fl.base, eyeH: fl.eyeH, pose: (q) => flightPoseAt(fl, q), tree: terrain.gm,
    march: (o, d, max) => {
      if (!_aimMarch) return null;
      _FLY_MARCH.maxDist = max;
      const r = _aimMarch(o, d, _FLY_MARCH);
      return r ? r.dist : null;
    }, pa: viewPixelAngle(), bld: buildings, now }, fl.F);
}
function flightNote(fl, r, kind, now) {
  if (fl.log.length < fl.F.log_max) fl.log.push({ t: Math.round(now - fl.t.beam), kind, stage: fl.stage, s: +r.s.toFixed(4), cam: +fl.pace.s.toFixed(4),
    ok: +fl.pace.ok.toFixed(4), h: +r.h.toFixed(1), white: r.white, tex: r.tex, geo: r.geo, bld: r.bld, rays: r.rays, sky: r.sky, cx: r.cx, texAt: r.texAt, chain: r.chain, pass: r.ok });
}
// one readiness probe of the pace's ladder (every other test_ms slot), else a
// view test at the camera — the entry's descent and the arc
function flightProbe(fl, spr, now) {
  const c = (fl.n & 1) ? null : fl.pace.probe(now);
  if (c !== null) {
    fl.n++;
    const r = flightCheck(c, now);
    // a pose inside the sprite range waits for the owner sprites (the old sprite gate)
    if (r.ok && !spr && flightPoseAt(fl, c).h < fl.N) { r.ok = false; r.spr = 1; }
    fl.pace.result(c, r.ok);
    fl.test = r;
    flightNote(fl, r, 'probe', now);
  } else if (now - fl.at >= fl.F.test_ms) {
    fl.n++; fl.at = now;
    flightNote(fl, flightCheck(fl.pace.s, now), 'view', now);
  }
}
// the old overlay's release terms (the moment the beam-in overlay would have
// gone): each one's first time in the record (fl.terms), the release itself
// fl.t.rel — from it the flight is hurried (control never later than today)
function flightTerms(fl, now) {
  const T = fl.terms, rel = (k, v) => { if (v && T[k] === undefined) T[k] = Math.round(now - fl.t.beam); return v; };
  const latch = rel('latch', !!_beamIn.at), warm = rel('warm', _warmPost), top = rel('top', topOk(now)), spr = rel('spr', spritesOk(now));
  const acts = rel('acts', firstActs.done), sm = rel('smooth', _beamIn.smooth >= OVL.frames);
  if (fl.t.rel === undefined && latch && ((warm && top && spr && acts && sm) || now - _beamIn.at >= OVL.max)) {
    fl.t.rel = now;
    fl.held = { stage: fl.stage, s: +fl.pace.s.toFixed(4), ok: +fl.pace.ok.toFixed(4), by: warm && top && spr && acts && sm ? 'terms' : 'ovl_max',
      test: fl.test ? { white: fl.test.white, tex: fl.test.tex, geo: fl.test.geo, bld: fl.test.bld } : null };
  }
  return { latch, warm, top, spr, acts };
}
// THE FLIGHT's stages (tick, in the beam-in block, instead of the overlay's
// release): zoom (the 2D stage) → hold (the 3D under the cover until the
// latch, the warm-up pass, top, first acts AND the pose passes flightTest;
// from the latch the 2D stage descends toward its floor while the pose
// fails) → fade (the crossfade; the path is built) → descend (paced:
// FlightPace, one test a slot, the current pose on the odd ones for the
// record; a pose inside the sprite range waits for the owner sprites) → land
// (s = 1: the tree's eye is the feet) → 'flight' at STAND (nearOk +
// nearColReady; load.dismiss_timeout_ms after landing it goes anyway,
// logged). From the old overlay's release (flightTerms) the hold fades and
// the descent is hurried: control never later than the overlay gave it
function flightStage(now) {
  const fl = _flight, F = fl.F;
  if (!fl.base) return null;
  if (fl.kind === 'vr') return vrFlightStage(fl, now);
  const k = fl.k = flightTerms(fl, now);       // (the Map wait's ring reads them: beamFillTick)
  if (fl.stage === 'zoom') {
    if (!fl.cover.zoomed) return null;
    fl.stage = 'hold'; fl.t.hold = now;
  }
  if (fl.stage === 'hold') {
    if (now - fl.at >= F.test_ms) { fl.at = now; fl.test = flightCheck(fl.pace.s, now); flightNote(fl, fl.test, 'hold', now); }
    const ready = k.latch && k.warm && k.top && k.acts && !!fl.test && fl.test.ok;
    // from the latch the 2D stage descends (flightCam) toward its floor while
    // the 3D at its height fails the test, and stops where it passes
    if (_beamIn.at && fl.test) fl.pace.ok = fl.test.ok ? fl.pace.s : fl.pace.cap;
    if (!ready && fl.t.rel === undefined) return null;
    if (!ready) console.warn('[load] flight crossfade by the overlay release:', fl.held, fl.test, latchState());
    fl.why = ready ? 'ready' : 'rel';
    // the crossfade at the pose passed: the path from here (s0)
    flightBuild(fl, fl.pace.s);
    fl.pace.ok = ready ? Math.max(fl.test.s, fl.pace.s) : fl.pace.s;
    fl.pace.cap = 1; fl.pace.step = F.step0;
    fl.pace.timed(F.entry_dur_ms / 1000);       // the descent: timed from here, readiness only slows it
    fl.stage = 'fade'; fl.t.fade = now; fl.hFade = fl.pose.h;
    setHidden(false);
    fl.cover.settle(F.fade_ms, now);           // the wait's move back to the matched view over the crossfade
    fl.cover.fade(F.fade_ms, () => {
      if (_flight !== fl || fl.stage !== 'fade') return;
      fl.stage = 'descend'; fl.t.descend = performance.now();
      const b = $('beam-fly');
      if (b) b.hidden = true;
    });
  }
  if (fl.stage === 'fade' || fl.stage === 'descend') {
    if (fl.t.rel !== undefined && !fl.hurried) {
      fl.hurried = true; fl.t.hurry = now;
      fl.pace.hurry(F.hurry_ms / 1000);
      console.warn('[load] flight hurried at the overlay release:', fl.held, latchState());
    }
    flightProbe(fl, k.spr, now);
    if (fl.stage === 'descend' && fl.pace.s >= 1) { fl.stage = 'land'; fl.t.land = now; }
    return null;
  }
  if (fl.stage === 'land') {
    // STAND = the latch (met once, as the overlay's flow took it), else now;
    // collision live
    const stand = (k.latch || nearOk(player.pos)) && buildings.nearColReady;
    if (!stand && now - fl.t.land < ((RENDER_CFG.load && RENDER_CFG.load.dismiss_timeout_ms) ?? 30000)) return null;
    if (!stand) console.warn('[load] flight: control by timeout, near ground not resident', latchState());
    fl.t.stand = now;
    flightNote(fl, flightCheck(1, now), 'land', now);
    return 'flight';
  }
  return null;
}
// the VR landing's stages (flightStage's while kind 'vr'; 'black' = the world unseen behind the
// loading view, xrload.js): black (the
// warm-up set and the test at the pose; failing, it descends toward its floor unseen) → fade (from black
// at that height, standing still) → descend (paced, the optic-flow cap) →
// land → 'flight' at STAND. The old VR release (latch, top, sprites, first
// acts, vegetation; fl.t.rel) lifts the black and hurries the rest; a landing
// skipped while black stays black until it (as the old wait)
function vrFlightStage(fl, now) {
  const F = fl.F, T = fl.terms, rel = (k, v) => { if (v && T[k] === undefined) T[k] = Math.round(now - fl.t.beam); return v; };
  const latch = rel('latch', !!_beamIn.at), top = rel('top', topOk(now)), spr = rel('spr', spritesOk(now)), acts = rel('acts', firstActs.done);
  const veg = rel('veg', latch && vegOk(now));
  if (fl.t.rel === undefined && latch && top && spr && acts && veg) fl.t.rel = now;
  if (fl.stage === 'black') {
    // THE WARM-UP behind the black (the PC's behind its 2D stage): the set at
    // the latch (bulk resumes there), the fade waits for it — no first-use
    // compile inside the descent. Class `prepare` (xr-stalls: at `behind` the
    // GLB tiles' prepare jobs starved it — 3 first uses of ~500 ms each landed
    // in play 3 min later, session 870); the old release caps the black only
    // once the set is through or vr_warm_ms after the latch
    if (latch && !_warmPost) warmShaders('prepare');
    if (latch && fl.t.warm0 === undefined) fl.t.warm0 = now;
    const warm = rel('warm', _warmPost && _warm === 'done');
    if (now - fl.at >= F.test_ms) { fl.at = now; fl.test = flightCheck(fl.pace.s, now); flightNote(fl, fl.test, 'hold', now); }
    if (latch && fl.test) fl.pace.ok = fl.test.ok ? fl.pace.s : fl.pace.cap;
    const ready = latch && warm && top && acts && veg && !!fl.test && fl.test.ok;
    const capped = fl.t.rel !== undefined && (warm || now - (fl.t.warm0 ?? now) >= (F.vr_warm_ms ?? 0));
    const st = latch ? vrSettle(fl, now) : null;
    if (!ready && !capped) return null;
    // THE SETTLE (w59; owner 2026-11-03 "you could even wait a bit longer before setting off the jump with a half
    // loaded world"): from here (fl.t.go) the view holds until the near world is resident and the frame rate steady
    // (xrload.js settleTerms over vrSettle), at most vr_settle_max_ms; a controller button still lands at once
    if (fl.t.go === undefined) fl.t.go = now;
    const settled = rel('settle', st.ok);
    if (!settled && now - fl.t.go < (F.vr_settle_max_ms ?? 0)) return null;
    if (!settled) console.warn('[load] VR landing: the settle capped at vr_settle_max_ms', st);
    if (!ready) console.warn('[load] VR landing: the black lifted by the old release', warm ? '' : '(warm-up set still running)', fl.test, latchState());
    fl.why = ready ? 'ready' : 'rel';
    fl.pace.ok = fl.pace.s; fl.pace.cap = 1; fl.pace.step = F.step0; fl.pace.v = 0;
    fl.stage = 'fade'; fl.t.fade = now; fl.hFade = fl.pose.h; fl.seen = true;
    setHidden(false);
  }
  if (fl.stage === 'fade' && now - fl.t.fade >= F.vr_fade_ms) { fl.stage = 'descend'; fl.t.descend = now; }
  if (fl.stage === 'fade' || fl.stage === 'descend') {
    if (fl.t.rel !== undefined && !fl.hurried) { fl.hurried = true; fl.t.hurry = now; fl.pace.hurry(F.vr_hurry_ms / 1000); }
    if (fl.stage === 'descend') flightProbe(fl, spr, now);
    if (fl.stage === 'descend' && fl.pace.s >= 1) { fl.stage = 'land'; fl.t.land = now; }
    return null;
  }
  if (fl.stage === 'land') {
    const stand = (latch || nearOk(player.pos)) && buildings.nearColReady && (fl.seen || fl.t.rel !== undefined);
    if (!stand && now - fl.t.land < ((RENDER_CFG.load && RENDER_CFG.load.dismiss_timeout_ms) ?? 30000)) return null;
    if (!stand) console.warn('[load] VR landing: control by timeout', latchState());
    fl.t.stand = now;
    flightNote(fl, flightCheck(1, now), 'land', now);
    return 'flight';
  }
  return null;
}
// THE SETTLE's inputs (vrFlightStage, from the latch; xrload.js settleTerms): the frame window pushed every frame,
// the near world's counts re-read every XRL_MS — on-screen ground tiles wanted / holes (groundgen visState none), GROUND-M
// nodes pending, building tiles within the cull not drawn, tree chunks queued, compile jobs → fl.settle {terms, ok, frac}
function vrSettle(fl, now) {
  const F = fl.F;
  if (!fl.fw) fl.fw = new FpsWindow(F.vr_settle_win_ms);
  fl.fw.push(now);
  if (fl.settle && now - fl.settle.at < XRL_MS) return fl.settle;
  const vs = terrain && terrain.ground ? terrain.ground.visState() : null, C = cq.state().classes || {};
  const s = { vis: vs ? [vs.want, vs.none] : null, gm: terrain && terrain.gm ? terrain.gm.pendingNear : 0,
    bld: buildings && player ? buildings.pendingNear(player.pos) : 0, trees: forest ? forest.queue.size + (forest.job ? 1 : 0) : 0,
    cq: Object.values(C).reduce((a, c) => a + (c.queued || 0) + (c.running || 0), 0), fps: fl.fw.read() };
  const T = settleTerms(s, { ground: F.vr_settle_ground, gm: F.vr_settle_gm, bld: F.vr_settle_bld, trees: F.vr_settle_trees,
    cq: F.vr_settle_cq, fps: F.vr_settle_fps, gap_ms: F.vr_settle_gap_ms, win_ms: F.vr_settle_win_ms });
  fl.settle = { at: now, ...T, n: { vis: s.vis, gm: s.gm, bld: s.bld, trees: s.trees, cq: s.cq, fps: +s.fps.fps.toFixed(1), gap: +s.fps.gap.toFixed(1) } };
  if (fl.slog && fl.slog.length < 240) fl.slog.push([Math.round(now - fl.t.beam), +T.frac.toFixed(2), vs ? vs.miss : null, s.vis ? s.vis[1] : null, s.gm, s.bld, s.trees, s.cq, +s.fps.fps.toFixed(1)]);
  return fl.settle;
}
// the flight ends: landed (control: the eye and the view the player had; the
// late warm-up set it held) or not (the Beam failed / was left)
function endFlight(landed) {
  const fl = _flight;
  if (!fl) return;
  _flight = null;
  fl.cover.stop();
  document.body.classList.remove('beam-flight');
  const b = $('beam-fly');
  if (b) b.hidden = true;
  const sk = $('beam-skip');
  if (sk) sk.hidden = true;
  if (_beamCol) { _beamCol.dispose(); _beamCol = null; }   // the column goes with the flight
  if (_fill) { _fill.stop(); _fill = null; }
  // the arc's lower resolution ramps back once the view stands (resTick), never on the control frame
  if (fl.scaled) _resBack = { at: performance.now(), stillAt: performance.now(), v: null, F: fl.F, ramp: null, k0: 1 };
  if (terrain && terrain.gm && 'leadEye' in terrain.gm) terrain.gm.leadEye = null;
  if (landed && fl.kind !== 'vr') {
    camera.position.set(0, player.eye, 0);
    // the mouse's offset kept: the view the last frame showed (3D → Map: the look at the press, under the Map)
    const L = fl.kind === 'map' && fl.dir === 'up' ? { yaw: fl.yaw1, pitch: fl.pitch1 } : landLook(fl.yaw1, fl.pitch1, fl.mlook);
    flat.lookYaw = L.yaw; flat.lookPitch = L.pitch;
    flat.keys.clear();                          // keys released during the flight never reached keyup
    flat.fireHeld = false;                      // nor the button's mouseup (swallowed in the flight)
    // the press held in the flight runs now (flightInput): the Map, the briefing
    if (fl.queued === 'map' && mode === 'flat' && fl.kind !== 'map') _rmbToggle();   // the inverse beam: a Map press was its own
    else if (fl.queued === 'brief' && mode === 'flat') briefing.open('3d', 'flat');
  }
  if (landed && fl.late) requestAnimationFrame(() => setTimeout(warmLate, 0));
  fl.t.end = performance.now();
  fl.landed = !!landed;
  _flightLast = flightRecord(fl);
}
// the arc's full resolution back (resBack): once the view (the body, the
// look) has stood still past the settle, the scale ramps to 1 over
// res_ramp_ms (LookScale.setK: nothing allocated, no long frame)
function resTick(now) {
  const r = _resBack;
  if (r.ramp === null) {
    const v = [player.pos.x, player.pos.y, player.pos.z, flat.lookYaw, flat.lookPitch];
    if (!r.v || v.some((x, i) => Math.abs(x - r.v[i]) > 0.002)) { r.v = v; r.stillAt = now; }
    if (!resBack(r.at, r.stillAt, now, r.F)) return;
    r.ramp = now; r.k0 = lookScale.k;
  }
  const k = r.k0 + (1 - r.k0) * Math.min(1, (now - r.ramp) / r.F.res_ramp_ms);
  lookScale.setK(k);
  if (k >= 1) _resBack = null;
}
function flightRecord(fl) {
  const rel = (v) => (v === undefined ? null : Math.round(v - fl.t.beam));
  return { kind: fl.kind || 'entry', stage: fl.stage, landed: fl.landed ?? null, why: fl.why, held: fl.held ?? null, terms: fl.terms, hFade: fl.hFade ?? null, s0: fl.s0,
    H: fl.H, N: fl.N, yaw1: fl.yaw1, lift: fl.path ? fl.path.lift : null, hurried: fl.hurried, skip: fl.skip, lead: fl.leadMode, s: fl.pace.s, ok: fl.pace.ok,
    h: fl.pose ? fl.pose.h : null, pitch: fl.pose ? fl.pose.pitch : null, F: fl.F,
    queued: fl.queued || null, res: fl.res ?? 1, hop: !!fl.hop,
    settle: fl.settle ?? null, slog: fl.slog ?? null,
    t: { placed: rel(fl.t.placed), hold: rel(fl.t.hold), go: rel(fl.t.go), fade: rel(fl.t.fade), lead: rel(fl.t.lead), descend: rel(fl.t.descend), rel: rel(fl.t.rel), hurry: rel(fl.t.hurry),
      skip: rel(fl.t.skip), land: rel(fl.t.land), stand: rel(fl.t.stand), end: rel(fl.t.end) },
    test: fl.test, log: fl.log, frames: fl.frames };
}

// ⤳ Beam before entry (inspect ctx.enterAt, "📍 Beam to my position").
// R7: everything gesture-bound runs synchronously in the click, before any
// await — desktop pointer lock; the phone's fullscreen retry + screen wake
// lock; VR's session request FIRST, in parallel with the
// place. VR: the session goes on AT ITS GRANT (its `end` listener, then
// setSession), so its first frame already draws the loading view (xrload.js;
// the place reply may come later). Then place → startWorld behind the beam-in
// overlay; the bulk class pauses until the latch. Resolves null when entered,
// else a status line (place refused, XR denied, the session ended while the
// place was pending: still on the Map).
function enterAt(x, z, o = {}) {
  if (mode) return Promise.resolve(t('main.beam.inWorld'));
  if (!entryMode) return Promise.resolve(t('main.beam.noMode'));
  if (_entering) return Promise.resolve(t('main.beam.busy'));
  const m = entryMode;
  let xrP = null;
  if (m === 'vr') {
    xrP = (navigator.xr
      ? navigator.xr.requestSession('immersive-vr', { optionalFeatures: ['local-floor', 'bounded-floor', ...swFeatures(classOn('xr_multiview'))] })
      : Promise.reject(new Error('WebXR unavailable'))).then((sess) => {
      const xr = { sess, ended: false };
      sess.addEventListener('end', () => { xr.ended = true; });
      // the end listener BEFORE setSession (onVrEnd ignores it until a mode runs)
      sess.addEventListener('end', onVrEnd);
      return renderer.xr.setSession(sess).then(() => xr, (err) => ({ err, sess }));
    }, (err) => ({ err }));
    // the loading view's panel: the Beam's name, the world's, "beaming…"
    if (xrLoad) xrLoad.show(o.label || '', _namedMeta ? (_namedMeta.region_label || _namedMeta.region || '') : '', t('main.html.beaming').replace(/^[^\p{L}]+/u, ''));
    _xrl.p = 0; _xrl.at = -Infinity; _xrl.miss0 = 0; _xrl.d0 = undefined; _xrl.place = false;
  } else if (m === 'flat') {
    _relock();
  } else {
    enterFullscreen();
    mobile.wakeAcquire();
  }
  Sfx.unlock();
  _entering = true;
  const tBeam = performance.now();
  _beamAt = tBeam;
  if (loadT.t_beam === null) {
    loadT.t_beam = tBeam;
    // the near ground there was already resident: the card's prefetch paid off
    _prefetchHit = _hasTarget && Math.hypot(_loadAt.x - x, _loadAt.z - z) < 64
      && nearOk({ x, z }) && buildings.nearColReady && vegSettled({ x, z });
  }
  setPhase('beam');             // the slicer's beam budget; the long-task split
  openHeavy();                  // quiet start ends at the latest here (behind the beam-in overlay)
  resumeBulk('save-data');      // an entry with no card first (📍) ends the Save-Data hold too
  pauseBulk('beam');
  _needBeam = { x, z };         // the pre-entry ticks keep scoring here until the place reply
  needTick(true, { x, z });     // the Beam is a target change: what follows the latch is ranked there
  warmTarget(x, z);             // and the first view to compile
  // VR: the player's comfort-menu pick (vrui.beamPick) over the world's flight.vr
  const fm = flightMode(m, { vr: vrBeam(vrui.beamPick, tunables && tunables.flight) });
  if (fm === 'flight') beginFlight(x, z, o.label || ''); else { showBeamIn(o.label || ''); if (fm === 'vrflight') beginVrFlight(); }
  // `key` an i18n key (main.beam.*), `vars` its placeholders; resolves the text
  const fail = (key, sess, vars) => {
    _entering = false;
    _needBeam = null;
    if (sess) sess.end().catch(() => {});
    endFlight(false);
    hideBeamIn();
    resumeBulk('beam');
    setPhase('map');
    if (m === 'flat' && document.pointerLockElement) document.exitPointerLock();
    Sfx.uiDeny();
    return t(key, vars || { s: PLACE_TIMEOUT_MS / 1000 });
  };
  // the Map was left while the Beam was pending (× → the landing page):
  // nobody is entering any more
  const gone = () => entryMode !== m || !playerMap.preEntry;
  // ONE automatic retry (the 3D→2D bounce): a refusal can be the server's
  // 2 s seat cooldown (a GPS seat or a Beam just before), a timeout a busy
  // server. A leave first — a late 'placed' of the first try would seat us
  // server-side (onPlaced ignores it locally; sim.leave is a no-op for an
  // unplaced player) — then BEAM_RETRY_MS, past that seat's cooldown.
  const place = () => placeAt(x, z, true).catch(() => ({ pos: null, why: 'offline' }));
  const placeTwice = () => place().then((r) => {
    if (r.pos || r.why === 'offline' || gone()) return r;
    net.leave();
    beamInSub('main.beam.again');
    return new Promise((t) => setTimeout(t, BEAM_RETRY_MS)).then(() => (gone() ? r : place()));
  });
  return Promise.all([placeTwice(), xrP]).then(([placed, xr]) => {
    const pos = placed.pos;
    if (xr && xr.err) {
      net.leave();                              // seated for nothing: un-place
      return fail('main.beam.vrFail', xr.sess || null, { err: xr.err.message || xr.err });
    }
    if (!pos) {
      // still no seat after the retry: the same leave, the reason on the card
      net.leave();
      return fail(BEAM_FAIL[placed.why] || BEAM_FAIL.refused, xr && xr.sess);
    }
    if (gone()) {
      net.leave();
      return fail('main.beam.abandoned', xr && xr.sess);
    }
    if (xr && xr.ended) {
      // the session ended while the place was pending (the system menu, the headset off)
      net.leave();
      return fail('main.beam.abandoned', null);
    }
    _entering = false;
    _needBeam = null;                           // entered: the player is the eye now
    gps.lastPlaceAt = Date.now();               // shares the GPS follow cooldown
    briefing.close();
    if (inspect && inspect.isOpen) inspect.close('beamed');
    playerMap.endPreEntry();
    _beamIn = { at: 0, smooth: 0 };
    firstActs.arm();
    _fv = m === 'vr' ? null : { busy: false, clean: false, at: -Infinity, water: null };
    _firstDraw = true;
    startWorld(m);
    if (_flight) flightPlaced();
    return null;
  });
}

// ☰ → Recenter: forward becomes where you look and the body stands under
// the head — the tracking origin moves to the head's floor point + yaw (an
// offset reference space; controllers follow) and the rig turns by that yaw,
// so the world in view does not turn. Three drops the custom space at the
// session's end.
const _rcE = new THREE.Euler();
function vrRecenter() {
  const ref = renderer.xr.getReferenceSpace();
  if (!ref || typeof XRRigidTransform === 'undefined' || !ref.getOffsetReferenceSpace) return false;
  _rcE.setFromQuaternion(camera.quaternion, 'YXZ');
  const h = _rcE.y;
  const rc = ref.getOffsetReferenceSpace(new XRRigidTransform(
    { x: camera.position.x, y: 0, z: camera.position.z }, { x: 0, y: Math.sin(h / 2), z: 0, w: Math.cos(h / 2) }));
  seatOnReset(rc);
  renderer.xr.setReferenceSpace(rc);
  rig.rotateY(h);
  _seat.zero();                 // the new origin is the head's floor point: the body is under it already
  if (player) player.yaw = rig.rotation.y;
  return true;
}

// VR: a trigger pulled with the laser off every panel = "the data at the
// point" (owner 2026-10-20), either hand. A station sign row boards first
// (zones.shootAt, as tryFire); else the inspect card of what the ray meets —
// through the head zoom window's reticle while it is up (the optic path),
// else along this controller's own ray (_vrAimCam, inspect's view()). Combat
// on: the sign only (the card is combat-off).
const _vrAimCam = new THREE.PerspectiveCamera();
let _vrAimOn = false;
function vrPoint(hand) {
  const ctrl = xrCtrl(hand);
  if (!ctrl || !player || player.dizzyActive) return;
  let ui = false;
  if (zones) {
    try { ui = zones.shootAt(ctrl); } catch (e) { console.error('[zones]', e); }
  }
  if (ui) { if (ui === 'ok') Sfx.hitConfirm(); else Sfx.uiDeny(); return; }
  if (_combatOn() || !inspect || !weapons.monoActive) { Sfx.uiDeny(); return; }
  if (inspect.isOpen) inspect.close('retarget');
  ctrl.updateWorldMatrix(true, false);
  ctrl.matrixWorld.decompose(_vrAimCam.position, _vrAimCam.quaternion, _vrAimCam.scale);
  _vrAimCam.updateMatrixWorld(true);
  _vrAimOn = !weapons.headOn;
  try { if (!inspect.open()) Sfx.uiDeny(); } catch (e) { console.error('[inspect]', e); }
  _vrAimOn = false;
}

// the world laser's length (vrui, ≤ 10 Hz per aiming hand): a building
// within BLD_MAX_M first, then the aim ray over what is resident (the block
// skip) — a pure read: no held tiles, no fetch, no dwell lead, so it never
// competes with the optic's aim lane; null = open sky / nothing there yet
function _vrWorldHit(ori, dir) {
  const b = _buildingStep(ori, dir);
  if (b) return b.dist;
  if (!_aimMarch) return null;
  const r = _aimMarch(ori, dir, _RANGE_MARCH);
  return r && r.dist !== null ? r.dist : null;
}

// the VR HUD's facts (vrui VrHud), at the desktop HUD's cadence: the same
// primary row and chips as updateHud, plain text, and this device's bindings
function updateVrHud() {
  if (mode !== 'vr' || !vrui || !vrui.hud.on || frameTimes.length % 12 !== 0) return;
  const hud = vrui.hud;
  hud.window = !!weapons.headOn;
  const sKey = _hudSurfKey();
  const primary = `▲ ${Math.round(player.pos.y)} m` + (_slopeDeg !== null ? `   ${slopeLabel(_slopeDeg)}` : '')
    + (sKey && !player.bike && !player.gliding ? `   ${sKey}` : '');
  const parts = _hudCond(sKey, false);
  if (player.dizzyFreeze > 0) parts.push(t('main.hud.down', { s: player.dizzyFreeze.toFixed(1) }));
  else if (player.dizzyT > 0) parts.push(t('main.hud.dizzy', { s: player.dizzyT.toFixed(1) }));
  if (zones && zones._rows) {
    const en = zones._rows.filter((r) => !r.dim && r.onShoot);
    // the 1st row is the right trigger's (pointing at the sign, vrPoint), the 2nd also B's (A = the menu)
    if (en.length) parts.push(`🚉 ${t('main.vr.zonePoint')} ${en[0].text}${en[1] ? ` · B ${en[1].text}` : ''}`);
  }
  // the key hints: pages of one line each, cycled on the HUD (keyhints.js)
  hud.setFacts(primary, parts.join(' · '), keyHintPages('vr', _keyHintState(), t));
  // the compass markers (the desktop's: waypoint, the map pin, the followed player)
  const m = hud.markers;
  m.length = 0;
  const bearingTo = (x, z) => {
    const b = Math.atan2(x - player.pos.x, -(z - player.pos.z)) * 180 / Math.PI;
    return b < 0 ? b + 360 : b;
  };
  if (beacons && beacons.waypoint && !_combatOn()) m.push({ brg: bearingTo(beacons.waypoint.x, beacons.waypoint.z), color: '#facc15' });
  if (playerMap && playerMap.mark) m.push({ brg: bearingTo(playerMap.mark.x, playerMap.mark.z), color: '#67e8f9' });
}

// VR session end → the Map before entry (R4): the player leaves the world
// (the server un-places it); the streaming target stays where it stood
function onVrEnd() {
  if (mode !== 'vr') return;
  vrui.closeAll();
  weapons.headOff();
  net.leave();
  if (inspect && inspect.isOpen) inspect.close('mode');
  resetRide();
  endFlight(false);
  mode = null;
  _beamIn = null;
  _fv = null;
  hideBeamIn();
  resumeBulk('beam');
  setPhase('map');
  _loadAt.copy(player.pos);
  _hasTarget = true;
  playerMap.openPreEntry('vr');
  startMapLoop();
}

function startWorld(m) {
  mode = m;
  // desktop + phone: ONE view (viewzoom.js) — the zoom narrows the main
  // camera, then renders through the optic (weapons.monoCam) in the band;
  // VR zooms in the head zoom window (weapons.js)
  playerMap.setMode(m);
  flat.active = m === 'flat';
  const oneView = m === 'flat' || m === 'mobile';
  $('view-hud').style.display = oneView ? 'block' : 'none';
  $('view-vignette').style.display = oneView ? 'block' : 'none';
  $('brief-help').hidden = !oneView;      // the 3D briefing's "?" (no DOM in the headset)
  if (scopeHud) {
    // the phone's HUD runs at half rate on a bolder face; the slider is its
    // MAG and the lock / button column / minimap are its insets
    scopeHud.every = m === 'mobile' ? 2 : 1;
    scopeHud.boost = m === 'mobile';
    scopeHud.mag = m !== 'mobile';
    scopeHud.setInsets(m === 'mobile' ? { left: 68, right: 136, below: 160 } : { left: 0, right: 174, below: 0 });
    if (!oneView) scopeHud.clear();
  }
  if (viewZoom) { viewZoom.reset(); viewZoom.snap(); }
  if (oneView) {
    hud.style.display = m === 'flat' ? 'block' : 'none';
    camera.position.set(0, player.eyeHeight, 0);
    camera.add(weapons.group);
    weapons.group.position.set(0.25, -0.22, -0.5);
    weapons.group.rotation.set(0, 0, 0);
    if (m === 'mobile') {
      // relative-heading fallback only: with a live compass updateCamera
      // ignores _yawOffset entirely (the phone owns the heading)
      mobile._yawOffset = player.yaw;
      mobile.show();
    }
  } else {
    hud.style.display = 'none';
    weapons.group.visible = true;      // the desktop / phone kit-hide rule is not VR's
    weapons.setBand(false);
    camera.fov = desktopVFov(camera.aspect);
    camera.updateProjectionMatrix();
    const c0 = renderer.xr.getController(0);
    const c1 = renderer.xr.getController(1);
    rig.add(c0, c1);
    // gun rides the right hand (handedness-resolved; the 'connected' handler
    // re-attaches if the map was not filled yet at entry)
    xrCtrl('right').add(weapons.group);
    weapons.group.position.set(0, -0.02, -0.06);
    // simple hand markers
    for (const c of [c0, c1]) {
      if (!c.userData.marker) {
        const marker = new THREE.Mesh(new THREE.SphereGeometry(0.035, 8, 6),
          new THREE.MeshLambertMaterial({ color: 0xcfd6e0 }));
        c.add(marker);
        c.userData.marker = marker;
      }
    }
  }
}

// ---------------------------------------------------------------- events → FX
// The server pushes a bullet's hit/impact BEFORE its shot event, so when the
// shot arrives we already know where that round stopped — that's what lets
// the near-miss whiz stay silent when the impact was close by (its own sound
// covers it) or when the round was stopped before it ever reached us.
const lastImpact = { t: -1e9, pos: new THREE.Vector3() };
function onGameEvent(ev) {
  switch (ev.e) {
    case 'shot': {
      if (ev.id === net.id) return;
      const o = new THREE.Vector3(...ev.o);
      const d = new THREE.Vector3(...ev.d).normalize();
      tracer(o, o.clone().addScaledVector(d, 400));
      // positional: HRTF pans it to the shooter, delayed by speed of sound.
      // Bots fire lasers, humans fire the real gunshot recordings.
      const dist = o.distanceTo(player.pos);
      const r = net.remotes.get(ev.id);
      const isBot = r ? r.bot : ev.id >= 1000;   // bot ids start at 1000 (sim)
      if (!isBot) {
        // near-miss whiz: the ray passes close to the head, from far enough
        // away that the muzzle blast isn't already on top of us
        const head = player.pos.clone();
        head.y += 1.5;
        const tStar = head.clone().sub(o).dot(d);            // closest approach
        const pass = o.clone().addScaledVector(d, tStar);
        const dperp = tStar > 0 ? head.distanceTo(pass) : 1e9;
        const imp = performance.now() - lastImpact.t < 150 ? lastImpact.pos : null;
        const impNear = imp && imp.distanceTo(head) < 12;    // impact sound covers it
        const stopped = imp && o.distanceTo(imp) < tStar - 1; // never reached us
        if (tStar > 8 && dperp > 0.6 && dperp < 5 && dist > 10 && !impNear && !stopped) {
          Sfx.whiz(pass);
        }
      }
      if (dist < 1200) {
        if (isBot) Sfx.laserAt(o, dist);
        else Sfx.shotAt(ev.w, o, dist);
      }
      break;
    }
    case 'impact': {
      const at = new THREE.Vector3(...ev.at);
      lastImpact.t = performance.now();
      lastImpact.pos.copy(at);
      puff(at, 0xcfc8ba);
      if (!mode) break;                       // no ears before entry
      const dist = at.distanceTo(player.pos);
      // the sniper slam carries much further than the rifle puff
      if (dist < (ev.w === 'sniper' ? 500 : 150)) Sfx.impact(at, ev.w, dist);
      break;
    }
    case 'hit': {
      const at = new THREE.Vector3(...ev.at);
      puff(at, 0xd83a2e);
      if (ev.by === net.id) { flashHit(); Sfx.hitConfirm(); }
      if (ev.target === net.id) { Sfx.damage(); Sfx.hitBody(); }
      else if (mode) {
        const d = at.distanceTo(player.pos);
        if (d < 400) Sfx.hitBody(at, d);
      }
      break;
    }
    case 'kill':
      if (ev.by === net.id) { flashHit(true); Sfx.kill(); }
      break;
    case 'dizzy':
      // dizzy death mode: no respawn — freeze in place, then recover slowly
      // (red overlay + slowed walk; step()/tryFire enforce the rest)
      if (ev.id === net.id) {
        player.setDizzy(ev.freeze_s, ev.decay_s);
        Sfx.damage();
      }
      break;
    case 'respawn':
      // movement is client-simulated, so the client must teleport ITSELF to
      // the server's respawn point: on the ground at the player's last
      // placed point (Sim respawnPoint; combat only)
      if (ev.id === net.id) {
        player.cancelGlide();
        player.pos.set(ev.at[0], ev.at[1] + 0.1, ev.at[2]);
        player.vel.set(0, 0, 0);
        player.state = 'ground';
        player.grounded = true;
        focus.reset();
        hf.ensureAround(player.pos.x, player.pos.z);
      }
      break;
    default: break;
  }
}

function tracer(from, to) {
  const geo = new THREE.BufferGeometry().setFromPoints([from, to]);
  const line = new THREE.Line(geo, new THREE.LineBasicMaterial({
    color: 0xffe9a8, transparent: true, opacity: 0.9,
  }));
  scene.add(line);
  fx.push({ obj: line, until: performance.now() + 90 });
}

function puff(at, color) {
  const s = new THREE.Mesh(new THREE.SphereGeometry(0.12, 6, 5),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85 }));
  s.position.copy(at);
  scene.add(s);
  fx.push({ obj: s, until: performance.now() + 260, grow: 3.5 });
}

// hit / kill confirm: the view HUD's centre dot flashes (red / gold)
function flashHit(big) {
  _hitUntil = performance.now() + HIT_FLASH_MS;
  _hitBig = !!big;
}

function stepFx(now) {
  for (let i = fx.length - 1; i >= 0; i--) {
    const f = fx[i];
    if (now > f.until) {
      scene.remove(f.obj);
      f.obj.geometry.dispose();
      f.obj.material.dispose();
      fx.splice(i, 1);
    } else if (f.grow) {
      f.obj.scale.multiplyScalar(1.15);
      f.obj.material.opacity *= 0.88;
    }
  }
}

// ---------------------------------------------------------------- firing
let _inspProbeAt = 0;
const INSP_PROBE_MS = 140;          // the phone's ◎ hint (Inspect.probe) runs at most this often
function tryFire(aimObj, now) {
  if (player.dizzyActive) return;   // dizzy: walking only (server rejects too)
  // station boarding sign: aim at a row and shoot to select — the shot is
  // consumed by the UI (no bullet), identical in flat and VR. Defensive
  // try/catch: a UI bug must never kill the frame loop (froze the game once).
  // This path stays active even with combat off — boarding is not a weapon.
  let uiHit = false;
  if (zones) {
    try { uiHit = zones.shootAt(aimObj); } catch (e) { console.error('[zones]', e); }
  }
  if (uiHit) {
    // distinct feedback: chirp = action fired (boarded/exited), double-beep
    // = shot absorbed by the panel but nothing happened (row dim / race)
    if (uiHit === 'ok') Sfx.hitConfirm();
    else Sfx.uiDeny();
    return;
  }
  // combat off + the monocular in hand: the trigger inspects at any zoom
  // (the card's own edge latch — this runs every frame the trigger is held)
  if (!_combatOn() && weapons.monoActive && inspect) {
    try { inspect.trigger(now); } catch (e) { console.error('[inspect]', e); }
    return;
  }
  if (!_combatOn()) return;         // combat off: no bullets
  if (!weapons.canFire(tunables, now)) return;
  const dir = weapons.fireDir(tunables, now, aimObj);
  const o = weapons.muzzle.getWorldPosition(new THREE.Vector3());
  net.fire(weapons.current, o, dir);
  tracer(o, o.clone().addScaledVector(dir, 300));
  Sfx.shot(weapons.current, o);   // positional at the muzzle
}

// ---------------------------------------------------------------- main loop
const _move = new THREE.Vector3();
const _steer = new THREE.Vector3();
// THE BODY STANDS UNDER THE HEAD (vr-sink, 2026-11-01; player.js _headMove):
// _hdRef = the head's floor point in rig (reference) space the rig placement
// compensates (rig = body − R(yaw)·_hdRef, so the eye stands over the body),
// _hdMove = this tick's head displacement in world m, the body's move
// (player.step extras.headMove). Zero while a VR flight owns the rig (no body
// control) and at ⟲ Recenter (the origin moves to the head: nothing to fold);
// the first play tick after a flight folds the whole offset into the body.
// ACROSS A TRACKING GAP (vr-redon, 2026-11-02; headseat.js): the reference
// space's 'reset', the session visible again, a one-frame head jump over
// SEAT_JUMP_M → a RE-SEAT (the baseline re-based on the head, no move, the
// eye height kept by _seat.lift, the body stood on the ground under the head:
// seatBody); an emulated viewer pose folds nothing (orientation only).
const _seat = new HeadSeat();
const _hdRef = _seat.ref;
const _hdMove = _seat.move;
let _xrVis = 'visible';        // the session's last visibilityState (a return to 'visible' re-seats)
function headFold(xrCam, emulated, dt) {
  const cams = xrCam.cameras, e0 = xrCam.matrix.elements;
  let hx = e0[12], hy = e0[13], hz = e0[14];
  if (cams && cams.length === 2) {       // the eyes' midpoint (cameraXR.matrix is the left eye's)
    const a = cams[0].matrix.elements, b = cams[1].matrix.elements;
    hx = (a[12] + b[12]) / 2; hy = (a[13] + b[13]) / 2; hz = (a[14] + b[14]) / 2;
  }
  if (_seat.fold(hx, hy, hz, rig.rotation.y, emulated, !!_flight, dt) === 'reseat'
    && player && !(zones && zones.riding)) seatBody(player);
}
// the viewer pose's emulatedPosition this XR frame (no pose = no position either)
function xrEmulated() {
  const fr = renderer.xr.getFrame(), ref = renderer.xr.getReferenceSpace();
  const vp = fr && ref ? fr.getViewerPose(ref) : null;
  return !vp || vp.emulatedPosition === true;
}
// a reference space's discontinuity (Quest: the don after a sleep, a system
// recenter) re-seats; every space the rig stands in (the session's, ⟲ Recenter's)
function seatOnReset(ref) {
  if (ref && ref.addEventListener) ref.addEventListener('reset', () => _seat.invalidate('reset'));
}
const _handPos = new THREE.Vector3();
const _eye = new THREE.Vector3();      // flat-mode eye anchor for the station sign
const _foreGrip = new THREE.Vector3();  // VR two-hand test, per frame
// pose report scratch: read into fresh arrays at once, never sent themselves
const _poseP = new THREE.Vector3();
const _poseQ = new THREE.Quaternion();
let prevT = performance.now();
let vrIndArmed = false;       // stick-flick indicator toggle latch (follow mode)

// mic mute toggle, audible either way (VR has no HUD to show mic state)
function toggleVoiceMute() {
  voice.setMuted(!voice.muted);
  if (voice.muted) Sfx.uiDeny(); else Sfx.hitConfirm();
}
// the view's zoom (desktop): one wheel notch = tunables.view.wheel_step,
// any weapon, any zoom — past the band it is the optic's own zoom
document.addEventListener('wheel', (e) => {
  if (mode !== 'flat' || !viewZoom) return;
  if (inspect && inspect.isOpen) return;      // the wheel scrolls the card
  if (playerMap && playerMap.fullOpen) return; // ... and zooms the Map
  e.preventDefault();
  if (!player || !player.dizzyActive) viewZoom.wheel(e.deltaY);
}, { passive: false });
// middle button (desktop, combat): next weapon — no keyboard needed
document.addEventListener('mousedown', (e) => {
  if (e.button !== 1 || mode !== 'flat' || !flat || !flat.locked) return;
  e.preventDefault();
  if (_combatOn() && weapons && !(player && player.bike)) weapons.toggle();
});

// A pending Beam (enterAt between the click and its outcome) owns the
// input: #beam-in sits over the Map and the card (index.html z-index 11),
// and the capture phase on window stops every press / key before the Map,
// the card, the × and the global shortcuts see it — nothing can leave the
// Map while the entry is in flight. Touch / wheel stay passive (a
// non-passive window touchstart would stall every pan of the Map): the
// overlay's touch-action: none covers their default.
for (const t of ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click', 'dblclick', 'auxclick',
  'contextmenu', 'wheel', 'touchstart', 'touchend', 'keydown', 'keyup']) {
  const passive = t === 'wheel' || t.startsWith('touch');
  window.addEventListener(t, (e) => {
    // the charge and the reply gap (PC): keys do nothing but Esc (inspect cancels the charge), nor
    // the right button (the Map); the card's left presses pass
    if ((_charge || _placing) && !_entering && !_flight) {
      if ((t.startsWith('key') && e.code !== 'Escape') || ((t.startsWith('mouse') || t.startsWith('pointer') || t === 'auxclick' || t === 'contextmenu') && e.button === 2)) {
        e.stopImmediatePropagation();
        if (e.cancelable && !t.startsWith('key')) e.preventDefault();
      }
      return;
    }
    if (!_entering && !_flight) return;       // a pending Beam, the beam flight
    e.stopImmediatePropagation();
    // VR: any key or click lands at once; flat: held / Space held (flightInput)
    if (_flight && _flight.kind === 'vr') { if (t === 'keydown' || t === 'pointerdown') flightSkip(); }
    else if (_flight) flightInput(t, e);
    if (!passive && e.cancelable && !t.startsWith('key')) e.preventDefault();
  }, { capture: true, passive });
}

// the skip hold needs the key held NOW: a blur / a hidden tab loses its keyup
for (const [tg, ev] of [[window, 'blur'], [document, 'visibilitychange']]) tg.addEventListener(ev, () => { if (_flight) _flight.spaceAt = null; });

document.addEventListener('keydown', (e) => {
  if (e.repeat) return;
  if (e.code === 'KeyM' && voice.ready) toggleVoiceMute();
  // ? (desktop): the briefing of the view in front — the Map's or the world's
  if (e.key === '?' && mode === 'flat') briefing.open(playerMap && playerMap.fullOpen ? '2d' : '3d', 'flat');
  // player map (flat): N toggles the full map (as RMB does), Esc closes it
  if (playerMap && mode === 'flat') {
    if (e.code === 'KeyN') playerMap.toggleFull();
    // Esc with a Map card up closes the card only (inspect's own Esc)
    else if (e.code === 'Escape' && !(inspect && inspect.isOpen)) playerMap.closeFull();
  }
  // bike turn indicators (edge-triggered): ←/→ always; A/D too in follow
  // mode, where they no longer steer (the route does)
  if (player && player.bike) {
    if (e.code === 'ArrowLeft' || (player.bikeFollow && e.code === 'KeyA')) {
      player.toggleIndicator(-1);
      Sfx.click && Sfx.click();
    }
    if (e.code === 'ArrowRight' || (player.bikeFollow && e.code === 'KeyD')) {
      player.toggleIndicator(1);
      Sfx.click && Sfx.click();
    }
  }
  // station boarding: F = first enabled action, G = second (flat mode)
  if (zones && zones._rows && mode === 'flat') {
    if (e.code === 'KeyF' || e.code === 'KeyG') {
      const enabled = zones._rows.filter((r) => !r.dim && r.onShoot);
      const target = enabled[e.code === 'KeyF' ? 0 : 1];
      if (target) {
        target.onShoot();
        Sfx.hitConfirm();
        e.preventDefault();
        return;
      }
    }
  }
});
let prevGliding = false;      // jet-suit ignition sfx edge detector
let prevBikeYaw = 0;          // bike steering turns the rider's view with it

// The XR camera's WORLD pose, once a VR tick before anything reads it: the
// rig's world matrix × this frame's viewer pose (reference space). three's
// ArrayCamera has no parent: a getWorldDirection / getWorldPosition on it
// re-composed matrixWorld from the reference-space pose alone and dropped the
// rig — the left-stick walk ran off by the rig's yaw (the landing yaw, every
// comfort turn, ⟲ Recenter: "the left joystick seems to go randomly", owner
// 2026-11-01), and every later reader in that tick (the wrist map, the zones,
// the listener, the painter's view, the pose sent to the server) got the same
// rig-less pose. Its matrix(World)AutoUpdate are off (boot), so a read never
// re-composes it; three's render writes the eye union's pose as before.
function xrEyeWorld(xc) {
  rig.updateWorldMatrix(true, false);
  xc.matrixWorld.multiplyMatrices(rig.matrixWorld, xc.matrix);
  xc.matrixWorldInverse.copy(xc.matrixWorld).invert();
}

// ------------------------------------------------------ landing-page idle
// Behind the opaque landing #overlay or the Map before entry (first load,
// VR session end, connection lost; never while a headset needs frames) a
// draw shows nobody anything. Its
// one use is WARM-UP: the first draw of newly resident content compiles its
// shaders and uploads its buffers/textures, and that must still happen here,
// not in the first frames after entry. So the landing draws only when a draw
// would do something new:
//  - the scene graph changed: an object added/removed/shown, a geometry or
//    material swapped, a material recompile. Terrain tiles, buildings,
//    vegetation, water, lifts, sky/horizon and avatars all arrive this way,
//    so a scan (not a list of subsystems) cannot forget a new one;
//  - the renderer freed a geometry or texture (the other half of a swap);
//  - the canvas was resized (another frustum culls another set);
//  - a 1 s heartbeat: a texture whose image lands in an already-resident
//    material (TextureLoader, terrain's shared uniforms) bumps only its own
//    version, which no scan of the scene graph can see.
// A landing draw counts once the renderer really drew (info.render.frame).
// Before entry a draw never COMPILES: while the scan sees a material with no
// program (`_landNew`) the draw waits for the queue's pre-entry walk (landingHold).
const LANDING_BEAT_MS = 1000;
// the scan walks the whole scene graph: at most this often (the heartbeat
// draw scans when it is due), never every frame
const LANDING_SCAN_MS = 250;
let _landScanAt = -Infinity;
const _landSig = new Float64Array(8);   // state at the last landing draw
const _landCur = new Float64Array(8);   // this frame's
let _landAt = -Infinity;                // when the landing last drew
let _landNew = 0;                       // objects of the last scan with an untried uncompiled material
function _landVisit(o) {
  _landCur[0]++;
  _landCur[1] += o.visible ? o.id : -o.id;
  if (o.geometry) _landCur[2] += o.geometry.id;
  if (o.isInstancedMesh) _landCur[3] += o.count + o.instanceMatrix.version;
  const m = o.material;
  if (Array.isArray(m)) for (let i = 0; i < m.length; i++) _landCur[4] += m[i].id * 4096 + m[i].version;
  else if (m) _landCur[4] += m.id * 4096 + m.version;
  if (cq.uncompiled(o, true)) _landNew++;
}
function _landScan() {
  _landScanAt = performance.now();
  _landCur.fill(0);
  _landNew = 0;
  scene.traverse(_landVisit);
  const mem = renderer.info.memory, cv = renderer.domElement;
  _landCur[5] = mem.geometries;
  _landCur[6] = mem.textures;
  _landCur[7] = cv.width * 65536 + cv.height;
}
function landingDue(now) {
  if (now - _landAt >= LANDING_BEAT_MS) { _landScan(); return true; }
  if (now - _landScanAt < LANDING_SCAN_MS) return false;
  _landScan();
  for (let i = 0; i < 8; i++) if (_landCur[i] !== _landSig[i]) return true;
  return false;
}
// a due landing draw before entry waits while new programs are being built
// off the frame (the queue's `pre` class: preWarm's walk, its textures) — else
// three compiles them all inside the draw (one 9.3–9.7 s frame, load reports
// 29712 / 29742). The scan seeing one asks for the walk. A material a job
// already handed to compile() (cq.uncompiled untried) never holds it: the
// draw is the fallback.
function landingHold() {
  if (mode) return false;
  if (_landNew && !cq.busy('pre')) preWarm();
  return cq.busy('pre');
}
// re-scanned AFTER the draw: its own uploads are not a change, and neither
// are the two material.version bumps three makes per double-sided
// transparent draw (clouds, lakes)
function landingDrew(now) {
  _landScan();
  _landSig.set(_landCur);
  _landAt = now;
}

// ------------------------------------------------------ shader warm-up
// THE SHADER WARM-UP: the policies — what is asked of the compile queue
// (compileq.js: the slicing, the driver's room, the first-use lane, the
// uploads, the in-frame counter), when, under which act labels and class
// (compileq PRIO, highest first: first > water > prepare > pre > behind >
// late). Nothing here compiles, links or first-uses itself.
// BEFORE ENTRY (preWarm, class `pre`): each phase-1 piece is compiled as it
// joins the scene while the Map is up; a request while the walk runs is one
// re-scan after it (the queue coalesces). A card or the Beam is its TARGET
// (_warmTgt): the programs the first view there draws go first (view:
// visible, within camera.far, nearest first), the rest after; a newer target
// stops the walk and the re-scan runs in the new order. Then the big
// textures (the queue's uploads, ≥ PRETEX_MIN_PX, visible objects, one per
// task; the Beam ends them): the same uploads the first frame after ⤳ Beam
// would otherwise do in one go (a 2507 ms 'frame' task 563 ms after the
// Beam, load 30297). The landing draw holds while the class has work.
// FROM THE PLACE REPLY TO THE OVERLAY (flat/mobile; fvTick, class `first`):
// the FIRST VIEW at the player — only its programs — compiled, linked and
// first-used, a pass every LANDING_SCAN_MS while new content joins; the
// overlay waits for a clean scan after the latch (fvReady), the water
// variants when a lake is near (WATER_NEAR_M, class `water`), the smooth
// frames.
// BEHIND THE OVERLAY, AFTER THE FIRST VIEW (flat/mobile, warmEarly +
// warmShaders, class `behind`: started by the first clean fvTick scan, before
// the latch, and once more by the first clean scan after it; the
// overlay waits for it — capped by ovl_max_ms; was after the overlay: 58
// first uses of 200-280 ms in play on an iGPU, 14-17 s, load reports
// 2026-10-24): what play would otherwise compile mid-game, every job SLICED:
//  - the canvas job: every resident material (the whole graph, visible or
//    not, like three's compile(): hidden kit, culled or off-screen content
//    and the focus layer are all covered);
//  - terrain's variant keepers (terrain.variantKeepers): every mask this
//    world can stream in later, pinned in the queue so an evicted variant is
//    never rebuilt;
//  - the optic passes (weapons.opticPasses: monoRt, the depth pack, the
//    sharpen quad), keepers included, each with its target bound: desktop
//    and phone (every zoom past the band renders through the optic), or VR
//    with combat on.
// Each new program's FIRST USE (the queue's lane): three's link diagnostics
// and uniform/attribute location fetch are synchronous GL queries that
// otherwise ran inside the first frame drawing it — for the optic set, all
// in the first band frame. Nothing drawn changes, nothing joins the scene.
// VR: once, at the latch, behind its landing's black (vrFlightStage; the
// fade waits for it); not in the instant wait. window.__vr.warm: idle → running → done.
// LATE PASS (warmLate, class `late`): phase 1 (ways, vegetation, water,
// lifts, the far ring) may still be landing. When all of it has settled, the
// same jobs run once more (a job still running: one re-scan after it): only
// what is new is built and first-used — the optic copies of the late
// materials, culled or off-screen content and terrain variants a late carve
// (the ways' bores) made reachable.
let _warm = 'idle';
let _warmSets = 0;                      // warm-up sets (warmEarly / warmShaders / warmLate) not yet through
let _warmPost = false;                  // warmShaders ran (the set after the latch)
let _preWarmOwed = false;
let _warmTgt = null;                    // {x, z} of the latest card / Beam: preWarm's first view

function preWarm() {
  if (mode || !renderer) return;
  if (!heavyOk()) { _preWarmOwed = true; return; }   // quiet start: at the first card / the Beam
  // the first view at the target first; a newer target stops the walk (the
  // re-scan takes its order); the place reply ends it (fvTick takes the
  // first view over, warmShaders the rest behind the overlay)
  const tg = _warmTgt;
  cq.want(scene, { label: 'prewarm.compile', use: 'prewarm.firstUse', prio: 'pre', view: tg, stop: () => !!mode || tg !== _warmTgt })
    .then(() => cq.upload(scene, { label: 'prewarm.tex', prio: 'pre', stop: () => !!mode }))
    .catch((e) => console.warn('[warm] preWarm:', e));
}
// a card or the Beam at (x, z): preWarm's first view moves there
function warmTarget(x, z) {
  _warmTgt = { x, z };
  preWarm();
}
// the optic passes of `root` (weapons.opticPasses; `only`: just the pass over
// root itself — the keepers, the optic ring), one job each, its target bound
function opticWants(root, label, use, prio, only = false) {
  if (!weapons) return [];
  return weapons.opticPasses(root).filter((ps) => !only || ps.root === root)
    .map((ps) => cq.want(ps.root, { label, use, prio, target: ps.lights, pass: ps }));
}
function warmLate() {
  if (!_warmPost) return;                 // the pass after the latch is still to come (or VR's instant wait)
  if (_flight) { _flight.late = true; return; }   // never inside the flight: at control (endFlight)
  warmSet('late');
}
// WATER-G: the class grid decides which terrain tiles meet water (terrain
// V_WATER, groundmat waterIn). Those variants are built here — canvas and
// optic, linked and first-used off the frame like warmShaders (the queue's
// `water` class) — BEFORE any resident tile swaps to them
// (terrain.applyWater): the grid landing never builds a program inside a
// frame. Every mode (VR too: a swap, not a load). The canvas set is sliced
// (it was ONE 11.6–17.8 s task before entry); the optic copies (a mode only)
// one job.
// While a beam-in overlay is up (_beamIn) the grid landing only marks it due
// (_waterDue): the tick runs it after the latch when water is within
// WATER_NEAR_M of the player — the first view shows it, the overlay waits
// (fvReady) — else once the overlay is gone. Before entry (the Map) and in
// play it runs at once.
const WATER_NEAR_M = 1500;
let _waterDue = false, _waterOwed = false;
// JOIN GATING (R8d): the water meshes (sky.js buildSheets / buildRivers) are
// built into an off-scene stage; after the quiet start the compile queue makes
// their programs (lit by the scene) and uploads their textures, and only then
// do they join the drawn scene. WHEN they appear moves, never how: the same
// meshes and materials (the stage is an identity group, famScene tags them
// 'water' as before). GLB / ways tiles join the same way (their prepare).
function waterStage() { return new THREE.Group(); }
async function joinWater(meshes, label) {
  if (!meshes || !meshes.length) return meshes || [];
  const stage = meshes[0].parent;
  await heavyReady();
  await cq.want(stage, { label: `${label}.compile`, use: `${label}.firstUse`, target: scene, prio: 'water' });
  await cq.upload(stage, { label: `${label}.tex`, prio: 'water', all: true });
  famScene(scene, 'water').add(...stage.children);
  return meshes;
}
function warmWaterSoon() {
  if (!mode && !heavyOk()) _waterOwed = true;          // quiet start: at the first card / the Beam
  else if (_beamIn) _waterDue = true;
  else warmWater();
}
// R8d3: while the class grid's water marks build (groundmat waterBoxesSteps)
// "near" is unknown: the overlay holds and the first-view warm-up waits for them
function waterNear(p) { return waterIn(p.x - WATER_NEAR_M, p.z - WATER_NEAR_M, p.x + WATER_NEAR_M, p.z + WATER_NEAR_M); }
// QUIET START ends (owner ruling 2026-10-17, smooth Map): the first card
// (prefetchAt) or the ⤳ Beam (enterAt). Until then, while the landing page
// or the Map before entry is up, the 3D side does nothing heavy — no
// landing draw (and no _landScan), no shader compile / warm-up (preWarm,
// warmWater), no structure draw or TLM object mesh build (heavyReady
// below), no ground texture pool allocation (gmtex.js heavyOk). Downloads go on
// (phase 1 unchanged); what was asked meanwhile runs now. One amendment
// (ruling 2026-10-24): the ground painter's set-up from the first Map touch
// (THE MAP SET-UP, armMapSetup).
function openHeavy() {
  if (heavyOk()) return;
  allowHeavy();
  if (_preWarmOwed) { _preWarmOwed = false; preWarm(); }
  if (_waterOwed) { _waterOwed = false; warmWaterSoon(); }
}
function warmWater() { return waterBoxesIdle().then(warmWaterNow); }   // the keepers read the marks
function warmWaterNow() {
  const keepers = cq.pin(terrain.variantKeepers());
  if (!keepers) { terrain.applyWater(); return Promise.resolve(); }
  const all = [cq.want(keepers, { label: 'warm.water', use: 'warm.water.firstUse', target: scene, prio: 'water' })];
  all.push(...opticWants(keepers, 'warm.water.optic', 'warm.water.firstUse', 'water', true));
  return Promise.all(all)
    .catch((err) => console.error(err))
    .then(() => terrain.applyWater());
}

// THE FIRST VIEW behind the beam-in overlay (flat / phone, every tick from
// the place reply until the overlay goes): at most every LANDING_SCAN_MS a
// scan of the first view at the player (cq.left); something uncompiled →
// one job over just that (view.only, label `warm.first`, class `first`: it
// overtakes any other at the queue's next task) and the frame does not draw
// meanwhile (`busy`: the draw would build those programs inside it, the
// first use a link wait); nothing → after the latch the first view is
// `clean`. A pass ends with the overlay.
function fvTick(now) {
  if (!_fv || _fv.busy || now - _fv.at < LANDING_SCAN_MS) return;
  _fv.at = now;
  const x = player.pos.x, z = player.pos.z;
  if (!cq.left(scene, x, z)) {
    _fv.clean = !!(_beamIn && _beamIn.at);
    // THE WARM-UP BEHIND THE OVERLAY: the first view warm — the rest now, so
    // play never first-uses a program: before the latch (warmEarly) and once
    // more at the first clean scan after it (warmShaders)
    if (mode === 'flat' || mode === 'mobile') { if (_fv.clean) warmShaders(); else if (_flight) warmEarly(); }
    return;
  }
  const fv = _fv;
  fv.clean = false;
  fv.busy = true;
  cq.want(scene, { label: 'warm.first', prio: 'first', view: { x, z, only: true }, stop: () => _fv !== fv })
    .catch((e) => console.warn('[warm] first view:', e))
    .then(() => { fv.busy = false; fv.at = performance.now(); });
}
// the overlay may go (flat / phone; with the smooth frames): latched, a clean
// first-view scan after it, the warm-up pass done (warmShaders, started by
// that scan), no near-water warm-up due or running, the vegetation of the
// draw range landed or OVL.veg after the latch
function fvReady(now) {
  return !!(_beamIn && _beamIn.at && _fv && _fv.clean && !_fv.busy && _warm === 'done' && !_fv.water
    && !(_waterDue && (waterBoxesBusy() || waterNear(player.pos))) && vegOk(now));
}
// no tree pops in under the player's eyes: the overlay holds for the draw
// range's tree / shrub tiles, at most OVL.veg after the latch (they are
// trunks to walk into, never a place to stand: the latch does not wait)
function vegOk(now) { return vegSettled(player.pos) || now - _beamIn.at >= OVL.veg; }
// THE TOP GATE (beam-order F1, every mode): the painter paints the latch's
// tiles before the top level (gmtex vtScore, THE LATCH ORDER), so at the
// latch a node outside them may have no texture yet — the overlay (VR: the
// black) holds until every top-level tile is resident (vt.topReady), at most
// load.dismiss_timeout_ms after the latch (a stuck painter)
function topOk(now) {
  const vt = terrain && terrain.gm.vt;
  return !vt || vt.topReady() || now - _beamIn.at >= ((RENDER_CFG.load && RENDER_CFG.load.dismiss_timeout_ms) ?? 30000);
}
// THE SPRITE GATE (veg-first, every mode): the owner's tree / bush sprites
// (veg/<sha16>.webp, a first family) restyle every near tree / bush in one
// frame when they land — the overlay holds until every material that asked
// for one wears it (vegOwn) or its fetch ended without one (a bake without
// the file keeps the painting; one naming none never waits), at most
// load.dismiss_timeout_ms after the latch
function spritesOk(now) {
  return spritesWorn() || now - _beamIn.at >= ((RENDER_CFG.load && RENDER_CFG.load.dismiss_timeout_ms) ?? 30000);
}
function spritesWorn() {
  const due = (m, c, url) => !!url && !m.userData.vegOwn.value.getComponent(c) && assetEnded(url) !== false;
  const sp = (forest && forest.veg && forest.veg.species) || [];
  const tree = (i) => { const sha = sp[i] && sp[i].sprite; return forest.baseUrl && typeof sha === 'string' && /^[0-9a-f]{16}$/.test(sha) ? vegSpriteUrl(forest.baseUrl, sha) : null; };
  if (forest && forest.mats && forest.mats.some((m, i) => due(m, 0, tree(i)))) return false;
  for (const m of [shrubs && shrubs.mesh && shrubs.mesh.material, grass && grass.bushes && grass.bushes.material]) {
    if (m && (m.userData.ownAsk || []).some((url, c) => due(m, c, url))) return false;
  }
  return true;
}

// the set at the first clean first-view scan after the latch: what phase 1
// joined since warmEarly (the Beam's pause holds bulk to the latch)
function warmShaders(prio = 'behind') {
  if (_warmPost) return;
  _warmPost = true;
  warmSet(prio);
}
// WARM-EARLY (w52 (a), p1 sampler: the compile queue idle from the first
// clean scan to the latch, 3.7-17 s after the Beam, then warmShaders ran 8 s
// after it): the same set from the first clean scan before the latch — class
// `behind`, below first / water / prepare, so the latch's own work goes first
function warmEarly() {
  if (_warm !== 'idle') return;
  warmSet('behind');
}
// one warm-up set at class `prio`: the canvas job, terrain's new variant
// keepers (pinned), the optic passes; a job of it still running from the
// set before re-scans once after it (the queue coalesces). `done` once every
// set asked is through.
function warmSet(prio) {
  _warm = 'running';
  _warmSets++;
  const keepers = cq.pin(terrain.variantKeepers());
  const all = [cq.want(scene, { label: 'warm.compile', use: 'warm.firstUse', prio })];
  if (keepers) all.push(cq.want(keepers, { label: 'warm.keepers', use: 'warm.firstUse', target: scene, prio }));
  // every mode zooms through the optic (desktop / phone past the band, VR the head zoom
  // window — combat or not, since vr-controls) and VR draws the window itself: both built
  // here, behind the overlay / the black, never at the first zoom in play (xr-stalls:
  // 'view' 241-580 ms x 9 on the Quest, session 870)
  all.push(...opticWants(keepers, 'warm.optic', 'warm.firstUse', prio));
  if (mode === 'vr' && weapons) all.push(cq.want(weapons.zoomWindow(camera), { label: 'warm.optic', use: 'warm.firstUse', target: scene, prio }));
  Promise.all(all)
    .catch((e) => console.warn('[warm] warmShaders:', e))
    .then(() => { if (--_warmSets === 0) _warm = 'done'; });
}

function tick() {
  act('frame');               // the long-task label of this frame's task (sched.js)
  secFrame(!!mode);           // upd_by sections (sched.js sec) count in play only
  const now = performance.now();
  _frameGap = now - prevT;
  _gapAvg += (_frameGap - _gapAvg) * (_gapAvg ? 0.05 : 1);
  adaptResTick(now, !!mode);
  prevT = now;
  watchFrame(_frameGap);
  if (glWatch) glWatch.frameStart(now);   // the GL health sample's fence not done by now: dropped (glwatch.js)
  if (_glLost) return;        // context lost: the world pauses until restored (watchContext)
  firstActs.frame();          // after a Beam: one cold first activation a frame (firstacts.js)
  const dt = Math.min(DT_MAX_S, _frameGap / 1000);
  frameTimes.push(_frameGap);
  if (_bench) benchFrame(now);
  sec('player');

  if (mode) {
    let jump = false, sprint = false, flare = false;
    let bikeSteer = 0, bikeThrottle = 0;
    _move.set(0, 0, 0);
    _steer.set(0, 0, 0);

    if (mode === 'flat') {
      // the Map is a full view: while it is up the body does not move (WASD,
      // sprint, flare, bike, jump, climb are dead; the keys stay tracked)
      const blind = !!(playerMap && playerMap.fullOpen) || !!_flight;   // the beam flight: no body control
      if (!blind) flat.moveDir(_move);
      sprint = !blind && flat.sprint;
      // 👁 Look (a Map card): ease the view onto the target; any mouse look
      // that moves the yaw away from the ease's own value hands control back
      if (_lookEase) {
        if (_lookEase.set !== undefined && (Math.abs(flat.lookYaw - _lookEase.set) > 1e-6 || Math.abs(flat.lookPitch - _lookEase.setP) > 1e-6)) _lookEase = null;
        else {
          const k = 1 - Math.exp(-dt * 9);
          let dy = _lookEase.yaw - flat.lookYaw;
          dy = Math.atan2(Math.sin(dy), Math.cos(dy));      // the short way round
          const dp = _lookEase.pitch - flat.lookPitch;
          flat.lookYaw += dy * k; flat.lookPitch += dp * k;
          _lookEase.set = flat.lookYaw; _lookEase.setP = flat.lookPitch;
          if (Math.abs(dy) < 0.002 && Math.abs(dp) < 0.002) _lookEase = null;
        }
      }
      if (_flight && _flight.pose) flightCam();
      else camera.rotation.set(flat.lookPitch, flat.lookYaw, 0, 'YXZ');
      beamFillTick(now);              // the charge's column, the flight's labels + card (beamcharge.js)
      player.yaw = flat.lookYaw;
      camera.getWorldDirection(_steer);
      flare = !blind && flat.keys.has('KeyS');

      // bike: [B] mounts/dismounts; W/S throttle+brake, A/D steer
      if (flat.keys.has('KeyB')) {
        flat.keys.delete('KeyB');
        if (!blind && player.toggleBike()) Sfx.click && Sfx.click();
      }
      if (player.bike && !blind) {
        bikeThrottle = (flat.keys.has('KeyW') ? 1 : 0) + (flat.keys.has('KeyS') ? -1 : 0);
        if (sprint && bikeThrottle > 0) bikeThrottle = 1;
        // follow mode steers itself; A/D become indicator toggles (keydown
        // handler above), ←/→ toggle them in both modes
        if (!player.bikeFollow) {
          bikeSteer = (flat.keys.has('KeyD') ? 1 : 0) - (flat.keys.has('KeyA') ? 1 : 0);
        }
      }

      // jump on Space (edge-triggered) — the jet-suit ignites by itself in the air
      if (flat.jump) {
        flat.keys.delete('Space');
        if (player.grounded && !blind) jump = true;
      }
      // climb: hold E against a steep slope
      const wantClimb = !blind && flat.keys.has('KeyE');
      flatClimbFlat(wantClimb, dt);

      // LMB: what the hand holds, along the centre ray of the view on screen
      if (flat.fireHeld && !_flight) tryFire(viewBand ? weapons.monoCam : camera, now);   // no fire in a beam flight
      if (_combatOn()) {
        if (flat.keys.has('Digit1')) weapons.switchTo('rifle');
        if (flat.keys.has('Digit2')) weapons.switchTo('sniper');
      }
      if (flat.keys.has('Digit3')) weapons.switchTo('monocular');
      if (flat.keys.has('KeyQ')) {
        flat.keys.delete('KeyQ');
        // Q on the bike toggles the riding method (auto-follow <-> manual);
        // off the bike it stays the weapon toggle
        if (player.bike) { player.toggleFollow(); Sfx.click && Sfx.click(); }
        else if (_combatOn()) weapons.toggle();
      }
    } else if (mode === 'mobile') {
      // stick X turns the body (yaw offset), same role as the VR rig rotation
      const turnDeg = tunables.locomotion.stick_turn_rate_deg ?? 160;
      if (Math.abs(mobile.turnRate) > 0) {
        mobile._yawOffset -= mobile.turnRate * turnDeg * (Math.PI / 180) * dt;
      }
      // device orientation → camera quaternion (same math as phone WebXR);
      // the 🔒 holds it where it is
      mobile.updateCamera(camera);
      _viewTilt(now);
      if (mobile.consumeMapToggle()) playerMap.toggleFull();
      camera.getWorldDirection(_steer);
      player.yaw = Math.atan2(-_steer.x, -_steer.z);
      mobile.moveDirWithYaw(_move, player.yaw);
      sprint = mobile.sprint;
      // ---- GPS follow: the avatar goes where the user really goes
      if (mobile.consumeGpsToggle()) {
        // after a failure the 📍 tap (and the card's "Try again", which sets the
        // same flag) means RETRY, not "switch off": the help card has just told
        // the user to fix a setting and tap again, and toggling off here would
        // look like the button doing nothing. The request still rides the touch
        // — this runs inside the same frame, within the gesture's activation.
        if (gps.on && gpsFailCause()) {
          gpsStopWatch();
          gps.err = null;
          gpsStart();
        } else {
          gps.on = !gps.on;
          if (gps.on) gpsStart(); else gpsStop();
        }
      }
      let gpsDist = 0;
      // a deflected stick means the user is driving — follow stands down and
      // resumes the moment the thumb comes off, without a mode to remember
      if (gps.on && gps.fix && !mobile.stickActive) {
        const g = gpsToGame(gps.fix.lat, gps.fix.lng, _meta);
        if (g.unsupported) gps.err = 'not CH';
        else if (!g.inBounds) gps.err = 'outside world';
        else {
          // clear only OUR coordinate complaints — a geolocation error still
          // matters even while an older fix is usable
          if (gps.err === 'not CH' || gps.err === 'outside world') gps.err = null;
          const dx = g.x - player.pos.x, dz = g.z - player.pos.z;
          gpsDist = Math.hypot(dx, dz);
          if (gpsDist > 40 && !gps.placing && Date.now() - gps.lastPlaceAt > 3000) {
            // too far to walk (drove here, or the first fix landed late): ask
            // the server to re-seat us. It refuses once we have moved and
            // combat is on — then the walk below just keeps closing the gap.
            gps.placing = true;
            placeAt(g.x, g.z).then(() => {             // onPlaced moved us (if accepted)
              gps.placing = false;
              gps.lastPlaceAt = Date.now();
            });
          } else if (gpsDist > 0.4) {
            // walk at full speed until the last 3 m, then ease in — GPS noise
            // is metres wide and a hard chase would jitter the body on the spot
            _move.set(dx / gpsDist, 0, dz / gpsDist).multiplyScalar(Math.min(1, gpsDist / 3));
            sprint = gpsDist > 6;
          } else {
            _move.set(0, 0, 0);          // arrived — inside the noise floor
          }
        }
      }
      mobile.setGps({
        on: gps.on, acc: gps.fix ? gps.fix.acc : null, err: gps.err, dist: gpsDist,
      });
      if (mobile.jump && player.grounded) jump = true;
      // the ◎ button = LMB (inspect, what the hand holds); FIRE holds a gun's
      // trigger. Both along the centre ray of the view on screen.
      const aim = viewBand ? weapons.monoCam : camera;
      if (mobile.fireHeld) tryFire(aim, now);
      if (mobile.consumeInspectTap()) tryFire(aim, now);
      // what the ◎ would open, ~7 Hz, through inspect's own resolver
      if (inspect && now - _inspProbeAt >= INSP_PROBE_MS) {
        _inspProbeAt = now;
        if (!_combatOn() && !playerMap.fullOpen) mobile.setInspect(inspect.probe());
      }
      if (mobile.consumeWeaponNext() && _combatOn()) weapons.toggle();
      // touch UI follows context: FIRE with a gun in combat, ⟳ in combat
      mobile.setUi({ combat: _combatOn(), weapon: weapons.current, mapOpen: playerMap.fullOpen });
    } else if (mode === 'vr') {
      const xrCam = renderer.xr.getCamera();
      xrEyeWorld(xrCam);
      headFold(xrCam, xrEmulated(), dt);   // the physical head's step = the body's move (player.step headMove)
      const inp = vr.poll(dt);
      // the VR landing: a button lands at once (flightSkip); the rig is the flight's
      const vrLanding = !!(_flight && _flight.kind === 'vr');
      if (_flight && _flight.kind === 'vr') {
        if (inp.faceA || inp.faceB || inp.faceX || inp.faceY || inp.jump || inp.menu || inp.mapToggle) flightSkip();
        if (_flight && _flight.pose) flightVr(xrCam);
      }
      // the laser first: either trigger is a click on a panel / the card,
      // else "the data at the point" (vrPoint); ☰ = the menu; the HUD
      vrui.update(inp, now, dt);
      _move.set(0, 0, 0);
      sprint = inp.sprint;
      // the gaze (xrEyeWorld: the head × the rig, this frame)
      xrCam.getWorldDirection(_steer);
      // the gaze heading on the ground: forward (fx, fz), right (−fz, fx);
      // looking straight up / down the rig yaw stands in
      const hl = Math.hypot(_steer.x, _steer.z);
      const fx = hl > 1e-3 ? _steer.x / hl : -Math.sin(rig.rotation.y);
      const fz = hl > 1e-3 ? _steer.z / hl : -Math.cos(rig.rotation.y);
      const lx = inp.axL.x, ly = inp.axL.y, lOn = Math.hypot(lx, ly) > 0.12;
      // jet-suit (owner 2026-11-03, jetvr.js): the head only LOOKS. The one
      // flight model (_stepJet) gets desktop's input — the forward of a yaw +
      // pitch — from the LEFT stick: X turns the heading (the rig turns with
      // it, below), Y pulled = nose up / pushed = nose down, released = the
      // launch's pitch, then level; the speed is automatic (the launch's
      // thrust → extras.jetThrust); no reverse, no brake; the right stick
      // does nothing in flight (no comfort turn, no zoom window). Off the jet it is the level line of the body's
      // motion, standing still the GAZE heading (the ignition frame's steer).
      _steer.copy(_jetVr.update(dt, tunables, player.gliding, player.vel, Math.atan2(-fx, -fz), inp));
      if (player.gliding) _move.set(0, 0, 0);   // the sticks fly the suit, not legs
      // the owner's map (2026-10-20, APP_PLAYER "VR controls"): X = bike on
      // / off; on the bike the left stick is throttle (Y) + steer (X) (manual)
      // or flick-toggles the turn indicators (auto-follow), Y = auto ⇄ manual
      if (inp.faceX && player.toggleBike()) Sfx.click && Sfx.click();
      if (player.bike) {
        bikeThrottle = -inp.axL.y;
        if (player.bikeFollow) {
          const ax = inp.axL.x;
          if (Math.abs(ax) > 0.65 && !vrIndArmed) {
            player.toggleIndicator(ax > 0 ? 1 : -1);
            vrIndArmed = true;
          }
          if (Math.abs(ax) < 0.3) vrIndArmed = false;
        } else {
          bikeSteer = inp.axL.x;
        }
        _move.set(0, 0, 0);            // stick drives the bike, not legs
      } else if (!player.gliding) {
        // the left stick = WASD: Y forward / back, X strafes, along the GAZE
        // heading — desktop's moveDir runs along the view (lookYaw), and VR's
        // view is the head. Full deflection runs (sprint, as Shift). The right
        // stick turns the rig.
        if (lOn) {
          // ly < 0 = stick pushed
          _move.set(-fx * ly - fz * lx, 0, -fz * ly + fx * lx);
          if (_move.lengthSq() > 1) _move.normalize();
        }
      }
      // A = the ☰ menu open / close everywhere — walking, bike, jet-suit, a
      // station (owner 2026-11-03: the wrist ☰ was "unusable, too near"); the
      // press that lands the VR landing only lands. A station sign's rows are
      // the right trigger's (vrPoint) — B first closes what is open (the
      // card, a panel), else fires the zone's 2nd enabled row. Y: off the
      // bike the mic, on it the riding method.
      if (inp.faceA && !vrLanding) vrui.toggleMenu();
      if (inp.faceB) {
        if (inspect && inspect.isOpen) inspect.close('back');
        else if (vrui.anyOpen) vrui.back();
        else {
          const zoneEnabled = zones && zones._rows ? zones._rows.filter((r) => !r.dim && r.onShoot) : [];
          const row = zoneEnabled[1];
          if (row) { row.onShoot(); Sfx.hitConfirm(); }
        }
      }
      if (inp.faceY) {
        if (player.bike) { player.toggleFollow(); Sfx.click && Sfx.click(); }
        else if (voice.ready) toggleVoiceMute();
      }
      // the left stick click = the Map panel (open / close); the left
      // trigger's own toggle is vrui's (off every target). In flight the left
      // stick flies (owner 2026-11-03): its click opens nothing.
      if (inp.mapToggle && !player.gliding) vrui.toggleMap();

      // jump (right stick click) — the jet-suit ignites by itself in the air;
      // the click's tilt zoomed nothing: its gesture's zoom is undone (vr-nomap)
      // in flight the right stick, its click included, does nothing (owner 2026-11-03)
      if (inp.jump && !player.gliding) weapons.zoomHeadCancel();
      if (inp.jump && player.grounded) jump = true;

      // climbing: grip near the mountain anchors that hand
      vrClimb(inp, dt);

      // combat is not bound in VR (owner 2026-10-20: "ignore the combat mode
      // for now"): no trigger fires, no weapon cycle; the triggers point
      // (vrui → vrPoint: a station sign row, else the inspect card there).
      // two-handed steady: OFF hand (left) close to the weapon fore grip
      const offHand = xrCtrl('left');
      if (offHand && weapons.model.userData.foreGripPos) {
        const fg = weapons.model.localToWorld(_foreGrip.copy(weapons.model.userData.foreGripPos));
        weapons.twoHanded = offHand.getWorldPosition(_handPos).distanceTo(fg) < 0.22;
      }
      // right stick X = the comfort turn (☰ → Comfort, persisted): smooth
      // (default, the body-turn rate) or one snap step per flick (re-armed)
      // flying: the jet's heading turn (smooth at glide.stick_turn_rate_deg,
      // or the snap step) turns the body, so the view turns with the course
      if (player.gliding) {
        if (_jetVr.dYaw) rig.rotateY(_jetVr.dYaw);
      } else if (vrui.turn === 'snap') {
        if (inp.snap) rig.rotateY(-inp.snap * vrui.snapDeg * Math.PI / 180);
      } else if (Math.abs(inp.turnX) > 0.15) {
        rig.rotateY(-inp.turnX * (tunables.locomotion.stick_turn_rate_deg ?? 160) * (Math.PI / 180) * dt);
      }
      // right stick Y = the zoom (the desktop wheel's twin): the head zoom
      // window (weapons.js). In flight the right stick does nothing and the
      // window never shows (owner 2026-11-03: "don't bring up the zoom map, flying is
      // to look around") — one up at the ignition closes.
      if (player.gliding) { if (weapons.headZTarget > 1 || weapons.headOn) weapons.headOff(); }
      else if (weapons.monoActive && !(inspect && inspect.isOpen)) weapons.zoomHeadRate(inp.zoomY, dt);
      player.yaw = rig.rotation.y;
      // the VR landing: no body control (stick, jump, bike) until STAND
      if (_flight) { _move.set(0, 0, 0); jump = false; sprint = false; bikeSteer = 0; bikeThrottle = 0; }
    }

    // the one view: zoom input + the naked camera's fov (desktop / phone)
    if (mode === 'flat' || mode === 'mobile') { _viewInput(dt); _viewZoomStep(dt); }

    // the device bench drives the body along its route (bench.js); else the player does
    if (!(_bench && _bench.driving && benchDrive(now)) && !seamDrive({ player, flat, hf })) {
      player.step(dt, _move, sprint, jump, { steerDir: _steer, flare, bikeSteer, bikeThrottle, headMove: mode === 'vr' ? _hdMove : null,
        jetThrust: mode === 'vr' ? _jetVr.thrust : 1 });
    }
    bikeView.update(player, dt);
    // on the bike the BODY faces where the bike goes: steering rotates the
    // view base (mouse/head still look around on top of it)
    if (player.bike) {
      const dYaw = player.bikeYaw - prevBikeYaw;
      if (dYaw) {
        if (mode === 'flat') flat.lookYaw += dYaw;
        else if (mode === 'mobile') mobile._yawOffset += dYaw;
        else rig.rotateY(dYaw);
      }
    }
    prevBikeYaw = player.bikeYaw;
    if (player.gliding && !prevGliding) Sfx.jetOn();
    prevGliding = player.gliding;

    // ride platform-carry (funicular car floor or aerial cabin floor).
    // BOTH updates must run every frame — they animate the cars/cabins.
    sec('lifts');
    const carryF = funicular.update(dt, player.pos);
    const carryA = aerialway.update(dt, player.pos);
    const carry = carryF || carryA;
    let carrySpeed = 0;
    riding = false;
    player.carried = false;
    // dizzy freeze: the server has the position locked, so the car carry must
    // not drag the body (it would diverge and trigger a corr snap)
    if (carry && player.state !== 'climb' && !player.gliding
        && player.dizzyFreeze <= 0) {
      player.pos.x += carry.x;
      player.pos.z += carry.z;
      // ride ON the car floor — never on the terrain below (which decouples
      // the rider in dips) and never under the surface (track imprecision).
      // But INSIDE the carved clearance corridor the terrain above the bed is
      // exactly what the carve removes — tunnel and gallery sections run under
      // the mountain — so re-clamping there shoved riders up to 2.9 m through
      // the cabin roof and out of the carry entirely (measured on 17 % of
      // St.Moritz-Corviglia). Only re-clamp where the corridor is not carved.
      player.pos.y = carry.floorY;
      if (corridorCovAt(player.pos.x, player.pos.z) <= 0.5) {
        const rg = hf.standHeight(player.pos.x, player.pos.z);
        if (rg !== null && rg > player.pos.y) player.pos.y = rg;
      }
      player.vel.y = 0;
      player.grounded = true;
      riding = true;
      player.carried = true;       // suspends the anti-trap watchdog (_antiStuck)
      carrySpeed = Math.hypot(carry.x, carry.y, carry.z) / Math.max(dt, 1e-3);
    }

    sec('zones');
    if (zones) {
      try {
        zones.dwellS = (tunables.world && tunables.world.funicular
          && tunables.world.funicular.dwell_s) || 12;
        zones.update(dt, player, mode === 'vr' ? renderer.xr.getCamera() : camera,
          mode === 'vr' ? null
            : _eye.set(player.pos.x, player.pos.y + player.eye, player.pos.z),
          mode);
      } catch (e) { console.error('[zones]', e); }
    }

    // chairlift riders have NO walk-in carry (the zone re-pin alone holds
    // them), so the carry block above saw nothing: mark the ride and give the
    // comfort vignette the cable speed it is actually moving at
    let chairRide = null;
    if (zones && zones.riding) {
      riding = true;
      const rl = zones.funicular.lines[zones.riding.li];
      if (rl && rl.kind === 'chair_lift') {
        chairRide = rl;
        carrySpeed = Math.max(carrySpeed, (tunables.world && tunables.world.aerial
          && tunables.world.aerial.chair_speed_mps) || 2.5);
      }
    }
    // boarding, arrival and stepping out all BEAM the body (stationzones.js),
    // and a wedge anchored at the platform we left streams the wrong valley
    if (zones && zones.riding !== _prevRiding) {
      _prevRiding = zones.riding;
      focus.reset();
    }

    rig.position.set(player.pos.x, player.pos.y, player.pos.z);
    if (mode === 'vr') {
      // the eye stands over the body: the rig is the body less the head's
      // floor point in rig space (headFold); a comfort turn so pivots about
      // the head, not around a rig origin beside it
      const c = Math.cos(rig.rotation.y), s = Math.sin(rig.rotation.y);
      rig.position.x -= _hdRef.x * c + _hdRef.z * s;
      rig.position.z -= -_hdRef.x * s + _hdRef.z * c;
      rig.position.y += _seat.lift;   // the eye height kept across a floor re-estimate (headseat.js)
    }
    if (mode === 'vr' && _flight && _flight.kind === 'vr' && _flight.pose) {
      const fl = _flight;       // the VR landing lowers the rig (translation only)
      rig.position.set(fl.base.x + fl.pose.dx, fl.base.y + fl.pose.h - fl.eyeH, fl.base.z + fl.pose.dz);
    }
    if (mode === 'flat' || mode === 'mobile') {
      if (_flight && _flight.pose) {
        const fl = _flight;     // the path from the base, the body's drift joined in over the end (handRel)
        handRel(camera.position, fl.base, fl.pose, player.pos, player.eye, fl.eyeH, fl.pace.s, fl.F);
      } else camera.position.y = player.eye;
      if (_resBack && !_flight) resTick(now);
    }
    // swimming in VR: the head pose is physical, so the RIG is nudged until
    // the HMD rides eye_over_m above the sheet — the head itself is never
    // steered (same rule as everywhere). Flat mode is the player.eye blend.
    if (mode === 'vr' && player._swimK > 0 && player._swimSurf !== null) {
      const over = (tunables.water && tunables.water.eye_over_m) ?? 0.35;
      const want = player._swimSurf + over - camera.position.y;
      rig.position.y += (want - rig.position.y) * player._swimK;
    }
    retractWeapon();

    // comfort vignette is a VR-motion-sickness aid — on a flat screen it just
    // draws a huge translucent dome over the world
    if (mode === 'vr') {
      vignette.update(dt, Math.max(player.speed, Math.abs(player.vel.y), carrySpeed, flightVig()),
        player.state, tunables);
    } else {
      vignette.mesh.visible = false;
    }
    // dizzy red wash: full while frozen, fading with the recovery, soft pulse
    if (dizzyMesh) {
      const dz = player.dizzy01;
      dizzyMesh.visible = dz > 0.01;
      if (dizzyMesh.visible) {
        dizzyMesh.material.opacity = 0.12 + 0.38 * dz
          + 0.06 * dz * Math.sin(now * 0.005);
      }
    }
    jetsuit.update(player, dt);
    sec('view');
    if (mode === 'vr') {
      weapons.updateScope(dt, camera, tunables);
      // label data scopeTarget skipped last frame (the RT's off-frame) but
      // that this updateMonocular draws after all. Computed before monoCam
      // is re-posed: the camera state scopeTarget would have used.
      if (_monoLabelOwed && weapons.monoDrawsNext()) _monoLabelData = _monoLabels();
      weapons.updateMonocular(dt, camera, _monoLabelData);
    } else {
      // desktop / phone: the band hand-over + the optic pose, then the
      // labels and the range with the pose that renders this frame
      _viewPose(dt, now);
    }
    // scope focus wedge: the optic cameras are posed by the calls above, so
    // this is the one spot where they are current AND still ahead of every
    // streaming manager below
    const ocam = opticFocusCam(viewBand);
    // the lazy ground materials' optic half (groundmat.js): the first optic /
    // magnified view, every tier (idempotent there; asked once here)
    if (ocam && !_opticNoted) { _opticNoted = true; noteGroundOptic(); }
    const viewFocus = !!ocam && ocam === camera;
    sec('focus');
    focus.update(ocam, player.pos, dt, viewFocus);
    if (panoRing) ringTick();
    needTick();                 // a move / turn / zoom re-scores the bulk queue (keyed: cheap when nothing changed)
    _viewFocusLayers(viewFocus);
    // the view's magnification over the naked eye: the desktop / phone view
    // (and its band) at Z, an optic at its zoom × detail_mag_1x. The far
    // window follows the wedge out to the relight's magnified reach; the
    // furniture's optic layer reaches its gates × it (furniture.js opticRange)
    const opticMag = viewFocus || (viewBand && (mode === 'flat' || mode === 'mobile'))
      ? viewZoom.z : focus.zoom * focus.mag1x;
    if (farWin) farWin.update(focus, opticMag);
    const speed3 = Math.hypot(player.vel.x, player.vel.y, player.vel.z);
    Sfx.listener(mode === 'vr' ? renderer.xr.getCamera() : camera);
    Sfx.wind(speed3, player.gliding);
    // water presentation: lapping loop keyed to stroke speed, one splash per
    // entry/exit (player.step arms player.splash), and — while the eye is
    // under the sheet (entry plunge / a dip) — a whole-mix muffle + blue wash
    const wCfg = tunables.water || {};
    Sfx.swim(player._swimK * (wCfg.lap_gain ?? 1),
      Math.hypot(player.vel.x, player.vel.z));
    if (player.splash > 0) {
      Sfx.splash(player.splash * (wCfg.splash_gain ?? 1));
      player.splash = 0;
    }
    const eyeWorldY = mode === 'vr'
      ? rig.position.y + camera.position.y : player.pos.y + player.eye;
    const wsurfHere = player.water
      ? player.water.surfaceAt(player.pos.x, player.pos.z) : null;
    const eyeDepth = wsurfHere !== null ? wsurfHere - eyeWorldY : 0;
    Sfx.duck(eyeDepth);
    if (waterMesh) {
      waterMesh.visible = eyeDepth > 0.02;
      if (waterMesh.visible) {
        waterMesh.material.opacity = Math.min(0.55, 0.25 + eyeDepth * 0.3);
      }
    }
    // chairlift hum: full while riding, faint standing under a passing chair
    const nearAerial = aerialway.nearest(player.pos);
    if (firstActs.go('audio')) {
      Sfx.engine(player.bikeSpeed, bikeThrottle, player.bike);
      Sfx.jet(player.gliding
        ? 0.3 + 0.7 * Math.min(1, speed3 / ((tunables.glide.max_speed_kmh || 100) / 3.6)) : 0);
      Sfx.lift(chairRide ? 0.6
        : (aerialway.nearestKind === 'chair_lift' && nearAerial < 18
          ? 0.35 * (1 - nearAerial / 18) : 0));
    }
    if (chairRide && zones.riding) {
      // sheave clack as the ridden chair crosses each pylon
      const car = chairRide.cars[zones.riding.carIdx];
      const sup = chairRide.line.sup;
      let at = -1;
      for (let i = 1; i < sup.length - 1 && at < 0; i++) {
        if (Math.hypot(car.position.x - sup[i][0],
          car.position.z - sup[i][2]) < 5) at = i;
      }
      if (at >= 0 && zones.riding._pyl !== at) Sfx.liftClack();
      if (at >= 0) zones.riding._pyl = at;
    }
    sec('terrain');
    // the flight: the tree selects for the camera; behind the Map (lead) for the landing view
    const lead = _flight && _flight.lead && _flight.leadCam;
    terrain.update(lead ? _flight.base : _flight && _flight.eye ? _flight.eye : player.pos);
    terrain.ground?.vt.view(lead ? _flight.leadCam : _charge && _charge.leadCam ? _charge.leadCam : mode === 'vr' ? renderer.xr.getCamera() : camera);   // the painter's view order (gmtex.js)
    sec('terrain.focus');
    terrain.updateFocus(focus);          // own cadence, keyed on focus.gen
    sec('buildings');
    buildings.update(player.pos, focus);
    sec('farbld');
    farBuildings.update(focus, buildings);   // the far LOD: wedge minus the drawn GLB tiles
    // the trail lines: the focus layer's instrument, fading in with the view
    // zoom on desktop / phone (0 at and below view_arm.zoom_on; a lingering
    // wedge at 1x draws none); always on there (no gradings switch). VR draws
    // none, the zoom window included (zoom 0: the Map's coloured lines were
    // the monocular look, vr-nomap). Draped on the drawn ground
    // (after terrain.updateFocus: the tile set this frame draws)
    sec('traillines');
    if (trailLines) {
      const flatView = mode === 'flat' || mode === 'mobile';
      trailLines.update(focus, terrain, flatView ? viewZoom.z : 0,
        (RENDER_CFG.focus.view_arm && RENDER_CFG.focus.view_arm.zoom_on) || 2, viewZoom ? viewZoom.zBand : 1,
        player.pos, now);
    }
    sec('trees');
    if (firstActs.go('trees')) forest.update(player.pos, focus);    // + the wedge's far trees (focus layer)
    sec('shrubs');
    if (!vrStyleOn()) shrubs.update(player.pos, focus);    // + the wedge's far shrubs (focus layer); THE VR LOOK: none
    sec('grass');
    if (!vrStyleOn()) grass.update(player.pos, dt);   // THE VR LOOK: no grass scatter (the ground colour carries it)
    sec('surface');
    if (player.surface && firstActs.go('surface')) player.surface.ensure(player.pos.x, player.pos.z);   // classmap 3 x 3 (no-op in-tile)
    sec('ways');
    roadnet.update(player.pos, focus);   // + the wedge's far-LOD road layer
    // drawn set: only after a 25 m move / a pending snap; + the wedge's far
    // items on the focus layer (re-planned per wedge gen / zoom notch)
    sec('furniture');
    if (furniture) furniture.update(player.pos, now, false, focus, opticMag);
    sec('shopsigns');
    if (shopSigns) shopSigns.update(player.pos);        // drawn set: only after a 25 m move
    sec('structs');
    if (structDraw) structDraw.update(player.pos, focus, terrain);   // view range + scope wedge + far rule
    // the terrain carve raster is a window around the player — recentre it as
    // they walk (see corridor.js: a world-sized raster would be 106 MB)
    sec('corridors');
    updateCorridors(player.pos);
    sec('misc');
    waterTime.value += dt;
    updateClouds(player.pos, dt);

    // pose report every other frame (~30-36 Hz)
    inputAccum += dt;
    if (inputAccum >= 1 / 32) {
      inputAccum = 0;
      let head = null, hands = null;
      if (mode === 'vr') {
        const xrCam = renderer.xr.getCamera();
        const hp = xrCam.getWorldPosition(_poseP);
        const hq = xrCam.getWorldQuaternion(_poseQ);
        head = { p: [r2(hp.x), r2(hp.y), r2(hp.z)], q: hq.toArray().map(r3) };
        hands = ['left', 'right'].map((h) => {
          const c = xrCtrl(h);
          if (!c) return null;
          const p = c.getWorldPosition(_poseP);
          const q = c.getWorldQuaternion(_poseQ);
          return { p: [r2(p.x), r2(p.y), r2(p.z)], q: q.toArray().map(r3) };
        });
      } else {
        const hp = camera.getWorldPosition(_poseP);
        head = { p: [r2(hp.x), r2(hp.y), r2(hp.z)], q: camera.quaternion.toArray().map(r3) };
      }
      net.sendInput(player.pos, player.yaw, player.state, head, hands,
        player.bike ? player.bikeInd : 0);
    }
  } else if (_hasTarget) {
    // before entry: keep streaming for `_loadAt` (a card's prefetch, a GPS
    // fix, the place reply). Both calls are gated (24 m / 1 s, 64 m); the
    // terrain's 1 s rescan retries a failed tile and its cut slot finishes
    // bore-cut tiles, which otherwise only ran in play. Nothing draws for it
    // unless a tile lands (landingDue). No target: nothing streams.
    terrain.update(_loadAt);
    buildings.update(_loadAt, null);
    if (forest) forest.stream(_loadAt);   // gated (64 / 48 m or 2 s): a failed tile is asked again
    if (shrubs) shrubs.stream(_loadAt);
    needTick();                 // a resize / pixel-angle change before entry
  }
  if (loadT.t_hf_ready === null && (mode || _hasTarget)) {
    const lp = loadPos();
    if (hf.readyAround(lp.x, lp.z)) loadT.t_hf_ready = now;
  }

  sec('gen');
  if (firstActs.go('canopy')) canopyTick(mode ? player.pos : _hasTarget ? _loadAt : null, now);   // the canopy tiles wanted (own cadence)
  if (firstActs.go('forestvol')) forestVolTick(mode ? player.pos : _hasTarget ? _loadAt : null);   // the far canopy volume's shell plan + one group build
  if (firstActs.go('gen')) genFrame(renderer);   // GENSURF generation (gensched.js: after heavyOk, its own ms budget)
  if (firstActs.go('forestvol')) splatForestTick(mode ? player.pos : _hasTarget ? _loadAt : null);   // THE SPLATS: reach, landed crown tiles, the volume's resident tiles
  sec('net');
  net.updateRemotes(scene, dt, player.roads);
  voice.update(net.remotes);    // voices follow the freshly interpolated heads
  sec('map');
  if (!_mapRaf) updatePlayerMap(now, dt);   // before entry the Map has its own loop (mapLoop)
  sec('hud');
  // Guide me (beacons.js RouteGuide): the waypoint ~150 m ahead on the route,
  // every GUIDE.period_ms; combat on hides the waypoint, so it holds still
  if (beacons && beacons.guide && mode && now - _guideAt >= GUIDE.period_ms && !_combatOn()) {
    _guideAt = now;
    beacons.guideTick(player.pos.x, player.pos.z, _rangeGround);
  }
  stepFx(now);
  // inspect: the trigger latch + its dismiss rules (after every tryFire of
  // this tick)
  if (inspect && mode) {
    try { inspect.frame(now); } catch (e) { console.error('[inspect]', e); }
  }
  if (mode === 'vr') scopeTarget();
  updateHud();
  updateVrHud();
  telemetry(now);

  // ── the beam-in latch (R13): from a successful ⤳ Beam until the player
  // can STAND — the near terrain rings (terrain.nearReady), the ground's L0
  // node block and the collision of the building tiles there; phase 1 and the
  // vegetation stream on behind. A near tile that fails for good (its key is
  // re-queued every rescan and never lands) would hold that forever:
  // load.dismiss_timeout_ms after the Beam it releases anyway, with a
  // warning. At the latch: the far building GLBs, the bulk class resumes,
  // the slicer's play budget. Flat / phone keep the overlay until the first
  // view is warm (fvTick / fvReady) and the frames run smooth (OVL), every
  // mode only once the top level is resident (topOk, THE TOP GATE) and the
  // owner's sprites are worn (spritesOk, THE SPRITE GATE) and the cold
  // first activations have each had their frame (firstActs.done); the load
  // report (the first one) goes when the overlay does, saying what released
  // it; the warm-up pass (warmShaders) runs behind it. VR's headset shows the
  // loading view (xrLoad) until the latch and the vegetation rule (vegOk).
  if (_beamIn && mode) {
    if (!_beamIn.at) {
      const ready = nearOk(player.pos) && buildings.nearColReady;
      const late = !ready
        && now - _beamAt >= ((RENDER_CFG.load && RENDER_CFG.load.dismiss_timeout_ms) ?? 30000);
      if (late) {
        console.warn('[load] beam-in released by timeout: near ground not resident', latchState());
      }
      if (ready || late) {
        _beamIn.at = now;
        if (loadT.t_dismiss === null) loadT.t_dismiss = now;
        // the ground tree raises nearReady in this same tick, and the
        // whenNearReady stamp is a microtask that would land after the report
        if (loadT.t_near_ready === null && terrain.nearReady) loadT.t_near_ready = now;
        _entryWhy = ready ? 'latch' : 'timeout';   // timeout: dismiss_timeout_ms let it go
        buildings.allowFar();
        resumeBulk('beam');
        resumeBulk('save-data');                // backstop: in the world, Save-Data holds nothing
        setPhase('play');
      }
    }
    // the water variants of a near lake belong to the first view: after the
    // latch, behind the overlay (a far one waits for the overlay to go)
    if (_fv && _beamIn.at && _waterDue && !_fv.water && !waterBoxesBusy() && waterNear(player.pos)) {
      _waterDue = false;
      const fv = _fv;
      fv.water = warmWater().then(() => { fv.water = null; });
    }
    fvTick(now);
    if (_beamIn.at) {
      // a smooth frame once the first view is warm counts; a slow one (or the
      // first view not warm again: new content joined) starts the count over
      const sm = ovlSmooth(_beamIn.gaps || (_beamIn.gaps = []), _frameGap);
      _beamIn.smooth = fvReady(now) && sm ? _beamIn.smooth + 1 : 0;
    }
    const release = _flight ? flightStage(now)
      : !_beamIn.at || !topOk(now) || !spritesOk(now) || !firstActs.done ? null : mode === 'vr' ? (vegOk(now) ? 'vr' : null)
        : _beamIn.smooth >= OVL.frames ? 'smooth' : now - _beamIn.at >= OVL.max ? 'max' : null;
    if (release) {
      if (release === 'max') console.warn('[load] beam-in released by ovl_max_ms:', fvReady(now) ? 'frames not smooth' : 'first view not warm', latchState());
      if (!terrain.gm.vt.topReady()) console.warn('[load] beam-in released by timeout: top level not resident', latchState());
      if (!spritesWorn()) console.warn('[load] beam-in released by timeout: owner sprites not worn', latchState());
      _beamIn = null;
      _fv = null;
      _ovlGoneAt = now;
      if (loadT.t_ovl === null) { loadT.t_ovl = now; _ovlRelease = release; }
      endFlight(true);
      sendLoadReport('entry', _entryWhy || 'latch');
      // a return visit's GPU probe: once, idle, never in XR; refreshes the store only (device.js).
      // Armed when the overlay is OFF (the fade ended), not at the fade's start: its long task fell inside the fade
      hideBeamIn(true, () => deferGpuProbe(renderer));
      if (mode === 'mobile') mobile.recenter();
      if (mode === 'flat' || mode === 'mobile') {
        // the warm-up pass ran behind the overlay (fvTick); released by
        // ovl_max_ms before it began, it runs now, past this frame and the next
        requestAnimationFrame(() => setTimeout(warmShaders, 0));
        // the first world of a mode: its briefing (releases the pointer lock); not over a bench run
        if (!_bench) briefing.auto('3d', mode);
      }
    }
  }
  if (xrLoad) {
    // THE VR LOADING VIEW (xrload.js): from the session's first frame after a ⤳ Beam (setSession at
    // the grant, enterAt) to the VR landing's fade — the room, the name, the readiness ring; never black
    const fl = _flight && _flight.kind === 'vr' ? _flight : null;
    const pres = renderer.xr.isPresenting, fadeMs = (tunables && tunables.flight && tunables.flight.vr_fade_ms) || 1200;
    const target = loadOpacity({ presenting: pres, mode, entering: _entering, beamIn: !!_beamIn,
      fl: fl ? { seen: !!fl.seen, stage: fl.stage, tFade: fl.t.fade } : null, now, fadeMs });
    if (pres && target > 0 && now - _xrl.at >= XRL_MS) _xrl.p = loadProgress(xrlState(fl, now), _xrl.p);
    // THE PLACE (xrrelief.js, once a Beam): the landing known (the VR landing's base; the instant wait: the body) →
    // the Map's own posts + picture of it in the room — the skyline from there, the valley's relief rising ahead
    if (pres && target > 0 && !_xrl.place && mode === 'vr' && _beamIn && (fl ? fl.base : player)) {
      const at = fl ? fl.base : player.pos;
      _xrl.place = true;
      xrLoad.setPlace({ posts: mapHeights ? mapHeights.posts : null, underlay: () => (playerMap ? playerMap.underlay() : null),
        W: mapHeights ? mapHeights.extentX : hf.extentX, H: mapHeights ? mapHeights.extentZ : hf.extentZ, at: { x: at.x, y: at.y, z: at.z }, far: farHf });
    }
    xrLoad.update(now, renderer.xr, target, _xrl.p, fadeMs);
  }
  // the water warm-up the class grid asked for during the beam-in
  // (warmWaterSoon) and the first view did not take: once the overlay is
  // gone — or the beam-in ended any other way — a frame later
  if (_waterDue && !_beamIn) {
    _waterDue = false;
    requestAnimationFrame(() => setTimeout(warmWater, 0));
  }

  // landing page or the Map before entry up: draw only what is new to warm
  // up (landingDue, R8); the beam-in overlay and play draw every frame
  const landing = (overlay.style.display !== 'none' || (playerMap && playerMap.preEntry && !mode))
    && !renderer.xr.isPresenting;
  sec(null);
  const tRnd = performance.now();
  // quiet start (openHeavy): no landing draw, no scan, before the first card / the Beam;
  // behind the overlay no draw while a first-view pass has work (fvTick)
  if (_flight) _flight.drew = false;
  if ((!landing || (heavyOk() && landingDue(now) && !landingHold())) && !(_fv && _fv.busy)) {
    if (_flight) _flight.drew = true;
    const f0 = renderer.info.render.frame;
    scene.updateMatrixWorld();  // this tick's one walk, after every mover above
    // the queue's in-frame counter measures the programs three builds inside
    // this draw (compileq.js drawn: 0 is the goal; __vr.compileq().inFrame)
    const dl = landing ? 'landing' : _firstDraw && mode ? 'frame.first' : 'frame';
    const draw = () => cq.drawn(() => {
      if (viewBand && (mode === 'flat' || mode === 'mobile')) { weapons.renderMonoFull(); return; }
      // the main pass culls its provably hidden units (viscull.js); never the landing's warm-up draw
      if (mode && !landing && visCull) visCull.arm(mode);
      const ls = lookScale && lookScale.on() ? lookScale : null;   // the flight's lower resolution: the sub-rect, then the copy
      if (ls) ls.begin();
      try {
        if (_viewFocusOn) {
          // view mode: the wedge terms (coarse-under-finer sink, ground fades)
          // bite in this pass only, as weapons.renderMonoFull brackets the optic
          focus.setPass(true, viewZoom.z);
          renderer.render(scene, camera);
          focus.setPass(false);
        } else if (!farviewDraw(THREE, renderer, scene, camera, mode)) renderer.render(scene, camera);
      } finally { if (visCull) visCull.disarm(); if (ls) ls.present(); }
    }, dl);
    // the first world draw after ⤳ Beam under its own label (load report
    // lt_by 'frame.first': its uploads / programs, apart from the frames)
    if (xrLoad && xrLoad.only(renderer.xr.isPresenting, mode)) {
      // the loading view ALONE (xrload.js only): the session's frames before the place reply and the
      // first ONLY_FRAMES of a Beam — cheap and certain, no world program built in them; the world
      // (and its frame.first) draws behind the view from the next frame on
      const m = camera.layers.mask;
      camera.layers.set(XRLOAD_LAYER);
      try { renderer.render(scene, camera); } finally { camera.layers.mask = m; }
      if (_flight) _flight.drew = false;
    } else if (_firstDraw && mode && !landing) { _firstDraw = false; act('frame.first', draw); } else if (mode && !landing) famTris.sample(draw); else draw();
    if (mode && !landing) visNote();
    if (landing && renderer.info.render.frame !== f0) landingDrew(now);
  }
  if ((mode === 'flat' || mode === 'mobile') && !landing) _viewHud(now);
  if (mode && !landing) {
    const i = _splitN++ % SPLIT_CAP;
    const tEnd = performance.now();
    _updMs[i] = tRnd - now;
    _rndMs[i] = tEnd - tRnd;
    xrFrame(now, tEnd);        // the slicer's phase reference in a headset (sched.js THE FRAME ROOM)
  }
  if (glWatch) glWatch.frameEnd(now);   // the GL health sample's fence (glwatch.js: drained in the gap, never a stall)
}

// The visibility frame of the view this tick drew (APP_VISIBILITY P0): built
// AFTER the draw, when every camera of the pass is current (VR: three's
// updateCamera in render posed the union camera + both eyes; the band's
// monoCam is updated by its own render). No family reads it yet — P2 moves
// the culls onto it; the ground tree's still cache is its bound-0 (exact)
// class (groundtree.js stillDraw). Its counts (__vr.vis) are the measured
// share of stable / exact frames per class.
function visNote() {
  const xr = renderer.xr.isPresenting;
  const cam = xr ? renderer.xr.getCamera() : (viewCam || camera);
  _visVp[0] = renderer.domElement.width; _visVp[1] = renderer.domElement.height;
  _visGens.terrain = terrain.gen;
  _visGens.buildings = buildings.drawnGen;
  _visGens.focus = focus.gen;
  visMain.begin(poseOf(cam, _visPose, _visVp, xr ? cam.cameras : null), _visGens, boundsFor(mode));
}

// VR climbing: latest grip near a grabbable surface wins
function vrClimb(inp, dt) {
  const held = { left: false, right: false };
  for (const g of inp.grips) held[g.hand] = g.pressed;
  if (climbHand) {
    if (!held[climbHand]) {
      player.endClimb();
      climbHand = null;
    } else {
      xrCtrl(climbHand).getWorldPosition(_handPos);
      player.updateClimb(dt, _handPos);
      return;
    }
  }
  for (const hand of ['left', 'right']) {
    if (!held[hand]) continue;
    xrCtrl(hand).getWorldPosition(_handPos);
    if (player.canGrab(_handPos)) {
      player.startClimb(hand === 'left' ? 0 : 1, _handPos);
      climbHand = hand;
      Sfx.grab();
      break;
    }
  }
}

// flat-mode climbing: hold E facing a steep slope to scale it
function flatClimbFlat(want, dt) {
  if (!want) {
    if (flatClimbing) { player.endClimb(); flatClimbing = false; }
    return;
  }
  const fwd = new THREE.Vector3();
  camera.getWorldDirection(fwd);
  const px = player.pos.x + fwd.x * 1.2, pz = player.pos.z + fwd.z * 1.2;
  const h = hf.height(px, pz);
  if (h === null) return;
  const n = hf.normal(px, pz);
  const steep = n && Math.acos(Math.min(1, n.y)) * 180 / Math.PI
    > tunables.climb.max_slope_walkable_deg - 8;
  const nearWall = h > player.pos.y + 0.4;
  if ((steep || nearWall) && player.stamina > 0.5 && tunables.climb.enabled
      && !player.dizzyActive) {
    if (!flatClimbing) { player.state = 'climb'; flatClimbing = true; Sfx.grab(); }
    const up = 2.2 * tunables.climb.pull_strength * dt;
    player.pos.y += up;
    player.pos.x += fwd.x * 0.9 * dt;
    player.pos.z += fwd.z * 0.9 * dt;
    player.vel.set(0, 0, 0);
    // don't climb INTO the mountain — Player.step early-returns for state
    // 'climb', so this path is the ONLY clamp it gets (the VR path has the
    // same allowance in updateClimb: body hugs the face, eye stays outside)
    const gc = hf.height(player.pos.x, player.pos.z);
    if (gc !== null && player.pos.y < gc - 1.2) player.pos.y = gc - 1.2;
    player._buildingResolve(false, true);   // walls stay solid while climbing
    hf.ensureAround(player.pos.x, player.pos.z);
    player.stamina = Math.max(0, player.stamina - dt);
    if (player.stamina <= 0) { player.endClimb(); flatClimbing = false; }
  } else if (flatClimbing) {
    player.endClimb();
    flatClimbing = false;
  }
}

/* global __BUILD_STAMP__ */
const BUILD = typeof __BUILD_STAMP__ !== 'undefined' ? __BUILD_STAMP__ : 'dev';
let bakeShort = '????????';

// ---------------------------------------------------------- scope target
const _scopeOri = new THREE.Vector3();
const _scopeDir = new THREE.Vector3();

// the ground at (x, z), near → far: the ground (GM L0) → the Map's GROUND-M
// posts (≈) → the far backdrop (pano ring + horizon, earth curvature baked
// in). The Map card's elevation and the guide's tick read it; the AIM reads
// aimray.js.
function _rangeGround(x, z) {
  // the frame's data answers only inside the playable frame: a ray leaving
  // it meets farHf, never the edge height extended to the horizon
  // (gazetteer.inView has the same guard)
  let gy = null;
  if (hf && x >= 0 && z >= 0 && x <= hf.extentX && z <= hf.extentZ) {
    gy = hf.height(x, z);
    if (gy === null && mapHeights) gy = mapHeights.heightAt(x, z);
  }
  if (gy === null && farHf) gy = farHf(x, z);
  return gy;
}
// The aim ray from _scopeOri along _scopeDir (aimray.js aimMarch: exact L0
// where resident, the envelope only to find where L0 is needed, the far
// data outside the frame). Returns the march result — {dist, x, y, z, src,
// final, need, undecided}; dist null + final = open sky, dist null + !final
// = undecided (terrain loading), never sky. `req`: 'now' asks AimGround for
// the needed L0 tiles at once (inspect); 'dwell' only once the same
// undecided tile has led for AIM_DWELL_MS (the 10 Hz readouts — panning
// fetches nothing); null never. Every march holds its tiles (AimGround.hold
// 'aim', LABELS-CHURN: the next march reads them again — not evicted).
const AIM_DWELL_MS = 300;
const AIM_REQ_TILES = 8;
let _aimLead = null;              // {k, since}: the dwell's leading needed tile
function _rayMarch(req = 'dwell', opts = undefined) {
  if (!_aimMarch) return null;
  const r = _aimMarch(_scopeOri, _scopeDir, opts);
  aimGround.hold('aim', wantOf(r, 0), performance.now());
  if (!r.final && r.need.length && req) {
    const now = performance.now(), k = r.need[0];
    if (!_aimLead || _aimLead.k !== k) _aimLead = { k, since: now };
    if (req === 'now' || now - _aimLead.since >= AIM_DWELL_MS) aimGround.request(r.need.slice(0, AIM_REQ_TILES), now);
  }
  return r;
}
// inspect: march, fetch what the ray still needs, march again — until the
// answer is final or `rounds` fetch rounds did not settle it (a tile that
// fails stays out for 5 s: the last answer is then the honest provisional one)
async function _aimRefine(ori, dir, rounds = 8) {
  _scopeOri.copy(ori); _scopeDir.copy(dir);
  let r = _rayMarch('now');
  for (let i = 0; r && !r.final && i < rounds; i++) {
    await aimGround.settle();
    _scopeOri.copy(ori); _scopeDir.copy(dir);
    r = _rayMarch('now');
  }
  return r;
}

// VR: the head zoom window's labels (weapons.drawLabels → paintZoomWindow:
// the projected POIs + the 1× layer's alpha — the window carries nothing
// else, owner 2026-11-01 "plain", vr-nomap) from monoCam's CURRENT state;
// gazetteer.inView is the cost, so it runs only for a frame that draws it
function _monoLabels() {
  // the aim lane along the window's centre (aimGround hold + dwell lead):
  // the right trigger's card resolves there (vrPoint, APP_TRAILS Inspect)
  weapons.monoCam.getWorldPosition(_scopeOri);
  weapons.monoCam.getWorldDirection(_scopeDir);
  _rayMarch();
  // depthAt: the line of sight on the depth the optic drew (weapons.js)
  // the window wider than the optic band: the pre-band layer (summits only,
  // the desktop's rule: pre = how far into the band's zoom)
  const wide = weapons.monoCam.fov > weapons.monoBaseFov;
  const pre = wide ? Math.log(weapons.headVfov / weapons.monoCam.fov) / Math.log(weapons.headVfov / weapons.monoBaseFov) : 1;
  const features = gazetteer ? gazetteer.inView(weapons.monoCam, 10000, hf, groundEnv, farHf, weapons.depthAt,
    wide ? { pre, peaks1x: _viewTun.labels_1x_peaks } : null) : [];
  const a1 = _viewTun.labels_1x_alpha;
  return { features, labelAlpha: wide ? a1 + (1 - a1) * pre : 1 };
}

// VR: the head zoom window draws its labels next frame
// (updateMonocular), on every other frame — label data only for a frame that
// draws it. A skipped frame stays owed: tick fills it if updateMonocular
// draws after all.
function scopeTarget() {
  scopeFrame++;
  if (weapons && weapons.monoActive && !weapons.monoFreeze) {
    const use = weapons.monoDrawsNext();
    _monoLabelData = use ? _monoLabels() : null;
    _monoLabelOwed = !use;
    return;
  }
  _monoLabelData = null;          // inspect freeze-frame / no optic in hand
  _monoLabelOwed = false;
}

// ------------------------------------------------------------ the one view
// Desktop + phone (APP_PLAYER.md §5): ONE view whose zoom (viewzoom.js)
// narrows the main camera down to the optic band, then renders through the
// optic (weapons.monoCam: dehaze + clarity ramped in, focus wedge, depth).
// The HUD (ScopeHud on #view-hud) morphs with it; labels and the centre range
// come from whichever camera is on screen.
const VIEW_LABEL_MS = 120;        // pre-band label refresh (≈ 8 Hz; reprojected every frame)
const VIEW_RANGE_MS = 100;        // centre range refresh (≈ 10 Hz)
const _RANGE_MARCH = { skip: true };   // the centre range's march (_viewRange)
const BLD_STEP_M = 2;             // building step test stride
const BLD_MAX_M = 300;            //   ... and reach (collision holds the near buildings)
const HIT_FLASH_MS = 140;
let _viewTun = { labels_1x_peaks: 4, labels_1x_alpha: 0.45, range_dim_m: 50, vignette_max: 0.55 };
const _labelCam = new THREE.PerspectiveCamera();   // inView reads cam.position: a world-space twin
const _vRay = new THREE.Vector3();
const _vUp = new THREE.Vector3();
const _vQ = new THREE.Quaternion();
const _vNdc = new THREE.Vector3();
let _rmbAt = -1e9;
const _vignette = () => $('view-vignette');

function _applyViewTunables(t) {
  const v = (t && t.view) || {};
  const m = (t && t.weapons && t.weapons.monocular) || {};
  for (const k of Object.keys(_viewTun)) if (typeof v[k] === 'number') _viewTun[k] = v[k];
  if (viewZoom) viewZoom.configure({ bandFov: m.fov_deg, maxZoom: m.max_zoom, step: v.wheel_step });
  if (tilt) tilt.configure({ enterDeg: v.tilt_enter_deg, exitDeg: v.tilt_exit_deg, dwellMs: v.tilt_dwell_ms });
}

// the mouse back to the game (only honoured inside a user gesture)
function _relock() {
  const el = renderer.domElement;
  if (document.pointerLockElement === el) return;
  try { const p = el.requestPointerLock(); if (p && p.catch) p.catch(() => {}); } catch { /* no gesture */ }
}

// RMB (desktop, any mode incl. combat): World ⇄ Map. Runs inside the
// mousedown, so leaving the Map can re-take pointer lock at once.
// the right button is dead while the beam-in overlay is up and RMB_QUIET_MS
// after it went (owner ruling 2026-10-19); the 2D pill and N are not affected
function rmbQuiet(now = performance.now()) {
  return !!_beamIn || _entering || now - _ovlGoneAt < RMB_QUIET_MS;
}
function _rmbToggle() {
  if (mode !== 'flat' || !playerMap) return;
  const now = performance.now();
  // a Map card goes with the Map (onClose closes it); a 3D-view card blocks
  if (inspect && inspect.isOpen && inspect.origin !== 'map') return;
  if (inspect && !inspect.isOpen && now - inspect._closedAt < 100) return;
  if (now - _rmbAt < 120) return;
  _rmbAt = now;
  if (playerMap.fullOpen) { playerMap.closeFull('rmb'); _relock(); } else playerMap.openFull();
}

// 👁 Look (a Map card): pin the target on every map + the compass strip,
// close the Map and face it — desktop eases yaw / pitch there (pitch within
// ±1.2 rad); the phone's view is the gyro's, so it only gets the pin
function _lookAt(x, y, z, label) {
  if (!playerMap) return;
  playerMap.pin(x, z, label);
  if (playerMap.fullOpen) playerMap.closeFull('look');
  if (mode !== 'flat' || !player) return;
  const ex = player.pos.x, ez = player.pos.z, ey = camera.getWorldPosition(_vRay).y;
  const dx = x - ex, dz = z - ez, h = Math.hypot(dx, dz);
  if (h < 0.5) return;
  const ty = y ?? _rangeGround(x, z) ?? ey;
  _lookEase = { yaw: Math.atan2(-dx, -dz), pitch: Math.max(-1.2, Math.min(1.2, Math.atan2(ty - ey, h))) };
}

// compass heading of a camera, degrees from grid north (clockwise). The
// horizontal part of forward + up stays defined looking straight down
// (the phone tipped flat for the Map).
function _headingOf(cam) {
  cam.getWorldDirection(_vRay);
  _vUp.set(0, 1, 0).applyQuaternion(cam.getWorldQuaternion(_vQ));
  const x = _vRay.x + _vUp.x * Math.max(0, -_vRay.y);
  const z = _vRay.z + _vUp.z * Math.max(0, -_vRay.y);
  let deg = Math.atan2(x, -z) * 180 / Math.PI;
  if (deg < 0) deg += 360;
  return deg;
}

// the zoom input of this frame (wheel is event-driven: see the listener)
function _viewInput(dt) {
  if (inspect && inspect.isOpen) return;        // the picture is frozen
  if (player.dizzyActive) viewZoom.reset();
  if (mode === 'mobile') {
    if (mobile.consumeZoomReset()) viewZoom.reset();
    const p = mobile.consumePinch();
    if (p !== 1) viewZoom.pinch(p);
    if (mobile.zoomTouching) viewZoom.setFrac(mobile.zoomFrac);
  }
}

// before player.step: the zoom eases and the naked camera narrows (look
// sensitivity follows, so a zoomed view turns the same pixels per count)
function _viewZoomStep(dt) {
  viewZoom.update(dt);
  const fov = Math.max(viewZoom.fov, viewZoom.bandFov);   // the band renders via monoCam
  if (Math.abs(camera.fov - fov) > 1e-4) { camera.fov = fov; camera.updateProjectionMatrix(); }
  flat.lookScale = viewZoom.lookScale;
  mobile.lookScale = viewZoom.lookScale;
}

// after the player moved: band hand-over, the optic pose, labels, range
function _viewPose(dt, now) {
  viewBand = viewZoom.band;
  // the focus wedge's view arm (opticFocusCam): hysteretic in ViewZoom, below
  // the band only, and only on a tier whose focus block has view levels
  const va = RENDER_CFG.focus && RENDER_CFG.focus.view_arm;
  viewArm = !!va && viewZoom.focusArm(va.zoom_on, va.hyst) && !viewBand && focus.viewOk;
  weapons.setBand(viewBand, viewZoom.monoZoom, viewZoom.clearRamp);
  if (viewBand) weapons.poseMonoFull(dt, camera);
  viewCam = viewBand ? weapons.monoCam : camera;
  const frozen = !!(inspect && inspect.isOpen);
  if (!frozen && firstActs.go('view.labels')) {
    // below the band the label refresh and the range refresh never share a
    // frame (viewDue: the range waits one); the band's labels run every frame
    const due = viewDue(now, !viewBand && _vLabels && !_vLabels.band ? _vLabels.at : -Infinity,
      _vRange ? _vRange.at : -Infinity, VIEW_LABEL_MS, VIEW_RANGE_MS, !viewBand);
    if (viewBand) {
      // the band: today's optic labels exactly (pre = 1, rendered depth)
      if (!weapons.monoFreeze) {
        _vLabels = { features: gazetteer ? gazetteer.inView(weapons.monoCam, 10000, hf, groundEnv, farHf, weapons.depthAt, { world: true }) : [], at: now, band: true };
      }
    } else if (due & VIEW_LABELS) {
      camera.getWorldPosition(_labelCam.position);
      camera.getWorldQuaternion(_labelCam.quaternion);
      _labelCam.fov = camera.fov; _labelCam.aspect = camera.aspect;
      _labelCam.near = camera.near; _labelCam.far = camera.far;
      _labelCam.updateProjectionMatrix();
      _labelCam.updateMatrixWorld(true);
      _vLabels = { features: gazetteer ? gazetteer.inView(_labelCam, 10000, hf, groundEnv, farHf, null,
        { pre: viewZoom.w, peaks1x: _viewTun.labels_1x_peaks, world: true }) : [], at: now, band: false };
    }
    if (due & VIEW_RANGE) _vRange = _viewRange(now);
  }
  // reproject the world-anchored labels with the pose that renders now
  if (_vLabels && _vLabels.features) {
    viewCam.updateMatrixWorld(true);
    for (const f of _vLabels.features) {
      if (f.wx === undefined) continue;
      _vNdc.set(f.wx, f.wy, f.wz).project(viewCam);
      f.off = _vNdc.z > 1 || Math.abs(_vNdc.x) > 1.05 || Math.abs(_vNdc.y) > 1.05;
      f.sx = (_vNdc.x + 1) * 512;
      f.sy = (1 - _vNdc.y) * 320;
    }
  }
}

// the building step test (no depth target below the band): the first solid
// building sample along the ray within `max` m, refined by bisection
function _buildingStep(eye, dir, max = BLD_MAX_M) {
  const col = player && player.collider;
  if (!col || !col.solidAt) return null;
  let prev = 0;
  for (let d = BLD_STEP_M; d <= max; d += BLD_STEP_M) {
    if (col.solidAt(eye.x + dir.x * d, eye.y + dir.y * d, eye.z + dir.z * d)) {
      let lo = prev, hi = d;
      for (let i = 0; i < 8; i++) {
        const mid = (lo + hi) / 2;
        if (col.solidAt(eye.x + dir.x * mid, eye.y + dir.y * mid, eye.z + dir.z * mid)) hi = mid; else lo = mid;
      }
      return { dist: hi };
    }
    prev = d;
  }
  return null;
}

// what the centre is on, for the range readout's name (Z ≥ 2)
function _centreName(x, y, z) {
  if (roadnet && roadnet.graph) {
    const rn = roadnet.graph.nameAt(x, y, z, 15, 8);
    if (rn) return rn;
  }
  if (gazetteer) {
    const poi = gazetteer.nearest(x, z, 200);
    const landform = poi && (poi.kind === 'peak' || poi.kind === 'pass' || poi.kind === 'ridge');
    if (poi && poi.name && (landform || poi.dist < 50)) return poi.name;
  }
  if (rivers) { const rn = rivers.nameAt(x, z); if (rn) return rn; }
  if (player.water) { const ln = player.water.nameAt(x, z); if (ln) return ln; }
  return '';
}

// the centre range: the optic's depth in the band, the building step test +
// the terrain march below it. Δh from the feet; dim when close.
function _viewRange(now) {
  const cam = viewCam || camera;
  cam.getWorldPosition(_scopeOri);
  cam.getWorldDirection(_scopeDir);
  let d = null;
  if (viewBand) {
    // sky in the optic's depth is not sky yet: the drawn far rings are the
    // lower envelope, the true ground can stand above them — the march
    // decides, as inspect's resolveTarget does
    const od = weapons.opticDepth(512, 320);
    if (od !== null && Number.isFinite(od)) d = od;
  } else {
    const b = _buildingStep(_scopeOri, _scopeDir);
    if (b) d = b.dist;
  }
  if (d === null) {
    // the march: an undecided ray with no hit yet reads '—' like sky until
    // its L0 lands (a steady aim fetches it, AIM_DWELL_MS). It skips the
    // resident L0 blocks the ray clears (aimMarch opts.skip, exact — the
    // labels' march): the plain 0.5 m stride over a valley of resident L0 was
    // up to ~20 k samples (~10 ms) ten times a second. Its cost is paid from
    // the labels' bucket (LabelSight.charge), one march budget for the view.
    const t0 = performance.now();
    const hit = _rayMarch('dwell', _RANGE_MARCH);
    if (labelSight) labelSight.charge(performance.now() - t0);
    d = hit && hit.dist !== null ? hit.dist : Infinity;
  }
  if (d === Infinity) return { dist: null, dh: null, name: '', dim: false, at: now };
  const x = _scopeOri.x + _scopeDir.x * d, y = _scopeOri.y + _scopeDir.y * d, z = _scopeOri.z + _scopeDir.z * d;
  const zoom = viewZoom ? viewZoom.z : 1;
  return { dist: d, dh: y - player.pos.y, name: zoom >= 2 ? _centreName(x, y, z) : '',
    dim: d < _viewTun.range_dim_m, at: now, x, z };
}

// the compass strip's markers (the view HUD, the VR zoom window): the
// waypoint beacon, the map pin, the followed player
function _hudMarkers() {
  const bearingTo = (x, z) => {
    let b = Math.atan2(x - player.pos.x, -(z - player.pos.z)) * 180 / Math.PI;
    return b < 0 ? b + 360 : b;
  };
  const markers = [];
  if (beacons && beacons.waypoint && !_combatOn()) {
    markers.push({ brg: bearingTo(beacons.waypoint.x, beacons.waypoint.z), color: '#facc15' });
  }
  if (playerMap && playerMap.mark) markers.push({ brg: bearingTo(playerMap.mark.x, playerMap.mark.z), color: '#67e8f9' });
  if (playerMap && playerMap.followId !== null) {
    const f = playerMap.list.find((p) => p.id === playerMap.followId);
    if (f) markers.push({ brg: bearingTo(f.x, f.z), color: '#f472b6' });
  }
  return markers;
}

// after the render: the HUD canvas + the vignette (+ the phone's slider)
function _viewHud(now) {
  const w = viewZoom.w;
  const vg = _vignette();
  if (vg) {
    const o = (w * _viewTun.vignette_max).toFixed(3);
    if (vg.style.opacity !== o) vg.style.opacity = o;
  }
  if (mode === 'mobile') mobile.setZoom(viewZoom.frac, `${viewZoom.target.toFixed(viewZoom.target < 10 ? 1 : 0)}×`);
  const brg = _headingOf(viewCam || camera);
  if (firstActs.go('scope.hud')) scopeHud.draw({
    bearing: brg, markers: _hudMarkers(),
    features: _vLabels ? _vLabels.features : null,
    labelAlpha: viewBand ? 1 : _viewTun.labels_1x_alpha + (1 - _viewTun.labels_1x_alpha) * w,
    w, band: viewBand, zoom: viewZoom.z, zMax: viewZoom.zMax, zBand: viewZoom.zBand,
    range: _vRange, frame: scopeFrame++,
    hit: now < _hitUntil ? (_hitBig ? 2 : 1) : 0,
  });
}

// the phone tipped flat opens the Map heading-up, raised closes it
// (viewzoom.js TiltSwitch); the 🔒 freezes it with the view
function _viewTilt(now) {
  tilt.locked = mobile.viewLock;
  if (tilt.open && !playerMap.fullOpen) tilt.release();       // closed by × / a tap
  if (playerMap.fullOpen && !tilt.open) return;                // someone else's map
  camera.getWorldDirection(_vRay);
  const pitch = Math.asin(Math.max(-1, Math.min(1, _vRay.y))) * 180 / Math.PI;
  const r = tilt.update(pitch, mobile.faceUp, now);
  if (r === 'open') playerMap.openFull({ headingUp: true });
  else if (r === 'close') playerMap.closeFull('tilt');
}

// ------------------------------------------------------ player map + beams
// Desktop + phone always have the Map; in combat it shows self + terrain
// only (playermap `combat`). With guns live a map of everyone and
// visible-from-anywhere beams are a radar, so the other players, follow and
// the beacons are combat OFF only — and so is VR's wrist map. Both read the
// interpolated remotes (updateRemotes ran).
const _pmDir = new THREE.Vector3();

// THE SLOPE AHEAD (APP_PLAYER "Slope ahead"): the mean incline from the
// ground under the feet to the ground SLOPE_M ahead along the view heading
// (rad from grid north) — whole degrees, + uphill / − downhill; null without
// ground. Both points on the standing resolver (spans / structures / bores;
// the probe ahead from just above the feet: the walkable level there), the
// ground chain where it has no node.
const SLOPE_M = 10;
let _slopeDeg = null;
function _slopeAhead(heading) {
  const p = player.pos;
  const y0 = player._groundY(p.x, p.y, p.z) ?? _rangeGround(p.x, p.z);
  if (y0 === null) return null;
  const x1 = p.x + Math.sin(heading) * SLOPE_M, z1 = p.z - Math.cos(heading) * SLOPE_M;
  const y1 = player._groundY(x1, y0 + 2, z1) ?? _rangeGround(x1, z1);
  if (y1 === null) return null;
  return Math.round(Math.atan2(y1 - y0, SLOPE_M) * 180 / Math.PI);
}
// THE MAP'S OWN FRAME LOOP (smooth Map): before entry the full Map paints
// from its own requestAnimationFrame callback, not from tick — the 3D tick
// (streaming, the landing draw) never sits in the Map's frame path. Started
// by launch / onVrEnd; it ends itself once a mode runs, the Map is no longer
// the page or a headset presents (window rAF is paused there): tick paints
// the Map again (`_mapRaf` 0).
let _mapRaf = 0, _mapPrev = 0;
function mapLoop(t) {
  _mapRaf = 0;
  if (mode || !playerMap || !playerMap.preEntry || (renderer && renderer.xr.isPresenting)) return;
  _mapRaf = requestAnimationFrame(mapLoop);
  const dt = Math.min(0.1, Math.max(0, (t - _mapPrev) / 1000));
  _mapPrev = t;
  act('map', () => updatePlayerMap(performance.now(), dt));
  if (_mapSetup && terrain) act('map.setup', () => terrain.ground.prep());
}
function startMapLoop() {
  if (_mapRaf) return;
  armMapSetup();
  _mapPrev = performance.now();
  _mapRaf = requestAnimationFrame(mapLoop);
  setTimeout(hudNumFmt, 0);       // the HUD's number format, in a Map task of its own
}
// THE HUD'S NUMBER FORMAT: ONE Intl.NumberFormat, made during the Map dwell
// before entry (startMapLoop) — Number#toLocaleString builds one per call,
// and the first (Intl's set-up) cost 12–23 ms inside the first play frames.
// The same locale and options as the toLocaleString() it replaces (none: the
// browser's default locale; the EN/DE switch never moved it), so the same
// strings (check-mapfirst §HUD). updateHud makes it itself if no Map came first.
let _hudFmt = null;
function hudNumFmt() { return _hudFmt || (_hudFmt = new Intl.NumberFormat()); }
// THE MAP SET-UP (owner ruling 2026-10-24, amends the quiet start): from the
// player's first touch of the Map before entry (a pointer, wheel or key on
// #pmap-full) the ground painter's set-up — its world prep and programs; no
// paint, no pool, the rest of the quiet start holds — runs in its sliced
// parts, one a Map frame (GroundGen.prep, the shown pace); the Beam's hidden
// pace takes over from the Beam
let _mapSetup = false, _mapSetupArmed = false;
const MAP_TOUCH = ['pointerdown', 'wheel', 'keydown'];
function armMapSetup() {
  const el = $('pmap-full');
  if (_mapSetupArmed || !el) return;
  _mapSetupArmed = true;
  const go = () => {
    if (mode || !playerMap || !playerMap.preEntry) return;
    _mapSetup = true;
    for (const ev of MAP_TOUCH) el.removeEventListener(ev, go, true);
  };
  for (const ev of MAP_TOUCH) el.addEventListener(ev, go, { capture: true, passive: true });
}
function updatePlayerMap(now, dt) {
  if (!playerMap) return;
  // the phone's GPS fix on the full Map (a dot + its accuracy ring)
  const g = (mode || entryMode) === 'mobile' ? _gpsGame() : null;
  playerMap.gpsFix = g ? { x: g.x, z: g.z, acc: gps.fix.acc } : null;
  if (!mode) {
    // the Map before entry: the full Map is the page (preEntry forces it
    // on), no self — no arrow, mini map, wrist, ◎ or 🧭; the others' dots
    // as in the game (combat on: none)
    if (!playerMap.preEntry) return;
    try {
      playerMap.update({ enabled: false, combat: _combatOn(), remotes: net.remotes, now, dt,
        self: null, wedge: null, xrLeft: null, eye: null });
    } catch (e) { console.error('[playermap]', e); }
    return;
  }
  const combat = _combatOn();
  const enabled = mode === 'vr' ? !combat : true;
  const cam = mode === 'vr' ? renderer.xr.getCamera() : camera;
  cam.getWorldDirection(_pmDir);
  // flat / phone: forward + up keeps the heading defined looking down (the
  // phone tipped flat shows the heading-up Map)
  const heading = mode === 'vr' ? Math.atan2(_pmDir.x, -_pmDir.z) : _headingOf(camera) * Math.PI / 180;
  // the slope ahead along this heading, at the HUD's cadence: desktop's HUD
  // row (updateHud), the phone's chip under the mini map, the VR wrist
  if (frameTimes.length % 12 === 0) {
    _slopeDeg = _slopeAhead(heading);
    playerMap.setSlope(mode === 'flat' ? null : _slopeDeg);
  }
  // your view on the full Map: the render camera's horizontal half-angle,
  // a length that grows with the zoom (350 m × Z, ≤ 15 km), the centre
  // range point when there is one
  let wedge = null;
  if (mode !== 'vr' && playerMap.fullOpen) {
    const vc = viewCam || camera;
    const half = Math.atan(Math.tan((vc.fov * Math.PI / 180) / 2) * (camera.aspect || 1));
    const Z = viewZoom ? viewZoom.z : 1;
    const r = _vRange && Number.isFinite(_vRange.dist) && _vRange.x !== undefined ? { x: _vRange.x, z: _vRange.z } : null;
    wedge = { half: Math.min(half, 1.4), len: Math.min(15000, 350 * Z), range: r };
  }
  try {
    playerMap.update({ enabled, combat, remotes: net.remotes, now, dt,
      self: { x: player.pos.x, z: player.pos.z, heading }, wedge,
      xrLeft: mode === 'vr' ? xrCtrl('left') : null, eye: mode === 'vr' ? cam : null });
    // view angle per screen pixel keeps the beam a few pixels wide at range
    const radPerPx = mode === 'vr' ? 0.001
      : 2 * Math.tan(camera.fov * Math.PI / 360) / Math.max(1, innerHeight);
    beacons.update({ enabled: !combat, remotes: net.remotes, cam, radPerPx,
      followId: playerMap.followId, time: now / 1000 });
  } catch (e) { console.error('[playermap]', e); }
}

// ---------------------------------------------------------------- HUD
// (the heading lives on the view HUD's compass strip — ScopeHud)
function _esc(s) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;'); }

// the trail chip (TRAILS-C): on a GRADED trail (WaysNet.trailAt, 8 m) for 4 s
// after entering it or after the grade changes; off a trail for 2 s re-arms
// it. `↗ name — T3 Bergwanderweg · surface` + the mark swatch (index.html .tm-sw)
const _trailHud = { key: null, until: 0, offAt: 0 };
function trailChip(now) {
  if (!roadnet || !roadnet.ready || !roadnet.trailByRun.size) return '';
  const t = roadnet.trailAt(player.pos.x, player.pos.y, player.pos.z);
  const S = _trailHud;
  if (!t || t.mark === 'none') {
    if (!S.offAt) S.offAt = now;
    if (now - S.offAt > 2000) S.key = null;
    return '';
  }
  S.offAt = 0;
  const key = t.mark + ':' + t.sac;
  if (key !== S.key) { S.key = key; S.until = now + 4000; }
  if (now >= S.until) return '';
  const sw = MARK[t.mark].bands.map((b) => `<i class="tm-${b}"></i>`).join('');
  const surf = t.tt && t.tt.surface ? ` · ${_esc(t.tt.surface)}` : '';
  return `<span class="tm-chip">↗ ${t.name ? _esc(t.name) + ' — ' : ''}<span class="tm-sw">${sw}</span> ${_esc(gradeText(t))}${surf}</span>`;
}

// the HUD's surface word under the feet: on a drawn way its own surface
// (ways2.js wayLabelAt), else the class map (desktop #hud, the VR HUD)
function _hudSurfKey() {
  const surf = player.surface ? player.surface.at(player.pos.x, player.pos.z) : null;
  return (roadnet && roadnet.wayLabelAt ? roadnet.wayLabelAt(player.pos.x, player.pos.z) : null) || (surf && surf.key);
}

// the HUD's conditional chips (jet-suit, bike, trail, lake, lifts): HTML
// for the desktop's #hud, plain text for the VR canvas (no trail chip there:
// its mark swatches are HTML)
function _hudCond(sKey, html) {
  const esc = html ? _esc : (x) => x;
  const parts = [];
  if (player.gliding) {
    const gy = player._groundY(player.pos.x, player.pos.y, player.pos.z);
    const agl = gy !== null ? Math.round(player.pos.y - gy) : '?';
    const spd = Math.round(Math.hypot(player.vel.x, player.vel.y, player.vel.z) * 3.6);
    parts.push(t('main.hud.jet', { v: spd, h: agl }));
  }
  if (player.bike) {
    const spd = Math.round(player.bikeSpeed * 3.6);
    const onRoad = player.roads && player.roads.at(player.pos.x, player.pos.z) > 0.3;
    const sk = onRoad ? t('main.hud.road') : (sKey || t('main.hud.offroad'));
    parts.push(t('main.hud.bike', { v: spd, surf: esc(sk) })
      + ' · ' + t(player.bikeFollow ? 'main.hud.auto' : 'main.hud.manual')
      + (player.bikeInd === -1 ? ' · ' + t('main.hud.indL')
        : player.bikeInd === 1 ? ' · ' + t('main.hud.indR') : ''));
  }
  const tchip = html ? trailChip(performance.now()) : '';
  if (tchip) parts.push(tchip);
  if (player.water) {
    const lakeName = player.water.nameAt(player.pos.x, player.pos.z);
    if (lakeName) parts.push(`🌊 ${esc(lakeName)}`);
  }
  if (zones && zones.riding) {
    parts.push(`🚋 → ${esc(zones.riding.dest || '')}`);
  } else if (funicular.nearest(player.pos) < 10) {
    parts.push(t('main.hud.funicular'));
  } else if (aerialway.nearest(player.pos) < 12) {
    parts.push(t(aerialway.nearestKind === 'chair_lift' ? 'main.hud.chair' : 'main.hud.cabin'));
  }
  return parts;
}

function updateHud() {
  if (frameTimes.length % 12 !== 0) return;
  // the phone has no #hud: only its key-hint line (mobile.js #mob-keys)
  if (mode === 'mobile' && mobile) { mobile.setKeyHints(keyHintPages('phone', _keyHintState(), t)); return; }
  if (mode !== 'flat') return;

  // -- primary row: altitude + surface
  const alt = Math.round(player.pos.y);
  const sKey = _hudSurfKey();
  const surfLabel = sKey && !player.bike && !player.gliding
    ? `  ${_esc(sKey)}` : '';
  const primary = `▲ ${alt} m` + (_slopeDeg !== null ? `  ${slopeLabel(_slopeDeg)}` : '') + surfLabel;

  // -- conditional row
  const parts = _hudCond(sKey, true);

  // -- status (dizzy / down)
  let statusHtml = '';
  if (player.dizzyFreeze > 0) {
    statusHtml = `<div class="hud-row hud-status">${t('main.hud.down', { s: player.dizzyFreeze.toFixed(1) })}</div>`;
  } else if (player.dizzyT > 0) {
    statusHtml = `<div class="hud-row hud-status">${t('main.hud.dizzy', { s: player.dizzyT.toFixed(1) })}</div>`;
  }

  // -- station zone boarding: point at the station and click (the inspect
  // card's action buttons); F / G are the keyboard accelerators
  let stationHtml = '';
  if (zones && zones._rows) {
    const rows = zones._rows;
    const enabled = rows.filter((r) => !r.dim && r.onShoot);
    const labels = rows.map((r) => {
      let key = '';
      if (!r.dim && r.onShoot) {
        if (r === enabled[0]) key = '<kbd>F</kbd> ';
        else if (r === enabled[1]) key = '<kbd>G</kbd> ';
      }
      const cls = r.dim ? ' class="dim"' : '';
      return `<span${cls}>${key}${_esc(r.text)}</span>`;
    });
    const title = zones.riding
      ? `→ ${_esc(zones.riding.dest || '')}`
      : (zones.zone ? `🚉 ${_esc(zones.zone.name)}` : '');
    if (title) {
      const how = !enabled.length ? ''
        : weapons.monoActive ? ` · <span class="dim">${t('main.hud.orClick')}</span>`
          : ` · <span class="dim">${t('main.hud.monoClick')}</span>`;
      stationHtml = `<div class="hud-row hud-station">${title}: ${labels.join(' · ')}${how}</div>`;
    }
  }

  // -- debug (compact)
  const info = renderer.info;
  const ft = frameTimes.slice(-90);
  const fps = Math.round(1000 / (ft.reduce((a, b) => a + b, 0) / ft.length));
  const debugHtml = `${fps} fps · `
    + `${player.pos.x.toFixed(0)},${player.pos.y.toFixed(0)},${player.pos.z.toFixed(0)} · `
    + `${hudNumFmt().format(info.render.triangles)} tris`;

  // -- assemble: a row is written only when its html moved; the key hints are
  // a persistent row (keyhints.js mountHints: the pages + their cross-fade)
  if (!_hudEl) {
    hud.innerHTML = '<div class="hud-debug"></div><div class="hud-main"><div class="hud-body"></div>'
      + '<div class="hud-row hud-keys kh"></div></div>';
    _hudEl = { debug: hud.firstChild, body: hud.querySelector('.hud-body'),
      keys: mountHints(hud.querySelector('.hud-keys')), bodyHtml: null };
  }
  _hudEl.debug.textContent = debugHtml;
  const bodyHtml = `<div class="hud-row hud-primary">${primary}</div>`
    + (parts.length ? `<div class="hud-row hud-cond">${parts.join(' · ')}</div>` : '')
    + statusHtml
    + stationHtml;
  if (bodyHtml !== _hudEl.bodyHtml) { _hudEl.bodyHtml = bodyHtml; _hudEl.body.innerHTML = bodyHtml; }
  // -- key hints (LMB does what the hand holds: a gun fires, the monocular inspects)
  _hudEl.keys.set(keyHintPages('desktop', _keyHintState(), t));
}

// the key-hint state (keyhints.js keyHintGroups), every platform
let _hudEl = null;
function _keyHintState() {
  return {
    bike: !!player.bike, follow: !!player.bikeFollow, glide: !!player.gliding, riding: !!(zones && zones.riding),
    combat: _combatOn(), mono: !!weapons.monoActive, voice: !!voice.ready, muted: !!voice.muted,
    compass: !!(mobile && mobile.compassLive), gyro: !!(mobile && mobile._gyroReady),
  };
}

// ------------------------------------------------------------ load report
// `load` messages (PROTOCOL.md; the server rebuilds each from its own
// whitelist, clamps every field and takes one per phase per connection):
//   phase 'entry'     when the beam-in overlay goes after the first latch
//                     (VR: at the latch), over the socket; `release` says
//                     what let it go (smooth | max | vr), 'left' = the page
//                     was hidden / left with the overlay still up (beacon)
//   phase 'pre-entry' once, before that latch: why 'timeout'
//                     (PRE_ENTRY_TIMEOUT_MS, over the socket) or 'hidden' /
//                     'pagehide' (an abandon: navigator.sendBeacon to the
//                     child's /report/load — a socket frame may not leave a
//                     page being torn down — the socket when no beacon)
// Dropped when offline.
// R8 M5c: a report for a page that stays is built IN STEPS (loadReportSteps
// under 'load.report', assetq sendInSteps) and sent after its last step; a
// page leaving (beacon: hidden / pagehide) builds it at once and drains every
// report still in steps first (assetq flushReports) — sent in this task.
function sendLoadReport(phase = 'entry', why = 'latch', beacon = why === 'hidden' || why === 'pagehide') {
  if (_loadSent[phase] || _noReport) return;
  if (phase === 'pre-entry' && loadT.t_dismiss !== null) return;   // in the world: entry's
  _loadSent[phase] = true;
  const send = (data, leaving) => {
    if (leaving && net.beaconLoad(data)) return;
    if (net.connected) net.sendLoad(data);
  };
  if (!beacon) { sendInSteps(loadReportSteps(phase, why), 'load.report', send, runSteps); return; }
  flushReports();
  let data;
  try { data = buildLoadReport(phase, why); } catch (e) { console.warn('[load] report not built:', e); return; }
  send(data, true);
}
// the pre-entry triggers, armed once the socket is up (a report needs the
// welcome's load key / the connection to be attributed)
function armPreEntryReport() {
  const abandon = (why) => {
    if (why !== 'timeout') flushReports();    // the page is leaving: a report in steps goes now
    if (loadT.t_dismiss === null) sendLoadReport('pre-entry', why);
    else if (_entryWhy && why !== 'timeout' && !_loadSent.entry) {
      // latched, the overlay still up (the smoothness gate): the entry report now
      if (_ovlRelease === null) _ovlRelease = 'left';
      sendLoadReport('entry', _entryWhy, true);
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') abandon('hidden');
  });
  addEventListener('pagehide', () => abandon('pagehide'));
  setTimeout(() => abandon('timeout'), Math.max(0, PRE_ENTRY_TIMEOUT_MS - performance.now()));
}
// how far the start-up got: the last milestone reached
function loadStage() {
  if (loadT.t_dismiss !== null) return 'play';
  if (loadT.t_beam !== null) return 'beam';
  if (loadT.t_card !== null) return 'card';
  if (loadT.t_map !== null) return 'map';
  if (loadT.t_meta !== null) return 'meta';
  return loadT.t_connect !== null ? 'connect' : 'boot';
}

// the report at once (the bench, a page leaving): the drain of loadReportSteps
function buildLoadReport(phase = 'entry', why = 'latch') {
  const it = loadReportSteps(phase, why);
  let s;
  do s = it.next(); while (!s.done);
  return s.value;
}
// THE REPORT in steps (R8 M5c): the per-entry tables (assetq reportTables:
// each entry's logical name + wire bytes once) and the bake rows a step per
// REPORT_STEP entries; the rest as one step at the end
function* loadReportSteps(phase = 'entry', why = 'latch') {
  const ms = (t) => (t === null ? null : Math.round(t));
  const res = [];
  let bytes = 0, enc = false;
  const entries = performance.getEntriesByType('resource');
  // `/o/<sha>` -> the bake-relative name it carries; the wire bytes
  const { names, wb: wbs, xfer } = yield* reportTables(entries);
  for (let k = 0; k < entries.length; k++) {
    if (k && k % REPORT_STEP === 0) yield;
    const e = entries[k], name = names[k];
    const i = name.indexOf('/bake/');
    if (i < 0) continue;
    const wb = wbs[k];
    bytes += wb;
    if (e.encodedBodySize > 0 && e.encodedBodySize < e.decodedBodySize) enc = true;
    // `/bake/<id>/terrain/L0/3_4.bin?x` -> `terrain/L0/3_4.bin`
    const path = name.slice(i + 6).replace(/[?#].*$/, '');
    const n = path.slice(path.indexOf('/') + 1).slice(0, 80);
    res.push({ n, b: wb, ms: Math.round(e.duration) });
  }
  const nRes = res.length;
  res.sort((a, b) => b.b - a.b);
  res.length = Math.min(res.length, 20);
  const c = navigator.connection;
  const nav = performance.getEntriesByType('navigation')[0];
  // the device behind the tier (device.js): `pcvr` is a desktop class in VR,
  // which only the chosen mode can tell
  const dd = detailDevice();
  const dev = mode === 'vr' && /^desktop/.test(dd.dev) ? 'pcvr' : dd.dev;
  const st = assetStats(xfer);  // transfer bytes per asset class
  const lt = longTaskAttribution();   // attributed long tasks (sched.js act marks)
  return {
    phase, why, stage: loadStage(), t_report: ms(performance.now()),
    // before entry `mode` is null: the launch tap's mode (null on the landing)
    mode: mode || entryMode, tier: detailTier(), gps: !!gps.on,
    dev, probe_ms: dd.probe_ms, tier_why: dd.why, gpu: detailGpu(),
    t_connect: ms(loadT.t_connect), t_meta: ms(loadT.t_meta),
    t_first_tile: terrain && terrain.firstTileAt ? Math.round(terrain.firstTileAt) : null,
    t_hf_ready: ms(loadT.t_hf_ready), t_near_ready: ms(loadT.t_near_ready),
    t_map: ms(loadT.t_map), t_card: ms(loadT.t_card), t_beam: ms(loadT.t_beam),
    t_dismiss: ms(loadT.t_dismiss),
    // the beam-in overlay went (the smoothness gate) and why
    t_ovl: ms(loadT.t_ovl), release: _ovlRelease,
    t_gps_fix: gps.on ? ms(loadT.t_gps_fix) : null,
    prefetch_hit: _prefetchHit,
    long: longTaskReport(), lt_top: lt.top, lt_by: lt.by,
    bulk_left: bulkPending(),     // bulk (phase 1) fetches still queued / in flight
    // the need score (assetq.js needReport): re-scores, drops, the bulk queue
    // waits and the longest-waiting bulk fetches with their score + wait
    need: needReport(24),
    bytes, b_map: st.map.bytes, b_near: st.near.bytes, b_bulk: st.bulk.bytes,
    n_res: nRes, res,
    // transfer bytes per artifact family (assetq.js familyBytes: groundm,
    // terrain, surface, ring, buildings, veg, materials, the boot
    // set's files by name)
    fam: familyBytes(entries, names, wbs),
    net: c ? { type: c.effectiveType || 'unknown', down: c.downlink, rtt: c.rtt } : null,
    // h2 / enc per NETWORK response + how each was answered (netseen.js)
    ...netSeen(entries, nav, enc),
    // WAYS-G (review F6c): the bake's ways look == the bundled wayslook
    // (false = mismatch, null = no ways tiles / no surface)
    ways_look: player && player.surface ? player.surface.waysLook : null,
    // GROUND-M M2: the ground tree's misses (stand misses), pops, decode and
    // stitch ms, drawn nodes / tris / vertex bytes (groundtree.js stats)
    groundm: terrain ? { ...terrain.gm.stats } : null,
    latch: latchState(),
    // assets the client dropped DEAD (a non-retryable answer or bytes that do
    // not parse): GLB tiles (buildings.js dropN)
    drop: { buildings: buildings ? buildings.dropN : 0 },
    // the generation queue up to now (gensched.js GenQueue.report: per pass runs / failures / ms / share /
    // over-runs, the debt left): who held the gen budget before the overlay went
    gen: genReport(),
    // the device bench's phases (bench.js report: entry / walk / jet), its report only
    ...(phase === 'bench' && _bench ? { bench: _bench.report() } : {}),
  };
}

// the beam-in latch's halves now (the load report's `latch`): terrain.nearReady,
// the hf 3×3, the building collision; the VT pool's share
// of terrain — floor tiles <= near_levels missing (vt.nearReady's test), loads
// in flight, and of those the bulk ones (held while bulk is paused)
function latchState() {
  const p = player && player.pos;
  const vt = terrain && terrain.gm.vt;
  let miss = null, held = null;
  if (vt) {
    miss = 0;
    for (const [key, r] of vt.want.naked) {
      if (!vt.res.has(key) && !vt.failed.has(key) && vt.latchKey(key, r)) miss++;
    }
    held = 0;
    for (const key of vt.loading) if (!vt._nearLoad(key)) held++;
  }
  return { terrain: !!(terrain && terrain.nearReady), hf: !!(hf && p && hf.readyAround(p.x, p.z)),
    col: !!(buildings && buildings.nearColReady), veg: !!(forest && shrubs && p && vegSettled(p)), vt_miss: miss, vt_top: vt ? vt.topReady() : null, spr: spritesWorn(), vt_loading: vt ? vt.loading.size : null, vt_held: held };
}

// the VR loading view's readiness inputs (loadProgress, xrload.js), sampled every XRL_MS: placed →
// the latch's four (hf, col, near ground, the near VT slots) → the VR landing's terms + the warm-up
// set's share (compile class prepare, counted from the set's start) + the flight test
function xrlState(fl, now) {
  _xrl.at = now;
  if (mode !== 'vr') return { placed: false };
  if (!_beamIn) return { placed: true, ready: true };
  if (!_beamIn.at) {
    const L = latchState();
    if (L.vt_miss !== null && L.vt_miss > _xrl.miss0) _xrl.miss0 = L.vt_miss;
    return { placed: true, latch: { hf: L.hf, col: L.col, terrain: L.terrain, vt: L.vt_miss === null ? 0 : _xrl.miss0 ? 1 - L.vt_miss / _xrl.miss0 : 1 } };
  }
  if (fl && fl.seen) return { placed: true, ready: true };
  const T = fl ? fl.terms : {}, c = cq.state().classes.prepare;
  if (c && _xrl.d0 === undefined && _warmPost) _xrl.d0 = c.done;
  const d = c && _xrl.d0 !== undefined ? c.done - _xrl.d0 : 0, left = c ? c.queued + c.running : 0;
  const warmFrac = d + left > 0 ? d / (d + left) : 0;
  return { placed: true, latch: true, warmFrac, test: fl ? readiness(fl.test) : 0, settle: fl && fl.settle ? fl.settle.frac : 0,
    terms: fl ? { top: T.top !== undefined, spr: T.spr !== undefined, acts: T.acts !== undefined, veg: T.veg !== undefined, warm: T.warm !== undefined }
      : { top: topOk(now), spr: spritesOk(now), acts: firstActs.done, veg: vegOk(now), warm: false } };
}

// FIRST-MINUTE (first-minute-measure; telemetry `gen.full`): per family the ms after the last placement
// (onPlaced: the Beam, an in-game beam) at which it first CAUGHT UP — ground: every on-screen wanted painter
// tile resident (groundgen visState miss 0, in play); canopy / shrub / ring: no wanted tile of the pass shown
// stale or not at all (gensched pending); shell: every planned forest-volume tile drawn at its planned level
// (forestvol plan / drawn, a bad tile excepted; no group, job, reconcile or replan left); sprites: the near trees + shrubs settled (vegSettled). Checked once a second.
const FULL_FAMS = ['ground', 'canopy', 'shrub', 'ring', 'shell', 'sprites'];
let _full = null, _fullAt = 0;
function fullTick(now) {
  if (!_full || now - _fullAt < 1000 || phase() !== 'play') return;
  _fullAt = now;
  const T = _full.t, pend = genPending(), vs = terrain && terrain.ground ? terrain.ground.visState() : null;
  const passDone = (pre) => {
    const ids = Object.keys(pend).filter((id) => id.startsWith(pre));
    return ids.length > 0 && ids.every((id) => pend[id].n === 0);
  };
  const V = forestVolume();
  const ok = { ground: !!vs && vs.want > 0 && vs.miss === 0, canopy: passDone('canopy'), shrub: passDone('shrub'),
    ring: passDone('ring'), shell: !!(V && V.plan && V.plan.tiles.length) && !V.dirty.size && !V._job && !V._recon && !V._replan
      && V.plan.tiles.every((q) => { const d = V.drawn.get(`${q.i}_${q.j}`); return (d && d.cellM === q.c) || V.bad.has(`${q.c}_${q.i}_${q.j}`); }),
    sprites: !!(forest && shrubs && vegSettled(player.pos)) };
  for (const f of FULL_FAMS) if (T[f] === undefined && ok[f]) T[f] = Math.round(now - _full.at);
}
// the generation's cumulative counters now (telemetry `gen` sends the window between two; __vr.genSnap)
function genSnap() {
  const R = genReport(), G = terrain && terrain.ground, vt = terrain && terrain.gm.vt;
  return { ms: Object.fromEntries(R.passes.map((p) => [p.id, p.ms])), runs: Object.fromEntries(R.passes.map((p) => [p.id, p.runs])),
    debt_ms: R.debt_ms, repaint: G ? G.stats.repaint : 0, fin_drop: G ? G.stats.fin_drop : 0,
    evict: vt ? vt.stats.evicted : 0, commit: vt ? vt.stats.copies : 0 };
}
let _genPrev = null;
function genWin() { const cur = genSnap(), g = genTel(cur, _genPrev); _genPrev = cur; return g; }
// the telemetry `gen` block (PROTOCOL.md telemetry): the window's gen ms / runs per pass, the ground painter's
// commits / repaints (a key this page already painted) / pool evictions / finest-level drops, debt ms; the
// state now: on-screen wanted tiles + missing (vis), ground tiles wanted not resident (pend), per missing
// on-screen tile its fallback (fb: resident 1 / 2 / 3 / 4+ levels coarser; none) and what holds it (why),
// per pass the wanted tiles not fresh (pend_by); full = the FIRST-MINUTE stamps (ms after the placement)
function genTel(cur = genSnap(), prev = _genPrev) {
  const d = (k) => Math.max(0, cur[k] - (prev ? prev[k] : 0));
  const by = (k) => Object.fromEntries(Object.entries(cur[k]).map(([id, v]) => [id, Math.max(0, Math.round((v - (prev && prev[k][id] || 0)) * 10) / 10)]));
  const vs = terrain && terrain.ground ? terrain.ground.visState() : null;
  return {
    ms: by('ms'), runs: by('runs'), debt_ms: Math.round(d('debt_ms') * 10) / 10,
    commit: d('commit'), repaint: d('repaint'), evict: d('evict'), fin_drop: d('fin_drop'),
    vis: vs ? [vs.want, vs.miss] : null, pend: vs ? vs.pend : null, fb: vs ? [...vs.fb, vs.none] : null, why: vs ? vs.why : null,
    pend_by: Object.fromEntries(Object.entries(genPending()).map(([id, p]) => [id, p.n])),
    full: _full ? { ...Object.fromEntries(FULL_FAMS.map((f) => [f, null])), ..._full.t } : null,
  };
}

// xr-rebind (client/vendor/three-r170-xr.cjs): how many XR frames found the frame's target unbound
// by a painter's resetState() since the last line (the draw went to the hidden canvas before the
// re-bind) — client-side only (game/gs.js rebuilds telemetry field by field); `end`: the session total
let _xrRb = { renders: 0, rebinds: 0 };
// quest-multiview (xrmv.js, the vendored three patch; APP_SURFACE_PERF "The XR path"): a VR launch on a
// class with `xr_multiview` (device.js CLASS_ON: quest) installs the controller and wants the multiview
// variant — the compile queue builds it from here, the session's layer is a texture array; every other
// class / mode never installs it (three's per-eye path). `xr_depth`: the eye depth submitted with the layer
function xrMultiview(on) {
  if (!renderer) return;
  if (on && !renderer.multiview && classOn('xr_multiview')) {
    renderer.multiview = createMultiview(renderer, { on: true, depth: classOn('xr_depth'), sw: createSpaceWarp(renderer, { rig }) });
  }
  const mv = renderer.multiview;
  if (mv && mv.want(on) !== on) console.info(`[xr] multiview not available here (${JSON.stringify(mv.state())}): three's per-eye path`);
}
// THE QUEST PICTURE PROFILE (quest-steady72, questprof.js; APP_PLAYER "The Quest picture profile"): the
// owner's switch (VR ☰ menu → Picture, `__vr.qp(name)`). The eye MSAA follows at the next frame (xrmv.js
// reads it per frame), the resolution band restarts at the new home (adaptres.js resBandRefresh), and every
// material whose program key moved (the ground lite: groundmat.js groundKey '-Ql') recompiles — the keys
// are compared before / after, so exactly those programs and no other (one hitch on a switch)
function switchQuestProfile(name) {
  if (!questProfileName() || !QUEST_PROFILE_NAMES.includes(name)) return false;
  if (questProfileName() === name) return true;
  const keys = new Map();
  const mats = new Set(), add = (o) => { const ms = o && o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []; for (const m of ms) if (m && m.customProgramCacheKey !== THREE.Material.prototype.customProgramCacheKey) mats.add(m); };
  scene.traverse(add);
  // the ground tree's resident nodes off the scene (their own materials, shown again later)
  if (terrain && terrain.gm && terrain.gm.nodes) for (const nd of terrain.gm.nodes.values()) add(nd.mesh);
  const each = (f) => { for (const m of mats) f(m); };
  each((m) => { if (!keys.has(m)) keys.set(m, m.customProgramCacheKey()); });
  setQuestProfile(name);
  resBandRefresh();
  let n = 0;
  for (const [m, k] of keys) if (m.customProgramCacheKey() !== k) { m.needsUpdate = true; n++; }
  console.info(`[qp] Quest picture profile ${name}: ${n} programs to recompile`);
  return true;
}
// THE SMOKE (dev, the GPU probe): the scene through an own multiview target vs each eye alone (xrmv.js smoke)
function mvTest(o = {}) {
  const prev = renderer.multiview;
  const mv = prev || createMultiview(renderer, { on: true });
  renderer.multiview = mv;                  // the program variants need a controller; `wanted` stays as it was
  try { return mv.smoke(scene, camera, o); } finally { renderer.multiview = prev; }
}
function xrRebindLog(end = false) {
  const x = renderer && renderer.xr;
  if (!x || !(x.frameRenders >= 0)) return;
  const d = { renders: x.frameRenders - _xrRb.renders, rebinds: x.frameRebinds - _xrRb.rebinds };
  if (end) console.info(`[xr] session end: the XR target re-bound in ${x.frameRebinds} of ${x.frameRenders} XR renders since load`);
  else if (d.rebinds > 0) console.info(`[xr] the XR target re-bound in ${d.rebinds} of ${d.renders} renders (painter resetState)`);
  _xrRb = { renders: x.frameRenders, rebinds: x.frameRebinds };
}

function telemetry(now) {
  if (mode) fullTick(now);
  if (now - lastTelemetry < 5000 || !mode) return;
  lastTelemetry = now;
  if (renderer.xr.isPresenting) xrRebindLog();
  const ft = frameTimes.splice(0);
  if (ft.length < 10) return;
  ft.sort((a, b) => a - b);
  const avg = ft.reduce((a, b) => a + b, 0) / ft.length;
  const p99 = ft[Math.floor(ft.length * 0.99)];
  const sn = Math.min(_splitN, SPLIT_CAP);
  _splitN = 0;
  const cv = renderer.domElement;
  net.telemetry({
    fps_avg: Math.round(1000 / avg * 10) / 10,
    fps_1pct: Math.round(1000 / p99 * 10) / 10,
    frames: ft.length,
    gap_max_ms: Math.round(ft[ft.length - 1]),   // the window's longest frame interval (a stall)
    corr_n: _corr.n, corr_max_m: Math.round(_corr.max * 100) / 100,   // server snaps (a discontinuity)
    tris: renderer.info.render.triangles,
    calls: renderer.info.render.calls,
    mode,
    zone: [Math.round(player.pos.x), Math.round(player.pos.z)],
    dpr: renderer.getPixelRatio(),
    canvas: [cv.width, cv.height],
    gpu: detailGpu(),
    tier: detailTier(),
    dev: mode === 'vr' && /^desktop/.test(detailDevice().dev) ? 'pcvr' : detailDevice().dev,
    upd_ms: splitMedian(_updMs, sn),
    rnd_ms: splitMedian(_rndMs, sn),
    upd_by: secWindow(sn),
    lt_by: longTaskWindow(),
    // the freeze watchdog since the last report (watchFrame / watchContext)
    freeze_n: _frz.n, freeze_max_ms: Math.round(_frz.max), ctx_lost_n: _frz.lost,
    res_scale: resScale(),   // automatic resolution's scale now (adaptres.js; 1 when off)
    ...(famTris.window() || {}),   // tris_by / calls_by: drawn per family (famtris.js)
    gen: genWin(),                 // the generation's window + FIRST-MINUTE (genTel)
    gl: glWatch ? glWatch.window() : null,   // GL errors + three's GL complaints in the window (glwatch.js)
  });
  _frz.n = 0; _frz.max = 0; _frz.lost = 0;
  _corr.n = 0; _corr.max = 0;
}

// ---------------------------------------------- freeze watchdog + context loss
// FREEZE WATCHDOG (freeze-free entry): in a mode, a frame gap over FREEZE_MS
// is a freeze — counted (freeze_n, freeze_max_ms) with every context loss
// (ctx_lost_n) and sent with the next telemetry. A gap across a page / XR
// visibility change or a frame-loop switch (XR session start / end) is not
// one: the browser stopped the frames (_visGen).
const FREEZE_MS = 2000;
const _frz = { n: 0, max: 0, lost: 0 };
let _visGen = 0, _tickVisGen = 0;
document.addEventListener('visibilitychange', () => { _visGen++; });
function watchFrame(gap) {
  const seen = _tickVisGen === _visGen;
  _tickVisGen = _visGen;
  if (!mode || !seen || gap <= FREEZE_MS) return;
  _frz.n++;
  if (gap > _frz.max) _frz.max = gap;
  console.warn(`[freeze] a ${Math.round(gap)} ms frame`);
}
// CONTEXT LOSS: the GPU process reset or the driver dropped the context (a
// lost context used to be a frozen last frame, no word, no report). Lost:
// preventDefault (a restore may come), tick pauses (no sim, no draw), a small
// note says so (#gl-reset), the loss is counted. Restored: three rebuilds its
// GL state and re-uploads what it still holds on the next draw, and the world
// goes on — some content (the ground texture pools, data uploaded once and
// released) may not come back, so the note offers ⟳ reload here, also when no
// restore came within GL_RESTORE_MS. The reload stashes the player's spot
// (sessionStorage `vrm.resume`, this bake): the Map opens there with a pin
// (launch, _resumeAt) and ⤳ Beam goes back in.
const GL_RESTORE_MS = 5000;
let _glLost = false;
function watchContext(cv) {
  const box = $('gl-reset'), msg = $('gl-reset-msg'), btn = $('gl-reset-reload');
  let timer = 0;
  const offer = (text) => {
    if (msg) msg.textContent = text;
    if (btn) btn.hidden = false;
  };
  if (btn) btn.addEventListener('click', () => reloadHere());
  cv.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    _glLost = true;
    _frz.lost++;
    console.warn('[gl] context lost');
    if (box) box.classList.add('on');
    if (msg) msg.textContent = t('main.gl.restoring');
    if (btn) btn.hidden = true;
    clearTimeout(timer);
    timer = setTimeout(() => { if (_glLost) offer(t('main.gl.lost')); }, GL_RESTORE_MS);
  }, false);
  cv.addEventListener('webglcontextrestored', () => {
    _glLost = false;
    if (glWatch) glWatch.restored();
    console.warn('[gl] context restored');
    clearTimeout(timer);
    offer(t('main.gl.restored'));
    timer = setTimeout(() => { if (box && !_glLost) box.classList.remove('on'); }, 20000);
  }, false);
}
const RESUME_KEY = 'vrm.resume';
function reloadHere() {
  try {
    if (mode && player) sessionStorage.setItem(RESUME_KEY, JSON.stringify({ x: player.pos.x, z: player.pos.z, bake: bakeShort }));
  } catch { /* storage off: a plain reload */ }
  _noReport = true;             // a reload we asked for is no abandon
  location.reload();
}
// the spot reloadHere stashed, once, for this bake; null otherwise
function _resumeAt() {
  let r = null;
  try { r = JSON.parse(sessionStorage.getItem(RESUME_KEY) || 'null'); sessionStorage.removeItem(RESUME_KEY); } catch { return null; }
  return r && r.bake === bakeShort && Number.isFinite(r.x) && Number.isFinite(r.z) ? { x: r.x, z: r.z } : null;
}
// median of the first n ring slots (sorted in place: the window is spent)
function splitMedian(a, n) {
  if (!n) return null;
  const s = a.subarray(0, n).sort();
  return r2(s[n >> 1]);
}

function r2(v) { return Math.round(v * 100) / 100; }
function r3(v) { return Math.round(v * 1000) / 1000; }

boot();
